import { z } from 'zod';
import { TEMPLATE_FIELD_KINDS } from './fieldCatalog';
import {
  MAX_TEMPLATE_HEIGHT_MM,
  MAX_TEMPLATE_WIDTH_MM,
  MIN_TEMPLATE_HEIGHT_MM,
  MIN_TEMPLATE_WIDTH_MM,
} from './labelLayout';
import { LabelFieldContract } from './labelFieldContract';
import { LabelNfcSchema } from './labelNfc';
import { TenantRegionSchema } from './tenantRegion';
import { LabelDeviceLocationSchema } from './labelLocation';
import { PaymentDisputeEntrySchema } from './paymentDisputes';
import { NOTIFICATION_KINDS } from './notificationKinds';
import { SellerEntityBrFiscalSchema } from './brFiscal';
import {
  Capability,
  CommercialMode,
  Sku,
  SubscriptionItemOrigin,
  ALL_SUBSCRIPTION_ITEM_ORIGINS,
  ALL_ACTIVITY_ACTIONS,
  ALL_ACTIVITY_SCOPES,
  ALL_ADMIN_ROLES,
  ALL_ALLERGENS,
  ALL_AUDIT_ACTIONS,
  ALL_AUDIT_ACTOR_TYPES,
  ALL_AUDIT_BILLING_ACTIONS,
  ALL_AUDIT_TARGET_TYPES,
  ALL_BARCODE_KINDS,
  ALL_BARCODE_SOURCES,
  ALL_BILLING_CYCLES,
  ALL_BLOCK_ALIGNS,
  ALL_ADS_CONVERSION_CHANNELS,
  ALL_ADS_CONVERSION_EVENTS,
  ALL_ADS_CUSTOMER_SEGMENTATIONS,
  ALL_CAPABILITIES,
  ALL_CHECKOUT_ATTEMPT_STATUSES,
  ALL_CHECKOUT_ORIGINS,
  ALL_COMMERCIAL_MODES,
  ALL_PRICING_SOURCES,
  ALL_COUNTRIES,
  ALL_COUPON_DURATIONS,
  ALL_CURRENCIES,
  ALL_FISCAL_PROVIDERS,
  ALL_NFSE_STATUSES,
  NfseStatus,
  ALL_LIFETIME_FREE_CATEGORIES,
  ALL_PROMOTION_SCOPES,
  ALL_SELLER_ENTITY_STATUSES,
  ALL_YEARLY_DISCOUNT_MODES,
  ALL_ENTITY_TYPES,
  ALL_INVOICE_STATUSES,
  ALL_LABEL_STATUSES,
  ALL_OTP_CHANNELS,
  ALL_PAYMENT_METHOD_STATUSES,
  ALL_PAYMENT_METHOD_TYPES,
  ALL_PAYMENT_PROVIDERS,
  ALL_PAYMENT_MODES,
  ALL_STATUS_CHANGED_VIA,
  ALL_PAYMENT_STATUSES,
  ALL_PLAN_IDS,
  ALL_PRINT_PROTOCOLS,
  ALL_PRINTER_SPEED_MODES,
  ALL_PRINTER_IDENTITY_TYPES,
  ALL_PRINT_TRANSPORTS,
  ALL_PRODUCT_CATEGORIES,
  ALL_REFUND_REASONS,
  ALL_REFUND_STATUSES,
  ALL_SLOT_CONTENT_KINDS,
  ALL_SLOT_IMAGE_REF_KINDS,
  ALL_STORAGES,
  ALL_SUBSCRIPTION_STATUSES,
  ALL_SUSPENSION_REASONS,
  ALL_KV_ALIGNS,
  ALL_TENANT_KINDS,
  ALL_TENANT_ROLES,
  ALL_TENANT_STATUSES,
  ALL_TEXT_SCALES,
  ALL_UNITS_OF_MEASURE,
  ALL_USER_STATUSES,
  ALL_VALIDITY_KINDS,
  ALL_VALIDITY_ORIGINS,
  ALL_VERTICAL_FITS,
} from './enums';
import type { LabelStatus } from './enums';

// ---------- Branded IDs ----------
type Brand<K, T> = K & { readonly __brand: T };
export type TenantId = Brand<string, 'TenantId'>;
export type UserId = Brand<string, 'UserId'>;
export type ProductId = Brand<string, 'ProductId'>;
export type LabelId = Brand<string, 'LabelId'>;
export type LabelTemplateId = Brand<string, 'LabelTemplateId'>;
export type PrinterId = Brand<string, 'PrinterId'>;
export type ActivityEventId = Brand<string, 'ActivityEventId'>;
export type DeviceId = Brand<string, 'DeviceId'>;
export type ResponsibleId = Brand<string, 'ResponsibleId'>;
export type SupplierId = Brand<string, 'SupplierId'>;

const nonEmpty = z.string().min(1);

/**
 * Sprint 35 PR G1 — `SkuSchema` declarado cedo porque outros schemas
 * deste arquivo (`PlanPriceSchema`, `SubscriptionItemSchema`,
 * `InvoiceLineSchema`) o usam antes do bloco
 * "Sprint 35 — Pricing platform schemas" lá embaixo (TDZ se for
 * declarado lá). A doc completa do schema fica no bloco original
 * (busque "PR G1 (2026-05-25)").
 */
export const SkuSchema = z.string().regex(/^[A-Z][A-Z0-9_]{2,30}$/, {
  message: 'SKU deve começar com letra maiúscula, conter só A-Z/0-9/_ e ter 3–31 caracteres.',
});

/**
 * #395 — chave de capability **derivada** de `items[]`. Formato igual ao do
 * SKU porque é literalmente o SKU do item (com `BASE` → `BASE_ACTIVE`).
 *
 * Aberto de propósito, e não `z.enum(ALL_CAPABILITIES)`: SKU é livre em
 * runtime desde o PR G1 (super-admin cria add-on sem deploy) e o reconciler
 * de webhook grava `caps.add(it.sku)` cru. Com o enum fechado, o primeiro
 * add-on novo em produção gravaria uma capability desconhecida e o
 * `safeParse` da subscription passaria a falhar em TODA callable de billing
 * daquele tenant — a validação fechada quebrava mais do que protegia.
 *
 * O enum `Capability` e o `CapabilitySchema` seguem fechados onde a lista
 * é curada por gente: `required_capabilities` de plano e de feature.
 */
export const CapabilityKeySchema = z.string().regex(/^[A-Z][A-Z0-9_]{2,30}$/, {
  message: 'Capability deve começar com letra maiúscula, conter só A-Z/0-9/_ e ter 3–31 caracteres.',
});

/**
 * B-19 — IDs de objetos externos (gateways de pagamento) com formato
 * permissivo mas explícito. Catch principal: detectar paste errado de
 * outro tipo de ID (super-admin colando `pi_*` num campo que esperava
 * `cus_*`). Não tenta validar autenticidade.
 *
 * Stripe IDs: `<prefix>_<base62>` ou `<prefix>_<live|test>_<base62>`.
 *  - `cus_*` (Customer), `sub_*` (Subscription), `in_*` (Invoice),
 *    `pi_*` (PaymentIntent), `ch_*` (Charge), `re_*` (Refund),
 *    `pm_*` (PaymentMethod), `cs_*` (CheckoutSession).
 *  - Comprimento típico ≥ 14, mas o formato "test_" pode ser maior.
 *
 * Regex: prefixo letras + `_` + base62 com eventual `live_`/`test_` no
 * meio. Tamanho mínimo total = 8 (prefixo curto + underscore + 5+
 * caracteres).
 */
const externalProviderId = z
  .string()
  .regex(/^[a-z]{2,5}_(live_|test_)?[A-Za-z0-9]{6,}$/, {
    message: 'ID externo do gateway com formato inválido (esperado <prefix>_<base62>).',
  });

/** O mesmo formato, exportado para validar id do gateway na borda de um
 *  webhook antes de gravá-lo (ex.: `handleChargeDispute`, #740). */
export const ExternalProviderIdSchema = externalProviderId;

/**
 * #748 — id de objeto do gateway que pode NÃO ter prefixo: o Asaas usa
 * `cus_`/`sub_`/`pay_` (casam com `externalProviderId`), mas parcelamento,
 * checkout, autorização Pix Automático e carteira são UUID (medido no spike
 * A0, `docs/billing/asaas.md`). Usar só onde um UUID é legítimo — os campos
 * de assinatura/cliente continuam no formato com prefixo.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1; consequência da pergunta 12 do
 * spike A0, #749).
 *
 * Usado por: `SubscriptionSchema.installment_plan_id`; `CheckoutAttemptSchema`
 * (`id`, `gateway_object_id`, `gateway_object_ids`) — este arquivo.
 *
 * Dados: `"sub_000000000000"` (prefixo) ou
 * `"00000000-0000-4000-8000-000000000000"` (UUID do checkout/autorização Pix).
 *
 * Dicas: não usar em `external_subscription_id`/`external_customer_id` — ali
 * um UUID é sinal de bug (id de checkout gravado no lugar da assinatura).
 */
export const GatewayObjectIdSchema = z.union([
  externalProviderId,
  // UUID em minúsculas, SEM exigir versão/variante (diferente do
  // `PayAttemptIdSchema`, que é o nosso v4): o formato é do Asaas, e o spike
  // só garantiu "UUID minúsculo", não a versão.
  z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/),
]);

/**
 * B-19 — Stripe Price IDs têm prefixo `price_*` (sempre, sem `live_`/`test_`
 * inline). Schema dedicado pra deixar mais claro no PlanPriceSchema.
 */
const stripePriceId = z
  .string()
  .regex(/^price_[A-Za-z0-9]{6,}$/, {
    message: 'Stripe Price ID precisa começar com `price_`.',
  });

/**
 * B-20 — `metadata` em Activity/Audit/etc deve ser um shape limitado a
 * tipos primitivos serializáveis pelo Firestore. Sem isso, qualquer
 * `unknown` cabia (incluindo classes, funções, BigInt, Date), com risco
 * de o Admin SDK falhar silenciosamente ao gravar e o caller assumir
 * sucesso. Aceita `string | number | boolean | null` — outros tipos
 * (Date → millis, etc.) devem ser convertidos antes pelo caller.
 */
const metadataValue = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
const metadataRecord = z.record(z.string(), metadataValue);

/**
 * Locales suportados pelo produto — a lista única que web (`apps/app`,
 * `apps/admin`), Functions (i18n server-side), Android e iOS seguem.
 *
 * Origem: B-21 — sem issue, commit anterior à #86 (antes era a regex
 * `^[a-z]{2}(-[A-Z]{2})?$`, que aceitava `zh-CN`, `de-DE`… e quebrava em
 * silêncio nos lookups de string/e-mail).
 *
 * Por quê: um enum fechado faz o `tenant.locale` desconhecido falhar no parse
 * em vez de cair numa tabela de strings que não existe. Os completos
 * (`FULL_LOCALES`) têm todas as chaves; os regionais (`REGIONAL_LOCALES`)
 * guardam só overrides e caem na base (`pt-PT` → `pt-BR`, `es-XX` → `es`).
 *
 * Contrato: `de` entrou em 2026-10-13 (#86). Valor fora do enum é erro de
 * parse — passe por `normalizeTenantLocale` antes (`de-AT` → `de`).
 *
 * Usado por: `tenant.locale` (`TenantSchema`), `MarketConfigSchema.language`
 * e o input de update de mercado (este arquivo); `assistedSale.ts`,
 * `selfServiceIap.ts`, `countryDefaults.ts`; web `apps/app/src/i18n/index.ts`
 * e `apps/admin/src/i18n/index.ts` (via `ALL_TENANT_LOCALES`); Functions
 * `lib/i18n.ts`; aba Idiomas do admin (`LanguagesTab`, #957); iOS pelo
 * contrato gerado (`Enums.swift`).
 *
 * Arquitetura: os arquivos de string de cada plataforma
 * (`packages/i18n/locales/*.json`, `res/values-*`, `Localizable.xcstrings`)
 * têm de cobrir esta lista — os gates de paridade (`check-parity.mjs`,
 * `check-i18n-parity.sh`, `check-ios-i18n-parity.mjs`) e o manifesto
 * `packages/i18n/coverage.json` (#957) provam isso.
 *
 * Dicas: e-mail e página de pagamento só têm a língua-base (`EmailLocale`,
 * `PayLangSchema`); a conversão regional → base fica em `resolveEmailLocale`
 * e `payLangFromTenantLocale`. Locale novo = este enum + os três arquivos de
 * string + `npm run contract && npm run contract:swift`.
 *
 * Issues: #86 (alemão e idiomas iguais nas plataformas), #957 (aba Idiomas).
 *
 * Histórico:
 *  - 2026-10-13 11:00 +0200 — (#86) `de` e as constantes `ALL_TENANT_LOCALES`,
 *    `FULL_LOCALES`, `REGIONAL_LOCALES`.
 */
export const TenantLocaleSchema = z.enum([
  'pt-BR',
  'pt-PT',
  'en',
  'es',
  'es-AR',
  'es-CL',
  'es-MX',
  'es-PY',
  'it',
  'fr',
  'de',
]);
export type TenantLocale = z.infer<typeof TenantLocaleSchema>;

/** Todos os locales suportados, na ordem do enum (#86). */
export const ALL_TENANT_LOCALES: readonly TenantLocale[] = TenantLocaleSchema.options;

/**
 * Locale regional → locale completo de que ele herda (#86). Regional guarda
 * só overrides; chave ausente cai na base.
 */
export const REGIONAL_LOCALE_BASE = {
  'pt-PT': 'pt-BR',
  'es-AR': 'es',
  'es-CL': 'es',
  'es-MX': 'es',
  'es-PY': 'es',
} as const satisfies Partial<Record<TenantLocale, TenantLocale>>;
export type RegionalLocale = keyof typeof REGIONAL_LOCALE_BASE;

/** Locales regionais (só overrides), na ordem do enum (#86). */
export const REGIONAL_LOCALES: readonly RegionalLocale[] = ALL_TENANT_LOCALES.filter(
  (l): l is RegionalLocale => l in REGIONAL_LOCALE_BASE,
);

/** Locales completos (todas as chaves em toda plataforma), na ordem do enum (#86). */
export const FULL_LOCALES: readonly TenantLocale[] = ALL_TENANT_LOCALES.filter(
  (l) => !(l in REGIONAL_LOCALE_BASE),
);

/**
 * Idiomas-base que o OWNER vê na escolha "Idioma do app" (#959): só a língua,
 * sem variante regional — a variante sai do país do estabelecimento
 * ([resolveAppLocaleForCountry]). Ordem = ordem de exibição.
 *
 * Origem: #959 — 2026-10-13 14:28 +0200 (owner: "não colocar as variações na lista;
 * selecionar a variação automaticamente pelo país do tenant").
 *
 * Usado por: web `LanguageCard`, Android `LanguagePreference`, iOS
 * `ProfileLanguageSection` (as três montam a lista com estas seis + Automático).
 */
export const APP_LOCALE_FAMILIES = ['pt', 'en', 'es', 'it', 'fr', 'de'] as const;
export type AppLocaleFamily = (typeof APP_LOCALE_FAMILIES)[number];

/** Família (língua-base) de um locale: `pt-PT` → `pt`, `es-AR` → `es`, `en` → `en` (#959). */
export function appLocaleFamily(locale: TenantLocale): AppLocaleFamily {
  return locale.split('-')[0] as AppLocaleFamily;
}

/**
 * Família escolhida pelo OWNER + país do tenant → o `TenantLocale` gravado em
 * `tenants/{tid}.appLocale` (#959).
 *
 * Origem: #959 — 2026-10-13 14:28 +0200 (decisão do owner citada em
 * [APP_LOCALE_FAMILIES]).
 *
 * Por quê: a lista não mostra variantes; quem decide entre `es` e `es-AR`, ou
 * `pt-BR` e `pt-PT`, é o país do estabelecimento (`tenant.countryCode`, fixado
 * na venda e imutável — resolver na gravação dá o mesmo que resolver na
 * leitura, e o valor gravado continua um dos 11 que as rules aceitam).
 *
 * Contrato: puro. Regra: o locale da família cuja região é o país
 * (`es` + `AR` → `es-AR`; `pt` + `PT` → `pt-PT`; `pt` + `BR` → `pt-BR`); sem
 * variante para o país (ou país ausente), o locale COMPLETO da família
 * (`pt` → `pt-BR`, `es` → `es`, demais → elas mesmas). Derivada do enum —
 * locale regional novo entra sozinho, sem tabela.
 *
 * Usado por: web `LanguageCard`; Android e iOS espelham a mesma regra (Kotlin
 * `AppLocale.resolveForCountry`, Swift `TenantLocale.resolve(family:country:)`)
 * com teste da mesma tabela de casos.
 *
 * Dados: `("es","AR")` → `"es-AR"`; `("es","CL")` → `"es-CL"`; `("es","ES")` →
 * `"es"`; `("pt","PT")` → `"pt-PT"`; `("pt","IT")` → `"pt-BR"`;
 * `("pt", null)` → `"pt-BR"`; `("it","AR")` → `"it"`.
 */
export function resolveAppLocaleForCountry(
  family: AppLocaleFamily,
  countryCode: string | null | undefined,
): TenantLocale {
  const country = (countryCode ?? '').trim().toUpperCase();
  const regional = ALL_TENANT_LOCALES.find(
    (l) => appLocaleFamily(l) === family && l.split('-')[1] === country && country !== '',
  );
  if (regional) return regional;
  // A versão completa da família: a única de `FULL_LOCALES` com essa língua.
  const full = FULL_LOCALES.find((l) => appLocaleFamily(l) === family);
  return full ?? 'en';
}

/**
 * #752 — o idioma no path das páginas públicas de pagamento
 * (`https://app.etiquetachef.com/{lang}/pay/…`). Só a língua-base: as
 * variantes regionais (`es-AR`, `pt-PT`) caem nela, como no e-mail. Sai do
 * tenant, no servidor — nunca de parâmetro do cliente (owner, 2026-09-27).
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3; anotação do owner na #752).
 *
 * Usado por: `CheckoutAttemptSchema.pay_lang` (este arquivo);
 * `GetPayAttemptOutput.lang` (`payPage.ts`); web `PayLangRoute.tsx` (valida o
 * path); Functions `lib/billing/checkoutReturnUrls.ts`, `billing/asaas/*`.
 *
 * Histórico:
 *  - 2026-10-13 11:00 +0200 — (#86) `de`.
 */
export const PayLangSchema = z.enum(['pt', 'en', 'es', 'it', 'fr', 'de']);
export type PayLang = z.infer<typeof PayLangSchema>;

/**
 * `tenant.locale` → `PayLang`. Ausente ou desconhecido cai na língua do país
 * (BR/PT → `pt`, hispânicos → `es`, IT → `it`, FR → `fr`) e, sem país, em `en`.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3).
 *
 * Contrato: puro, nunca lança; `country` é opcional (chamadores antigos).
 *
 * Usado por: Functions — `billing/asaas/saleLink.ts` (idioma do link e da
 * tentativa), `billing/asaas/payOption.ts`, `lib/billing/checkoutReturnUrls.ts`
 * (retorno do Stripe no idioma do tenant), `admin/changeTenantCommercialMode.ts`.
 *
 * Dados: `("pt-BR", "BR")` → `"pt"`; `("es-AR", "AR")` → `"es"`;
 * `(null, "BR")` → `"pt"`; `(null, null)` → `"en"`.
 *
 * Falhas conhecidas: 2026-09-27 15:48 +0200 (#752, A3, B-L6) — tenant BR sem
 * locale recebia a página em inglês; o fallback pelo país é a correção.
 *
 * Histórico:
 *  - 2026-09-27 14:28 +0200 — (#752) criada, só pelo locale.
 *  - 2026-09-27 15:48 +0200 — (#752, A3, B-L6) fallback pelo país.
 *  - 2026-10-13 11:00 +0200 — (#86) `de` (locale e países DE/AT/CH/LI).
 */
export function payLangFromTenantLocale(locale: string | null | undefined, country?: string | null): PayLang {
  // `pt-BR` → `pt`, `es-AR` → `es`. O `?? ''` só satisfaz o
  // `noUncheckedIndexedAccess` — `split` sempre devolve ao menos um elemento.
  const base = (locale ?? '').trim().toLowerCase().split('-')[0] ?? '';
  const parsed = PayLangSchema.safeParse(base);
  if (parsed.success) return parsed.data;
  // Os países dos mercados com locale próprio (`TenantLocaleSchema`); o
  // resto — e país ausente — vai para `en`, a língua neutra das páginas.
  // Auditoria 2026-09-27 15:48 +0200 (#752, A3, B-L6): sem este `switch`, o
  // tenant BR sem `locale` recebia o link em `/en/pay/…`. Não tirar o
  // fallback pelo país.
  switch ((country ?? '').toUpperCase()) {
    case 'BR':
    case 'PT':
      return 'pt';
    case 'AR':
    case 'CL':
    case 'MX':
    case 'PY':
    case 'ES':
      return 'es';
    case 'IT':
      return 'it';
    case 'FR':
      return 'fr';
    // #86 (2026-10-13): países de língua alemã — o tenant sem locale recebe
    // a página em alemão, como o BR sem locale recebe em português (B-L6).
    case 'DE':
    case 'AT':
    case 'CH':
    case 'LI':
      return 'de';
    default:
      return 'en';
  }
}
const tenantLocale = TenantLocaleSchema;

const CANONICAL_TENANT_LOCALES = new Set<string>(TenantLocaleSchema.options);

/**
 * Coage um locale BCP-47 livre (`pt`, `en-US`, `PT-br`, …) para o enum
 * canônico de `TenantLocaleSchema`. Fonte única — clientes (app/admin) devem
 * passar `tenant.locale` por aqui ANTES do `TenantSchema.safeParse`, senão um
 * locale legado (ex.: `pt` nu, gravado por fluxos antigos do onboarding)
 * derruba o parse inteiro e o doc vira `ERROR` — o que faz gates como o
 * `SuspensionGate`/`PaymentGate` (que só agem em `READY`) falharem ABERTO.
 *
 * Caixa BCP-47 normalizada (idioma minúsculo, região maiúscula); fora do enum,
 * mapeia pela língua-base (`pt` → `pt-BR`). Língua não suportada é devolvida
 * intacta para o Zod recusar e reportar.
 */
export function normalizeTenantLocale(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const [langRaw = '', regionRaw] = value.trim().split('-');
  const lang = langRaw.toLowerCase();
  const canon = regionRaw ? `${lang}-${regionRaw.toUpperCase()}` : lang;
  if (CANONICAL_TENANT_LOCALES.has(canon)) return canon;
  switch (lang) {
    case 'pt':
      return 'pt-BR';
    case 'en':
      return 'en';
    case 'es':
      return 'es';
    case 'it':
      return 'it';
    case 'fr':
      return 'fr';
    case 'de':
      return 'de';
    default:
      return value;
  }
}

/**
 * Branded IDs do wire. O `.meta()` não muda parse nem tipo: descreve o que já
 * está lá (string não-vazia) para que `z.toJSONSchema(..., { io: 'output' })`
 * consiga representar o `transform` do brand. Sem ele, o snapshot de contrato
 * do app iOS (#106 §9) sai vazio para todo id — e o Swift gerado perderia
 * justamente os campos que identificam o documento.
 */
export const TenantIdSchema = nonEmpty
  .transform((s) => s as TenantId)
  .meta({ type: 'string', minLength: 1 });
export const UserIdSchema = nonEmpty
  .transform((s) => s as UserId)
  .meta({ type: 'string', minLength: 1 });
export const ProductIdSchema = nonEmpty
  .transform((s) => s as ProductId)
  .meta({ type: 'string', minLength: 1 });
export const LabelIdSchema = nonEmpty
  .transform((s) => s as LabelId)
  .meta({ type: 'string', minLength: 1 });
export const LabelTemplateIdSchema = nonEmpty
  .transform((s) => s as LabelTemplateId)
  .meta({ type: 'string', minLength: 1 });
export const PrinterIdSchema = nonEmpty
  .transform((s) => s as PrinterId)
  .meta({ type: 'string', minLength: 1 });
export const ActivityEventIdSchema = nonEmpty
  .transform((s) => s as ActivityEventId)
  .meta({ type: 'string', minLength: 1 });
export const DeviceIdSchema = nonEmpty
  .transform((s) => s as DeviceId)
  .meta({ type: 'string', minLength: 1 });
export const ResponsibleIdSchema = nonEmpty
  .transform((s) => s as ResponsibleId)
  .meta({ type: 'string', minLength: 1 });
export const SupplierIdSchema = nonEmpty
  .transform((s) => s as SupplierId)
  .meta({ type: 'string', minLength: 1 });

/**
 * Constructors leves para branded IDs. Type e value coexistem: o
 * `type` está no namespace de tipo, o `const` no namespace de valor.
 *
 * Use estes em vez de `as unknown as TenantId` no boundary cliente
 * (props/state vindos como `string` que precisam virar branded
 * antes de chamar callable/repo). Não validam — só fazem o brand.
 * Para validação real (entrada externa, parse de Firestore raw),
 * use `TenantIdSchema.parse(...)`.
 */
export const TenantId = (s: string): TenantId => s as TenantId;
export const UserId = (s: string): UserId => s as UserId;
export const ProductId = (s: string): ProductId => s as ProductId;
export const LabelId = (s: string): LabelId => s as LabelId;
export const LabelTemplateId = (s: string): LabelTemplateId => s as LabelTemplateId;
export const PrinterId = (s: string): PrinterId => s as PrinterId;
export const ActivityEventId = (s: string): ActivityEventId => s as ActivityEventId;
export const DeviceId = (s: string): DeviceId => s as DeviceId;
export const ResponsibleId = (s: string): ResponsibleId => s as ResponsibleId;
export const SupplierId = (s: string): SupplierId => s as SupplierId;

// ---------- Enums (derivam de lib/enums.ts — fonte única de verdade) ----------
export const TenantKindSchema = z.enum(ALL_TENANT_KINDS);
export const TenantRoleSchema = z.enum(ALL_TENANT_ROLES);
export const StorageSchema = z.enum(ALL_STORAGES);
export const ValidityKindSchema = z.enum(ALL_VALIDITY_KINDS);
export const ValidityOriginSchema = z.enum(ALL_VALIDITY_ORIGINS);
export const PrintProtocolSchema = z.enum(ALL_PRINT_PROTOCOLS);
export const PrinterSpeedModeSchema = z.enum(ALL_PRINTER_SPEED_MODES);
export const PrinterIdentityTypeSchema = z.enum(ALL_PRINTER_IDENTITY_TYPES);
export const PrintTransportSchema = z.enum(ALL_PRINT_TRANSPORTS);
export const LabelStatusSchema = z.enum(ALL_LABEL_STATUSES);

/**
 * Status de **leitura** de uma etiqueta: os do wire mais `'UNKNOWN'` (épico
 * #323 fase 0 — issue #330), que **não existe no Firestore**. É o que o parse
 * devolve quando o doc traz um status que esta versão não conhece (escrito por
 * uma versão mais nova): a etiqueta aparece como histórico genérico, **sem
 * ação disponível**, em vez de sumir da lista sem explicação.
 *
 * Um valor próprio, e não um apelido de `DISCARDED`, porque a diferença não é
 * cosmética: como `DISCARDED` a UI ofereceria "Desfazer baixa" sobre um estado
 * que ninguém entende — no Android o write é cego e destruiria o dado.
 *
 * `LabelStatus`/`LabelStatusSchema` continuam sendo **só o wire**: é o que se
 * escreve, o que as queries filtram e o que a API pública valida. `UNKNOWN`
 * nunca é gravado — se aparecer num payload de escrita, é bug de tipo, e o
 * compilador acusa.
 */
export const ALL_READ_LABEL_STATUSES = [...ALL_LABEL_STATUSES, 'UNKNOWN'] as const;
export type ReadLabelStatus = (typeof ALL_READ_LABEL_STATUSES)[number];
export const ReadLabelStatusSchema = z.enum(ALL_READ_LABEL_STATUSES);
export const UNKNOWN_LABEL_STATUS_FALLBACK: ReadLabelStatus = 'UNKNOWN';

/**
 * `true` quando o valor é um `LabelStatus` que esta versão conhece. Serve para
 * o ponto de leitura **reportar** o desconhecido antes de o `.catch()` do
 * schema aplicar o fallback silenciosamente — sem DevTools do cliente, um
 * fallback mudo é um bug invisível (`feedback_no_client_devtools`).
 */
export function isKnownLabelStatus(value: unknown): value is LabelStatus {
  return typeof value === 'string' && (ALL_LABEL_STATUSES as readonly string[]).includes(value);
}
export const ActivityScopeSchema = z.enum(ALL_ACTIVITY_SCOPES);
export const ActivityActionSchema = z.enum(ALL_ACTIVITY_ACTIONS);
export const EntityTypeSchema = z.enum(ALL_ENTITY_TYPES);
export const ProductCategorySchema = z.enum(ALL_PRODUCT_CATEGORIES);
export const AllergenSchema = z.enum(ALL_ALLERGENS);
export const UnitOfMeasureSchema = z.enum(ALL_UNITS_OF_MEASURE);
export const CountrySchema = z.enum(ALL_COUNTRIES);
/**
 * País de um TENANT (e da assinatura dele): qualquer ISO-3166 alpha-2 — duas
 * letras maiúsculas.
 *
 * Origem: #942 — 2026-10-12 11:40 +0200 (assinatura self-service por IAP no iOS).
 *
 * Por quê: o tenant do IAP nasce com o país do storefront da App Store, que
 * cobre ~175 territórios — não só os 5 mercados de `Country` (BR/MX/US/CA/IT),
 * que continuam significando "mercado com PSP/CRM". Decisão do owner
 * (2026-10-12): "valida 2 letras; permitir qualquer entrada com 2 letras
 * maiúsculas". Conceitos diferentes, schemas diferentes: `CountrySchema`
 * segue fechado onde o campo É um mercado comercial.
 *
 * Contrato: aceita `^[A-Z]{2}$`. Não confere se o código existe na ISO — o
 * servidor só grava país vindo do storefront (já ISO) ou de um `Country`.
 *
 * Usado por: `TenantSchema.countryCode`, `SubscriptionSchema.country_code`.
 * Consumidores que indexam tabela por `Country` (`countryDefaults`, preços,
 * `MarketConfig`) precisam de fallback para país fora dos 5 — ver #942.
 *
 * Falhas conhecidas: até 2026-10-12 o campo era `CountrySchema`; um tenant
 * `JP-…` derrubaria o parse do tenant inteiro no web e no Android.
 */
export const TenantCountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, {
  message: 'countryCode precisa ser ISO-3166 alpha-2 (2 letras maiúsculas).',
});
export const OtpChannelSchema = z.enum(ALL_OTP_CHANNELS);
export const TenantStatusSchema = z.enum(ALL_TENANT_STATUSES);
export const UserStatusSchema = z.enum(ALL_USER_STATUSES);
export const TextScaleSchema = z.enum(ALL_TEXT_SCALES);
export const BlockAlignSchema = z.enum(ALL_BLOCK_ALIGNS);
export const KvAlignSchema = z.enum(ALL_KV_ALIGNS);
export const VerticalFitSchema = z.enum(ALL_VERTICAL_FITS);
export const BarcodeKindSchema = z.enum(ALL_BARCODE_KINDS);
export const BarcodeSourceSchema = z.enum(ALL_BARCODE_SOURCES);
export const SlotContentKindSchema = z.enum(ALL_SLOT_CONTENT_KINDS);
export const SlotImageRefKindSchema = z.enum(ALL_SLOT_IMAGE_REF_KINDS);

/**
 * Telefone em formato E.164 (ex.: +5511987654321). Validação ortográfica;
 * a validação semântica de país acontece via libphonenumber nas Functions.
 */
export const PhoneE164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/);

/**
 * Código OTP de 6 dígitos. Hash SHA-256 é o que é persistido — nunca o código
 * em claro. A validade de 10 minutos do CÓDIGO é conferida pelo `confirmOtp`
 * contra `expiresAt`; o TTL do Firestore NÃO a enforça — ele apaga o DOC
 * `/otps/{id}` pelo campo `expireAt` (Timestamp), até ~24 h depois do prazo.
 * Comentário corrigido em 2026-10-03 18:22 +0200 (#795, FN-6): dizia que o TTL
 * enforçava os 10 min, e não havia política nenhuma declarada.
 */
export const OtpCodeSchema = z.string().regex(/^\d{6}$/);

export type {
  ActivityAction,
  ActivityScope,
  Allergen,
  BarcodeKind,
  BarcodeSource,
  BlockAlign,
  BlockKind,
  Country,
  EntityType,
  FieldSourceKind,
  KvAlign,
  LabelStatus,
  OtpChannel,
  PrintProtocol,
  ProductCategory,
  SlotContentKind,
  SlotImageRefKind,
  Storage,
  TenantKind,
  TenantRole,
  TextScale,
  UnitOfMeasure,
  ValidityKind,
  ValidityOrigin,
  VerticalFit,
} from './enums';

// ---------- Entities (Firestore) ----------
/** #679 §4.2 — ver `TenantSchema.sales_origin`. */
export const TenantSalesOriginSchema = z.object({
  crm_board_id: z.string().min(1).max(128),
  crm_lead_id: z.string().min(1).max(128),
  /** Dono do lead no CRM — quem recebe a comissão da venda. */
  seller_uid: UserIdSchema,
  /** Quem clicou em "gerar link" (pode ser um gerente, não o vendedor). */
  created_by_uid: UserIdSchema,
  created_at: z.number().int().nonnegative(),
});
export type TenantSalesOrigin = z.infer<typeof TenantSalesOriginSchema>;

export const TenantSchema = z.object({
  id: TenantIdSchema,
  name: z.string().min(1).max(120),
  kind: TenantKindSchema,
  // B-21: lista fechada (era `regex /^[a-z]{2}(-[A-Z]{2})?$/`, aceitava
  // qualquer locale BCP-47).
  locale: tenantLocale,
  /**
   * Idioma do app para todos os membros deste estabelecimento (#959 — 2026-10-13 13:30 +0200).
   * Escolhido só pelo OWNER, no Perfil (web `LanguageCard`, Android
   * `ProfileScreen`, iOS `ProfileView`); web, Android e iOS aplicam o do
   * tenant ATIVO a quem estiver logado. Ausente ou `null` = automático (cada
   * aparelho no próprio idioma, inglês de reserva). Diferente de `locale`
   * (acima), que é o idioma de e-mails/cobrança e está sempre gravado.
   * Rules: `isValidAppLocale` (só OWNER, só os 11 valores). `.catch(null)`:
   * um locale que esta versão não conhece degrada para automático em vez de
   * derrubar o parse do tenant inteiro — linha em `CONTRACT_FALLBACKS`.
   */
  appLocale: TenantLocaleSchema.nullable().optional().catch(null),
  timeZone: z.string(),
  /**
   * País da Matriz (Fase 11). Fixado pelo telefone OTP do criador, não editável
   * depois — mudar país = nova Matriz. Tenants pré-Fase 11 ganham `BR` no
   * backfill com `needsTaxId: true`.
   *
   * #942 (2026-10-12): qualquer ISO alpha-2 — o tenant do IAP herda o país do
   * storefront da App Store, que pode estar fora dos 5 mercados de `Country`.
   */
  countryCode: TenantCountryCodeSchema.optional(),
  /**
   * Região Firebase do tenant (banco, bucket, RTDB, Functions) — #960, F2 #1078
   * (2026-10-29 09:31 +0100). Gravada na criação (`buildTenantProvisionDocs`, com a região
   * resolvida por `resolveTenantRegion`) e imutável fora da migração da F8 (#1086); o OWNER não
   * a altera (`firestore.rules`, lista de campos proibidos do tenant). **Ausente =
   * `southamerica-east1`** (todo tenant anterior à #960). Sem `.catch()` DE PROPÓSITO: valor
   * desconhecido reprova o parse — ler um tenant europeu como SA mandaria as escritas para o banco
   * errado; quem lê bloqueia e reporta (enum fechado no contrato, `CONTRACT_ENUMS`).
   */
  region: TenantRegionSchema.optional(),
  /**
   * Identificação fiscal da Matriz (CNPJ/CUIT/RUT/RUC/RFC/EIN/P.IVA conforme
   * `countryCode`). Obrigatório para tenants criados via `completeOnboarding`.
   * Filiais não têm taxId próprio — herdam o do tenant.
   */
  taxId: z.string().max(40).optional(),
  /**
   * Razão social da Matriz, congelada na venda assistida a partir do lead do CRM
   * (`runSaleProvision`, `billing/provisionTenantForSale.ts`). `name` é o nome
   * FANTASIA; este é o nome do documento.
   *
   * Origem: #998 — 2026-10-18 14:22 +0200.
   *
   * Por quê: na venda RESELLER (#965) o tomador da NFS-e é o parceiro, e o
   * corpo da nota identifica o cliente final pela razão social + CNPJ + plano
   * + módulos (decisão do owner, 2026-10-18). A revenda no BR exige o campo.
   *
   * Segurança — NUNCA editável pelo OWNER (owner, 2026-10-18, #998): este
   * texto vai no corpo da NFS-e emitida para o PARCEIRO, e na revenda o OWNER é
   * o cliente final. Se ele pudesse editá-lo, escreveria o que quisesse num
   * documento fiscal de outra empresa. Por isso: está na blocklist do update
   * do OWNER em `firebase/firestore.rules` (teste em
   * `firebase/test/rules/tenants.test.ts`); não entra em `updateTenantBasics`
   * nem em tela de Perfil (web, Android, iOS); e só o Admin SDK escreve.
   * Correção de razão social errada, se um dia existir, é callable de admin
   * com audit — nunca o cliente. Mesma classe do `taxId`.
   *
   * Usado por: Functions `licensedClientDescription`
   * (`billing/asaas/nfseDescription.ts`, NFS-e do Asaas). Escrito só pelo
   * servidor (venda e `scripts/backfill-tenant-legal-name.ts`). Ausente em
   * tenant de onboarding/self-service e nos anteriores à #998 — a nota
   * identifica só pelo CNPJ (nunca pelo `name`, que o OWNER edita).
   */
  legalName: z.string().max(160).optional(),
  /**
   * Sinaliza tenants pré-Fase 11 que precisam preencher taxId. UI exibe banner
   * persistente até regularização.
   */
  needsTaxId: z.boolean().optional(),
  /**
   * Endereço do estabelecimento (Sprint 16 — fixado no
   * `completeOnboarding` via Google Places Autocomplete). `placeId`
   * é o canônico (estável); `address` e coords são derivados,
   * podem mudar com o tempo. Optional para tenants criados antes
   * dos campos terem sido introduzidos no schema.
   */
  address: z.string().max(300).optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  placeId: z.string().max(300).nullable().optional(),
  /**
   * Logo do tenant — usado em blocos `BlockKind.TENANT_LOGO` de templates
   * de etiqueta. Storage path canônico: `tenants/{id}/branding/logo.png`.
   * `logoUrl` é o download URL cacheado (lido pelo cliente sem nova auth);
   * `logoPath` é o path do Storage para deleção/upload. Ambos são
   * write-only-via-callable (`updateTenantLogo`).
   *
   * `''` é aceito só como legado (#571): até 2026-10-08 o `removeTenantLogo`
   * gravava string vazia em vez de apagar os campos, e o doc parava de passar
   * neste schema — o web travava em "Carregando…" no `SuspensionGate`
   * (fail-closed, #795) e o admin sumia o tenant da busca. Hoje a callable
   * apaga os campos; `''` continua significando "sem logo" para os docs já
   * gravados. Consumidor testa `length > 0`, nunca só `!== undefined`.
   */
  logoUrl: z.union([z.url().max(1000), z.literal('')]).optional(),
  logoPath: z.string().max(500).optional(),
  createdAt: z.number().int().nonnegative(),
  /**
   * Millis epoch da primeira etiqueta impressa. Gravado server-side pelo
   * trigger `firstLabelPrinted` na primeira impressão; ausente enquanto o
   * tenant nunca imprimiu. Sinal do passo "Imprimir" no Guia de Primeiros
   * Passos da Home (`docs/screens/home.md` §4.A).
   */
  firstLabelAt: z.number().int().nonnegative().optional(),
  /**
   * UID do usuário "mãe" — quem criou o tenant (via `completeOnboarding` ou
   * `createTenant`). Imutável. Esse membro nunca pode ser removido do tenant
   * (gate em `removeUserFromTenant` + `firestore.rules`). Optional para
   * tenants criados antes do campo existir.
   */
  founderUid: UserIdSchema.optional(),
  /**
   * Tenant de demonstração comercial (usado em demos do CRM). Excluído de
   * relatórios internos e de métricas de negócio — ver
   * `dailyInactiveTenantsReport`. Só super-admin escreve, via o callable
   * `setTenantDemoFlag`; `firestore.rules` bloqueia o OWNER de tocar no
   * campo (senão ele se esconderia dos relatórios sozinho). Ausente =
   * `false` para todo consumidor.
   *
   * NÃO confundir com `isDemo` do doc de MEMBRO
   * (`/tenants/{tid}/members/{uid}`), que marca o *convidado* de uma
   * concessão de acesso demo do CRM (`crm-functions/lib/demoAccess.ts`) —
   * lá o campo qualifica a pessoa, aqui qualifica o estabelecimento.
   */
  isDemo: z.boolean().optional(),
  /**
   * #679 §4.2 — o tenant nasceu de uma venda assistida pelo CRM
   * (`provisionTenantForSale`). Ausente = nasceu pelo self-service ou pelo
   * admin. Escrito só pelo Admin SDK, na criação; lido pelo CRM para ganhar o
   * lead no pagamento e por `onTenantCreated` para NÃO mandar boas-vindas nem
   * conversão de signup a quem ainda não pagou nem tem senha.
   *
   * `.catch(undefined)`: um objeto malformado não pode derrubar o parse do
   * tenant inteiro no cliente (lição do #95) — o campo não é lido lá.
   */
  sales_origin: TenantSalesOriginSchema.optional().catch(undefined),
  /**
   * #743 (F8 da #679) — parceiro (`crm_partners/{id}`) que vendeu e atende
   * este tenant, quando `billing/subscription.commercial_mode` é `PARTNER`.
   * Chave do extrato de repasse e do portal do parceiro. O parceiro NÃO é
   * membro do tenant (9.15): não vê dado operacional; o suporte é nosso.
   *
   * `.catch(undefined)` pelo mesmo motivo de `sales_origin`.
   */
  partner_id: z.string().min(1).max(128).nullable().optional().catch(undefined),
  /**
   * Status operacional do tenant (Sprint 1 — substitui o antigo `active:
   * boolean`). Mutável via callables admin (`suspendTenant`,
   * `reactivateTenant`) e pelo scheduler de billing (`SUSPENDED_BILLING`
   * quando atrasa pagamento além do graceDays).
   */
  status: TenantStatusSchema,
  /**
   * Millis epoch da última transição de `status`. Setado pela mesma
   * callable/scheduler que altera o status.
   */
  statusChangedAt: z.number().int().nonnegative().optional(),
  /**
   * Sprint 19 — millis epoch da última revogação de sessões do tenant.
   * Setado por `requestTenantDeletion` (e por `restoreTenant` quando o
   * tenant volta) para invalidar tokens cacheados nos clientes. Cliente
   * Android/web compara o `sessionEpoch` lido em força-refresh contra
   * o último visto e desloga se mudou.
   */
  sessionEpoch: z.number().int().nonnegative().optional(),
  /**
   * Sprint 19 — millis epoch do soft-delete via super-admin. Set
   * juntamente com `status === DELETED`; limpo pelo `restoreTenant`.
   * Job de retenção LGPD (Sprint 13) usa `deletedAt + 90d` para apagar
   * subcoleções fisicamente.
   */
  deletedAt: z.number().int().nonnegative().optional(),
  /**
   * Sprint 19 — UID do super-admin que executou o soft-delete. Auditoria
   * cruza com `/audit/{eventId}` (`AuditAction.TENANT_DELETED`).
   */
  deletedByUid: UserIdSchema.optional(),
  /**
   * Sprint 19 — motivo livre informado pelo super-admin no soft-delete.
   * Sanitizado no callable (sem HTML/markup); usado em audit log e
   * telas internas.
   */
  deletionReason: z.string().max(500).optional(),
});
export type Tenant = z.infer<typeof TenantSchema>;

/**
 * Perfil do usuário em `/profiles/{uid}`. Campos `*Verified*`, `*VerifiedAt`
 * e `phoneCountry` são write-only-via-callable (rules bloqueiam write direto).
 * `simCountry`/`networkCountry` são telemetria gravada após login.
 *
 * Sprint 22 PR E.4: `createdAt`/`updatedAt` viraram nullable porque o
 * cliente agora escreve sem esses campos — o trigger `onProfileWritten`
 * carimba server-side (evita clock skew). Docs antigos já têm valor.
 */
/**
 * Consentimento de mídia paga (Meta CAPI e futuras redes). Separado do
 * consentimento de analytics (PostHog/Firebase) de propósito — LGPD/GDPR
 * tratam publicidade direcionada com base legal própria. Vive em
 * `/profiles/{uid}.adsConsent`. Postura **legítimo interesse / opt-out**
 * (decisão owner 2026-06-28, foco LATAM): ausência = consentido; só
 * `ads === false` (opt-out explícito do titular) bloqueia o envio de PII.
 * Escrito SÓ via callable `setMyAdsConsent` (Admin SDK) — a rule de update
 * de `/profiles` não whitelista esta chave, então escrita direta do cliente
 * é bloqueada. Ver issue #93 Bloco E.
 */
export const AdsConsentSchema = z.object({
  ads: z.boolean(),
  updatedAt: z.number().int().nonnegative(),
  /** Plataforma onde o titular deu/retirou o consentimento (auditoria). */
  source: z.enum(['web', 'android', 'ios']).optional(),
});
export type AdsConsent = z.infer<typeof AdsConsentSchema>;

export const ProfileSchema = z.object({
  uid: UserIdSchema,
  displayName: z.string().max(120).default(''),
  email: z.email().or(z.literal('')).default(''),
  emailVerified: z.boolean().default(false),
  emailVerifiedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * #942 — primeiro pedido de código pelo OTP de e-mail do self-service do iOS
   * (`requestEmailOtp`, servidor). Marca a conta como nascida no "Criar conta"
   * do iPhone: só conta com esta marca pode ser apagada pela varredura de
   * contas abandonadas (`selfServiceAbandonedAccountsSweep`). Ausente em toda
   * conta de outro caminho (convite, CRM, web).
   */
  selfServiceOtpRequestedAt: z.number().int().nonnegative().nullable().optional(),
  phoneE164: z.string().nullable().optional(),
  phoneCountry: CountrySchema.nullable().optional(),
  phoneVerifiedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * O perfil nasceu de um CONVITE (hoje: demonstração concedida pelo CRM), não
   * de cadastro self-serve. Quem entra assim já chega com e-mail e telefone
   * carimbados — ver `preVerifiedBy`.
   */
  isInvited: z.boolean().optional(),
  /**
   * Quem carimbou a verificação de contato quando ela NÃO veio de OTP/clique no
   * link de e-mail. Escrito só por Admin SDK; as rules proíbem o cliente de
   * semear os campos `*Verified*` (A-23).
   *
   * Valores conhecidos em `PreVerifiedBy`: `CRM_DEMO` (demonstração concedida
   * pelo CRM), `ADMIN` (super-admin carimbou pela tela global de Usuários,
   * issue #257), `CRM_INVITE` (aceite de convite de colaborador do CRM — o
   * token do convite prova a posse do e-mail, issue #269) e `CRM_SELF_DEMO`
   * (self-healing do auto-acesso à demonstração — uid == ator, issue #269) e
   * `CRM_SALE` (OWNER provisionado na venda assistida, #679 9.6).
   * Campo puramente descritivo — nenhum gate o consulta.
   *
   * **Tipado como string livre DE PROPÓSITO, não como `z.enum`.** Um enum faz
   * o `safeParse` do perfil INTEIRO falhar em cliente com bundle antigo assim
   * que um valor novo aparece; no web isso vira `ProfileDocResult.ERROR` →
   * `profile: null` → `evaluateUser` devolve `PHONE_NOT_VERIFIED`
   * (`apps/app/src/auth/authStatus.ts:52`), ou seja, **usuário legítimo
   * expulso para `/verify-contact`**. Writers usam o type `PreVerifiedBy`;
   * leitores toleram desconhecidos.
   */
  preVerifiedBy: z.string().max(40).optional(),
  simCountry: z.string().length(2).nullable().optional(),
  networkCountry: z.string().length(2).nullable().optional(),
  adsConsent: AdsConsentSchema.nullable().optional(),
  /**
   * #679 9.13 — aceite dos Termos de Uso e da Política de Privacidade.
   * Escrito só pelo servidor: pelo `checkout.session.completed` (a partir do
   * `consent` da própria sessão, na venda DIRECT) ou pela callable
   * `acceptTerms` (gate de primeiro login). `termsVersion` é a versão aceita,
   * para um aceite antigo não valer pelos termos novos.
   */
  termsAcceptedAt: z.number().int().nonnegative().nullable().optional(),
  termsVersion: z.string().max(40).nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type Profile = z.infer<typeof ProfileSchema>;

/**
 * Valores canônicos de `Profile.preVerifiedBy`. Use este tipo ao **escrever**;
 * o schema aceita string livre por resiliência de leitura (ver o comentário do
 * campo).
 */
export type PreVerifiedBy =
  | 'CRM_DEMO'
  | 'ADMIN'
  | 'CRM_INVITE'
  | 'CRM_SELF_DEMO'
  /** #679 9.6 — OWNER do cliente, provisionado na venda assistida pelo CRM. */
  | 'CRM_SALE';

/**
 * Registro de OTP em `/otps/{phoneE164Hash}`. `codeHash` é SHA-256 do código
 * concatenado com o `salt` aleatório por registro. `expiresAt` é a validade
 * do CÓDIGO (10 min), conferida pelo `confirmOtp` — NÃO aciona TTL. O TTL do
 * doc é o campo `expireAt` (Timestamp, ≈ envio + 10 min + 24 h de lockout),
 * gravado por `requestOtp` e com política `otps.expireAt` em
 * `firebase/firestore.indexes.json`. `attempts` enforça 3 tentativas;
 * `lastAttemptAt` + `lockedUntil` enforça cooldown de 24h após estouro.
 *
 * Atenção: o `requestOtp` grava `expiresAt` como `Timestamp`, não número —
 * este schema não é usado para ler `/otps` hoje; usá-lo exige alinhar o tipo.
 *
 * Comentário corrigido em 2026-10-03 18:22 +0200 (#795, FN-6): afirmava que
 * `expiresAt` acionava TTL, e não havia política de TTL para `/otps` — o doc,
 * com telefone em claro e IP, vivia para sempre.
 */
export const OtpRecordSchema = z.object({
  destination: z.string(),                      // phone E.164
  destinationHash: z.string(),                  // SHA-256(destination) — doc id
  channel: OtpChannelSchema,
  codeHash: z.string(),                          // SHA-256(salt + code)
  salt: z.string(),
  // M-35: regex permissiva para IPv4/IPv6 (caracteres permitidos em IPs).
  // Sem isso, atacante injetava string randomizada por request e burlava
  // rate-limit por IP (índice otps.ip+createdAt).
  ip: z.string().regex(/^[0-9a-fA-F:.]{2,45}$/).optional(),
  attempts: z.number().int().nonnegative().default(0),
  maxAttempts: z.number().int().positive().default(3),
  lockedUntil: z.number().int().nonnegative().nullable().optional(),
  // B-18: enum em vez de string solta. Drift entre código e Twilio fica
  // explícito no parse — antes, qualquer string era aceita silenciosamente.
  // Os valores cobrem o callback do Twilio Messages API:
  // https://www.twilio.com/docs/messaging/guides/track-outbound-message-status
  deliveryStatus: z
    .enum(['queued', 'sent', 'delivered', 'failed', 'undelivered'])
    .nullable()
    .optional(),
  deliveryError: z.string().nullable().optional(),
  twilioSid: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),     // validade do código (NÃO é o campo de TTL — #795)
});
export type OtpRecord = z.infer<typeof OtpRecordSchema>;

/**
 * Preferências de notificação push por-membro. Subconjunto dos
 * `NOTIFICATION_KINDS` → ligado/desligado. Campo ausente = default do kind
 * (ver `NOTIFICATION_DEFAULTS`/`resolveNotificationPrefs`); não há backfill.
 * Gravado pelo próprio membro via dot-path (`notificationPrefs.<kind>`).
 */
export const NotificationPrefsSchema = z
  .object(
    Object.fromEntries(NOTIFICATION_KINDS.map((kind) => [kind, z.boolean()])) as Record<
      (typeof NOTIFICATION_KINDS)[number],
      z.ZodBoolean
    >,
  )
  .partial();
// O tipo canônico `NotificationPrefs` vive em `notificationKinds.ts` (puro,
// sem Zod) e é estruturalmente idêntico ao inferido deste schema.

export const MemberSchema = z.object({
  uid: UserIdSchema,
  tenantId: TenantIdSchema,
  role: TenantRoleSchema,
  displayName: z.string().max(120),
  email: z.email().or(z.literal('')),
  /**
   * Telefone do membro no contexto do tenant (gravado por `inviteUserToTenant`).
   * Ausente para o OWNER criado no onboarding (o telefone dele vive em
   * `/profiles/{uid}`). `''` aceito por dados legados.
   */
  phoneE164: PhoneE164Schema.or(z.literal('')).optional(),
  joinedAt: z.number().int().nonnegative(),
  /**
   * Foto de perfil do membro (URL do Storage). Réplica de
   * `profiles/{uid}.photoUrl` — o app faz fan-out para todos os tenants do
   * usuário quando a foto muda, e é por isso que ela, e não o snapshot do
   * Firebase Auth, é a fonte canônica na UI (#321). Ausente em quem nunca
   * definiu foto. String livre porque URLs do Storage carregam token de
   * download; `z.url()` recusaria formatos legados sem ganho real.
   */
  photoUrl: z.string().max(2000).optional(),
  /** Preferências de notificação push (por-tenant). Ausente em membros legados. */
  notificationPrefs: NotificationPrefsSchema.optional(),
  // `lastWasteReportMonth` e `lastWasteReportRequestedAtMillis` saíram do
  // schema em 2026-10-07 12:41 +0200 (#664): o relatório mensal de perdas
  // (#308) foi removido. Docs antigos ainda os têm em produção — o parse sem
  // `.strict()` os descarta, e nada mais os lê. Não reintroduzir.
});
export type Member = z.infer<typeof MemberSchema>;

/**
 * Doc `tenants/{tenantId}/devices/{deviceId}` — registro de um aparelho
 * Android acoplado ao estabelecimento. `deviceId` = `ANDROID_ID` do
 * aparelho (1 doc por aparelho físico, não por usuário).
 *
 * Escrita dividida em duas fontes:
 *  - **Apps Android e iOS** (heartbeat ao entrar em foreground, sempre com
 *    merge): `platform`, `deviceModel`, `appVersion`, `lastUserUid`,
 *    `lastUserName`, `lastUserPhotoUrl`, `lastSeenAtMillis`/`lastSyncAtMillis`.
 *  - **Cloud Function `onPresenceWritten`** (trigger RTDB, Admin SDK):
 *    `online`, `presenceChangedAtMillis` e `presenceSourceAtMillis` (#1040),
 *    espelhados de `presence/{tenantId}/{deviceId}` no Realtime Database
 *    (e `deviceId`/`tenantId`/`lastUserUid` quando cria o doc).
 *
 * Origem: sem issue — commit 0aa0afec2, 2026-05-11 20:18 +0200 (presença via
 * RTDB + espelho no Firestore).
 *
 * Usado por: web `apps/app` `lib/useDevices.ts` (card Dispositivos, parse com
 * este schema); Android `FirestoreDeviceRepository.kt` e iOS
 * `Data/Sources/Devices/FirestoreDeviceRepository.swift` (campo a campo, à mão);
 * Functions `onPresenceWritten`, `lib/tenantDevices.ts` (apaga no
 * `removeUserFromTenant`), `admin/courtesyMigration.ts` (lê o build);
 * integração `rules-x-functions.itest.ts`.
 *
 * O nome de exibição canônico do usuário é `/members/{uid}.displayName`;
 * `lastUserName` aqui é só um snapshot de fallback. A foto (`lastUserPhotoUrl`)
 * vem do Firebase Auth (login Google) e não existe em outro lugar do
 * Firestore — por isso é propagada aqui.
 */
export const DeviceSchema = z.object({
  deviceId: DeviceIdSchema,
  tenantId: TenantIdSchema,
  platform: z.enum(['android', 'web', 'ios']).default('android'),
  deviceModel: z.string().max(120).default(''),
  appVersion: z.string().max(60).default(''),
  lastUserUid: UserIdSchema.or(z.literal('')).default(''),
  lastUserName: z.string().max(120).default(''),
  lastUserPhotoUrl: z.string().max(2000).default(''),
  /** Heartbeat: última vez que o app entrou em foreground / sincronizou. */
  lastSeenAtMillis: z.number().int().nonnegative().nullable().optional(),
  /** Compat com o card legado (Sprint 21 D.1) — mesmo valor de `lastSeenAtMillis`. */
  lastSyncAtMillis: z.number().int().nonnegative().nullable().optional(),
  /** Espelhado de RTDB pelo trigger `onPresenceWritten`. */
  online: z.boolean().default(false),
  /** Millis epoch da última transição de `online` (espelhado de RTDB). */
  presenceChangedAtMillis: z.number().int().nonnegative().nullable().optional(),
  /**
   * Hora (`event.time`, relógio do servidor do RTDB) do último evento de
   * presença que o espelho aplicou. Só o trigger `onPresenceWritten` escreve e
   * só ele lê — é a guarda de ordem que descarta evento atrasado (#1040,
   * 2026-10-27). A rule de `devices` recusa o campo vindo do cliente (um valor
   * no futuro travaria o espelho). Ausente em doc anterior à correção. Não use
   * para UI: o "visto por último" é `presenceChangedAtMillis`.
   */
  presenceSourceAtMillis: z.number().int().nonnegative().optional(),
});
export type Device = z.infer<typeof DeviceSchema>;

/**
 * Nó `presence/{tenantId}/{deviceId}` no Realtime Database. Escrito pelo
 * app Android ao entrar em foreground, com `onDisconnect()` registrado
 * para virar `online: false` quando o app fecha ou perde rede (offline
 * praticamente instantâneo). Só o trigger `onPresenceWritten` lê (via
 * Admin SDK) — clientes nunca leem RTDB, leem o espelho em Firestore.
 */
export const DevicePresenceSchema = z.object({
  online: z.boolean(),
  uid: UserIdSchema,
  /** `ServerValue.TIMESTAMP` resolvido (millis epoch). */
  lastChangedAt: z.number().int().nonnegative(),
});
export type DevicePresence = z.infer<typeof DevicePresenceSchema>;

/**
 * Token FCM (registration token). Web: ~150-220 chars; Android pode ser
 * maior. Cap generoso só para barrar lixo óbvio — não valida formato.
 */
const fcmToken = z.string().min(1).max(4096);
const pushUserAgent = z.string().max(500);

/**
 * Doc `tenants/{tenantId}/pushTokens/{token}` — registração de um device
 * (web ou Android) para FCM push. O `{token}` do path é o próprio
 * registration token. Escrito pelos callables `registerPushToken` /
 * `unregisterPushToken`; os jobs server-side de fan-out
 * (`sendEachForMulticast`) usam esses tokens e removem os que voltarem
 * `messaging/registration-token-not-registered`.
 *
 * Endereçamento por papel (`sendNotification`, `sendWebPushToAllOwners`) usa esta coleção
 * cruzando `uid` com `members.role`. O tópico FCM legado
 * `tenant_{tenantId}_alerts` foi descontinuado — todo fan-out (broadcast
 * tenant ou direcionado por papel) passa por tokens em `pushTokens`.
 */
export const PushTokenSchema = z.object({
  token: fcmToken,
  tenantId: TenantIdSchema,
  uid: UserIdSchema,
  platform: z.enum(['web', 'android', 'ios']).default('web'),
  userAgent: pushUserAgent.default(''),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type PushToken = z.infer<typeof PushTokenSchema>;

/**
 * Doc `/adminPushTokens/{token}` (top-level) — registração de um device do
 * console super-admin (`apps/admin`) para web push. Sem `tenantId` porque
 * super-admins não são membros de tenant. Hoje a infra existe mas nenhum
 * evento faz fan-out para super-admins (ver `reference-fcm-web-push`).
 */
export const AdminPushTokenSchema = z.object({
  token: fcmToken,
  uid: UserIdSchema,
  platform: z.enum(['web']).default('web'),
  userAgent: pushUserAgent.default(''),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type AdminPushToken = z.infer<typeof AdminPushTokenSchema>;

/** Input do callable `registerPushToken` (web, Android e iOS — #106). */
export const RegisterPushTokenInput = z.object({
  tenantId: TenantIdSchema,
  token: fcmToken,
  userAgent: pushUserAgent.optional(),
  platform: z.enum(['web', 'android', 'ios']).default('web'),
});
export type RegisterPushTokenInput = z.infer<typeof RegisterPushTokenInput>;

/** Input do callable `unregisterPushToken` (web e Android). */
export const UnregisterPushTokenInput = z.object({
  tenantId: TenantIdSchema,
  token: fcmToken,
});
export type UnregisterPushTokenInput = z.infer<typeof UnregisterPushTokenInput>;

/** Input do callable `registerAdminPushToken` (console super-admin). */
export const RegisterAdminPushTokenInput = z.object({
  token: fcmToken,
  userAgent: pushUserAgent.optional(),
});
export type RegisterAdminPushTokenInput = z.infer<typeof RegisterAdminPushTokenInput>;

/** Domínio da plataforma — destino permitido do link do broadcast (#795). */
export const PLATFORM_LINK_DOMAIN = 'etiquetachef.com';

/**
 * `true` se `s` é uma URL https absoluta em `etiquetachef.com` ou num
 * subdomínio dele (`app.`, `help.`…), sem credencial embutida nem porta não
 * padrão (`:443` é normalizado pelo `URL` e passa — é a mesma origem).
 *
 * Origem: #795 — 2026-10-08 11:37 +0200 (Admin: "`broadcastToOwners` com link
 * arbitrário").
 *
 * Por quê: o broadcast vai, com a marca, para TODOS os donos; com qualquer
 * https aceito, um link de phishing (`https://etiquetachef-login.com`) saía
 * pelo canal oficial. Decisão do owner (2026-10-08): só o domínio da
 * plataforma e subdomínios. A comparação é por `URL.hostname` — nunca
 * `includes`/`startsWith` no texto, que deixariam passar
 * `https://etiquetachef.com.evil.io`, `https://evil.io/?etiquetachef.com` e
 * `https://etiquetachef.com@evil.io` (userinfo; o host real é `evil.io`).
 *
 * Usado por: `BroadcastToOwnersInput` (servidor, callable `broadcastToOwners`)
 * e o formulário `NotificationsBroadcastPage` do `apps/admin` (mesma regra no
 * cliente, para o erro aparecer antes do envio).
 */
export function isPlatformHttpsLink(s: string): boolean {
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return false;
  }
  if (u.protocol !== 'https:' || u.username !== '' || u.password !== '' || u.port !== '') {
    return false;
  }
  // `URL` já devolve o hostname em minúsculas. Host com ponto final
  // (`etiquetachef.com.`) não casa com nenhum dos dois testes e é recusado.
  const host = u.hostname;
  return host === PLATFORM_LINK_DOMAIN || host.endsWith(`.${PLATFORM_LINK_DOMAIN}`);
}

/**
 * Input do callable `broadcastToOwners` (super-admin). Envia por multicast
 * para os tokens de todo OWNER que tem token registrado (até a #767 era o
 * tópico FCM `etiquetachef_owners_v1`, cuja inscrição dependia da InstanceId
 * API, desligada em 2027-09-29). `link` é a URL aberta ao clicar na notificação;
 * obrigatoriamente absoluto (`https://`) porque o FCM Web abre o link
 * sem resolver relativo. Sem i18n auto — o super-admin escolhe a
 * língua e dispara separado se quiser cobrir múltiplos locales.
 */
export const BroadcastToOwnersInput = z.object({
  title: z.string().trim().min(1).max(60),
  body: z.string().trim().min(1).max(200),
  // Só https (#767, auditoria 2026-09-28): o FCM v1 recusa `fcmOptions.link`
  // sem https em cada token com `invalid-argument`, e `z.url()` puro aceitava
  // `http:`, `ftp:` e até `javascript:`.
  // Só etiquetachef.com e subdomínios (#795, 2026-10-08 11:37 +0200): antes
  // qualquer https passava, e o push oficial podia levar a phishing.
  // `normalize` (#795, auditoria 2026-10-08 12:10 +0200, B1): grava o `href`
  // canônico. Sem ele, formas que o `URL` aceita mas que não começam com
  // `https://` (`https:etiquetachef.com/x`, `HTTPS://…`, `https:\\app…`) iam
  // cruas ao FCM e o `toAbsoluteLink` as prefixava com o domínio do app
  // (`https://app.etiquetachef.com/https:etiquetachef.com/x`); e
  // `https://etiquetachef.com\@evil.io` ia cru para parsers não-WHATWG.
  link: z
    .url({ protocol: /^https$/, normalize: true })
    .refine(isPlatformHttpsLink, { message: 'link_not_platform_domain' })
    .optional(),
});
export type BroadcastToOwnersInput = z.infer<typeof BroadcastToOwnersInput>;

/** Input do callable `unregisterAdminPushToken` (console super-admin). */
export const UnregisterAdminPushTokenInput = z.object({
  token: fcmToken,
});
export type UnregisterAdminPushTokenInput = z.infer<typeof UnregisterAdminPushTokenInput>;

/**
 * Sprint 27: regra de validade redesenhada.
 *
 * - `name`: texto livre (1–100 chars) que o usuário digita — vira o "label"
 *   da regra no select de criação de etiqueta.
 * - `durationHours`: persistido em horas (UI mostra em dias — multiplica
 *   por 24 no save, divide no load).
 * - `isDefault`: marca a regra como padrão. Exatamente UMA regra do
 *   produto tem `isDefault = true`. Com 1 só regra, a UI marca automático;
 *   com 2+ o usuário promove via botão "Tornar padrão".
 *
 * Sumiu: `kind` (enum), `origin` (sempre data de produção = `openedAtMillis`
 * implícito), `appliesToStorage` (regra serve a qualquer conservação) e o
 * override `label` (redundante com `name`).
 */
/** Limite máximo de duração — 365 dias × 24h = 8760h (decisão do owner, 2026-05-13). */
export const MAX_VALIDITY_DURATION_HOURS = 365 * 24;

/**
 * Teto de produtos por tenant, ativos + arquivados (decisão do owner,
 * 2026-08-11 — #253). Enforcement é client-side (web e Android gateiam
 * criar/duplicar/importar): Firestore rules não contam documentos.
 * Espelhado à mão no Kotlin (duplicação intencional de tipos).
 */
export const MAX_PRODUCTS_PER_TENANT = 1000;

export const ProductValidityRuleSchema = z.object({
  name: z.string().min(1).max(100),
  durationHours: z.number().int().positive().max(MAX_VALIDITY_DURATION_HOURS),
  isDefault: z.boolean(),
});
export type ProductValidityRule = z.infer<typeof ProductValidityRuleSchema>;

export const ProductSchema = z.object({
  id: ProductIdSchema,
  tenantId: TenantIdSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(1000).default(''),
  category: ProductCategorySchema,
  sku: z.string().max(60).default(''),
  barcode: z.string().max(60).nullable().optional(),
  ingredients: z.array(z.string().max(120)).max(80).default([]),
  allergens: z.array(AllergenSchema).default([]),
  // A unidade de medida (`UnitOfMeasure`) é o único atributo de
  // "apresentação" no produto — enum compartilhado com todo o sistema. A
  // quantidade do lote (`quantity`) e a conservação (`storage`) deixaram de
  // viver aqui: ambas são escolhidas na CRIAÇÃO da etiqueta (cada lote pode
  // ter quantidade/conservação diferentes), portanto moram em `LabelSchema`.
  unit: UnitOfMeasureSchema,
  imagePath: z.string().max(500).nullable().optional(),
  imageUrl: z.string().max(1000).nullable().optional(),
  // Sprint 24/27: ÚNICA fonte da verdade das regras de validade do produto.
  // Sprint 27: schema reescrito — cada regra é `{ name, durationHours,
  // isDefault }`; a "padrão" agora é uma flag por regra (exatamente 1 com
  // `isDefault = true`), em vez de um campo `defaultRuleKind` no produto.
  // Mín. 1 regra por produto (decisão do owner, 2026-05-12): sem regra
  // não há como gerar etiqueta. Sem seed: o editor inicia sem nenhuma
  // regra e o usuário cria a primeira manualmente (decisão do owner,
  // 2026-05-13).
  validityRules: z.array(ProductValidityRuleSchema).max(20).default([]),
  // Estoque mínimo (`docs/screens/stock.md`, 2026-05-19). Inteiro ≥ 0 na
  // unidade do produto (`unit`); `null` = sem alerta de estoque baixo
  // pra esse produto (default). Cliente escreve direto no doc (rules
  // permitem OWNER/MANAGER em produto ativo); trigger `onProductWritten`
  // grava audit `PRODUCT_MIN_STOCK_UPDATED` e recalcula `stockAlertState`
  // — escrita entra na fila offline do Firestore SDK e sincroniza ao
  // recuperar conexão (fix do issue Crashlytics 5d81c6d3).
  // Tolerância a docs legados sem o campo via `.default(null)`.
  minStock: z.number().int().min(0).max(1_000_000).nullable().default(null),
  // Fornecedores do produto (issue #193, docs/screens/suppliers.md).
  // Many-to-many via array de IDs de `tenants/{tid}/suppliers/{id}` — a tela
  // "produtos do fornecedor" filtra o catálogo in-memory, sem `array-contains`.
  // Fornecedor arquivado permanece no array (sem limpeza automática, como
  // produto arquivado em etiquetas). `.default([])` tolera docs legados.
  supplierIds: z.array(SupplierIdSchema).max(20).default([]),
  active: z.boolean(),
  // Sprint 22 PR E.2: cliente escreve sem timestamps; trigger
  // `onProductWritten` carimba server-side. Janela entre o write do
  // cliente e o run do trigger pode ter `null`/ausente — schema aceita
  // ambos para o parse não falhar.
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  createdByUid: UserIdSchema.optional(),
  updatedByUid: UserIdSchema.optional(),
});
export type Product = z.infer<typeof ProductSchema>;

/**
 * Estado de alerta de estoque baixo por produto, gravado pelo trigger
 * `onLabelWritten` (e `onProductWritten` quando `minStock` muda) em
 * `tenants/{tid}/products/{pid}/stockAlertState/current` —
 * `docs/screens/stock.md` §7.1.
 *
 * Cliente NÃO escreve este doc — só **lê** (gating das Rules em
 * `firestore.rules`). Server usa pra:
 *  - evitar recomputar saldo a cada onLabelWritten quando nada mudou;
 *  - detectar **transição** (ex.: OK → LOW) — único momento que dispara
 *    push FCM;
 *  - aplicar **rate-limit de 6h** (`lastAlertAtMillis`) entre pushes do
 *    mesmo produto, mesmo que o saldo oscile dentro da janela.
 */
export const StockAlertStateSchema = z.object({
  /**
   * Último saldo computado (`Σ Label.quantity` em DRAFT+PRINTED daquele
   * produto). Pode ser decimal desde a issue #39 (produtos kg/L têm
   * quantidade fracionária — ex. 1,5 kg).
   */
  lastSaldo: z.number().nonnegative(),
  /**
   * - `NONE`: `minStock == null` (produto sem alerta configurado).
   * - `OK`:   `saldo >= minStock`.
   * - `LOW`:  `0 < saldo < minStock`.
   * - `EMPTY`: `saldo == 0 && minStock > 0`.
   */
  lastStatus: z.enum(['OK', 'LOW', 'EMPTY', 'NONE']),
  /** `null` até o 1º push. Rate-limit: novo push só se `(now - lastAlertAtMillis) > 6h`. */
  lastAlertAtMillis: z.number().int().nonnegative().nullable().default(null),
  /** Server timestamp do último recálculo (ms). */
  updatedAtMillis: z.number().int().nonnegative().nullable().default(null),
});
export type StockAlertState = z.infer<typeof StockAlertStateSchema>;

// ---------- Label Templates (Sprint 19) ----------
//
// Sprint 19 substituiu o modelo original (`TemplateField` + `columnsLayout`)
// por uma pilha linear de `TemplateBlock`s top→bottom. Cada bloco é uma
// "linha lógica" e pode conter 1 valor (`TextBlock`), 2 slots em key-value,
// 2 slots lado a lado (`InlineRow`), uma régua horizontal, ou um barcode.
// Caption "Escaneie no app para dar baixa" é renderizada automaticamente
// abaixo de qualquer `BarcodeBlock` com `barcodeKind = QR` — não é
// configurável.

/**
 * Sprint 34: origem de campo togglável no modelo de etiqueta. É o subconjunto
 * de `FieldSourceKind` exposto como checkbox (ver `TEMPLATE_FIELD_KINDS` em
 * `fieldCatalog.ts`). Origens fora do catálogo (`TextoLivre`, `Empty`, `Origin`,
 * `RuleLabel`, `Duration`) não entram em `includedFields`.
 */
export const TemplateFieldKindSchema = z.enum(TEMPLATE_FIELD_KINDS);

export const LabelTemplateSchema = z.object({
  id: LabelTemplateIdSchema,
  tenantId: TenantIdSchema,
  name: z.string().min(1).max(60),
  widthMm: z.number().int().positive(),
  heightMm: z.number().int().positive(),
  /** Gap entre etiquetas físicas no rolo (mm). Calibração da impressora. */
  gapMm: z.number().int().nonnegative().default(2),
  /**
   * Espaço vertical mínimo entre campos, em dots. Default 4 ≈ 0.5mm @ 203 DPI.
   * Em modo `verticalFit = FILL`, este é o piso — gaps podem aumentar para
   * preencher altura disponível.
   */
  rowGapDots: z.number().int().nonnegative().max(40).default(4),
  /** Sprint 19: aproveitamento vertical. Default `FILL` (etiqueta nunca fica meio-vazia). */
  verticalFit: VerticalFitSchema.default('FILL'),
  /**
   * Sprint 34: campos LIGADOS na etiqueta (origens do catálogo —
   * `TEMPLATE_FIELD_KINDS`). A ORDEM de impressão e o ESTILO vêm do catálogo
   * (`fieldCatalog.ts`), não deste array — a renderização reordena via
   * `orderedIncludedFields(...)`. QR + logo do EtiquetaChef são fixos no rodapé
   * e NÃO entram aqui (`project_label_qr_logo_always`). Pode ser vazio (etiqueta
   * só com QR + logo). Substituiu o antigo `blocks: TemplateBlock[]`.
   */
  includedFields: z.array(TemplateFieldKindSchema).max(TEMPLATE_FIELD_KINDS.length).default([]),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  createdByUid: UserIdSchema.optional(),
  updatedByUid: UserIdSchema.optional(),
}).superRefine((tpl, ctx) => {
  // includedFields não pode ter duplicatas (cada campo aparece no máx 1×).
  const seen = new Set<string>();
  tpl.includedFields.forEach((kind, idx) => {
    if (seen.has(kind)) {
      ctx.addIssue({ code: 'custom', message: `Campo duplicado em includedFields: ${kind}.`, path: ['includedFields', idx] });
    }
    seen.add(kind);
  });
});
export type LabelTemplate = z.infer<typeof LabelTemplateSchema>;

/**
 * Input do callable `updateTenantLogo`. Valida ownership do path
 * (`tenants/{tenantId}/branding/...`) no servidor — caller manda
 * `logoPath` resolvido pelo Storage upload e a URL de download
 * cacheada.
 */
export const UpdateTenantLogoInput = z.object({
  tenantId: TenantIdSchema,
  logoPath: z.string().min(1).max(500),
  logoUrl: z.url().max(1000),
});
export type UpdateTenantLogoInput = z.infer<typeof UpdateTenantLogoInput>;

export const RemoveTenantLogoInput = z.object({
  tenantId: TenantIdSchema,
});
export type RemoveTenantLogoInput = z.infer<typeof RemoveTenantLogoInput>;

export const PrinterProfileSchema = z.object({
  id: PrinterIdSchema,
  tenantId: TenantIdSchema,
  name: z.string().min(1),
  /**
   * Sprint 31 PR C: nome BT de fábrica capturado no pareamento (ex.:
   * "D520BT-Z"). Imutável após criação — usado pra derivar `modelKey`
   * no PR G (knowledge base) e diferenciar profiles do mesmo modelo
   * com `name` renomeado pelo usuário.
   */
  model: z.string().nullable().optional(),
  /**
   * O endereço do rádio — quando a plataforma o expõe.
   *
   * Deixou de ser obrigatório na Fase 8 da #106 (item B11). O iOS não vê MAC: a
   * Apple não entrega o endereço Bluetooth a apps de terceiros, e não há
   * entitlement nem biblioteca que contorne. Lá a impressora é identificada por
   * `identity` — número de série do firmware, ou uma âncora local.
   *
   * Documento sem `macAddress` **tem** que trazer `identity`; é o refinamento no
   * fim deste schema. Quem tem MAC continua com ele, byte por byte: os
   * documentos criados pelo Android não mudam. (O `onPrinterWritten` populava
   * `printerMacs/` a partir daqui até a #795, que desligou a base global.)
   */
  macAddress: z
    .string()
    .regex(/^[0-9A-Fa-f:]{17}$/)
    .optional(),
  /**
   * Quem é esta impressora, independente do transporte (item B11 da #106).
   *
   * Opcional porque toda impressora anterior à Fase 8 se identifica pelo MAC e
   * não vai ser migrada: quem lê um documento sem este campo deve inferir
   * `type: 'MAC'` a partir do `macAddress`. Nenhuma migração destrutiva.
   *
   * `stableKey` é o que decide o id do documento, e é por isso que ele **nunca**
   * pode ser um endereço mutável (IP, porta) nem um identificador de sessão: dois
   * aparelhos que resolvem a mesma impressora precisam chegar à mesma chave, ou
   * a mesma máquina vira dois cadastros e o contador de etiquetas racha ao meio.
   */
  identity: z
    .object({
      type: PrinterIdentityTypeSchema,
      /** A chave que decide o id do documento. Prefixada, para que a força da chave apareça em qualquer log. */
      stableKey: z.string().min(1).max(128),
      /** O nome que o aparelho anuncia. Rótulo para a pessoa escolher na lista — vários clones anunciam o mesmo. */
      displayName: z.string().max(120).optional(),
      transport: PrintTransportSchema,
      /** Modelo declarado pelo firmware. Escolhe o perfil de impressão; nunca identifica a instância. */
      model: z.string().max(120).optional(),
      manufacturer: z.string().max(120).optional(),
      protocol: PrintProtocolSchema,
    })
    .optional(),
  protocol: PrintProtocolSchema,
  /**
   * Por onde o iPhone fala com esta impressora, e se ela responde (#667).
   *
   * **Não é configuração: é medição do módulo.** O canal GATT varia por
   * hardware — a `D520BT-Z` atende em `FF00`/`FF02`, a `AI-110F` em
   * `18F0`/`2AF1` — e até a #666 o app exigia o primeiro de todas, o que a
   * deixava inalcançável. Guardado aqui, o próximo aparelho não precisa
   * varrer a árvore para descobrir de novo.
   *
   * `silent` é o outro achado da #666: essa impressora **imprime sem
   * confirmar nada**, e o lote dela termina por tempo estimado. Sem este
   * campo, todo lote paga uma vez o prazo de descoberta do silêncio.
   *
   * Ausente no Android, que fala SPP e não tem serviço nem característica.
   * Ausente também em toda impressora anterior à #667 — quem não tem o campo
   * descobre em tempo de conexão, como antes.
   *
   * **Por MAC, nunca por modelo.** Dois clones do mesmo modelo podem trazer
   * módulos Bluetooth diferentes; promover isto ao agregado por modelo daria
   * conselho errado com confiança, que é a #312.
   */
  bleChannel: z
    .object({
      /** UUID do serviço, como o CoreBluetooth o imprime (`18F0`, ou 128 bits). */
      service: z.string().min(4).max(36),
      /** A característica que aceita escrita sem resposta. */
      write: z.string().min(4).max(36),
      /** A característica de retorno, quando existe — e ela pode existir e nunca falar. */
      notify: z.string().min(4).max(36).optional(),
      /**
       * A impressora não emitiu um byte durante um lote que saiu no papel.
       * Só é gravado depois de uma impressão confirmada; ausência significa
       * "ainda não se sabe", nunca "ela responde".
       */
      silent: z.boolean().optional(),
    })
    .optional(),
  /**
   * Sprint 16: width/height (dimensões da etiqueta) saíram do profile e
   * vivem no LabelTemplate. `dpi` e `gapMm` continuam no profile —
   * são calibração da impressora, validados no test print e re-utilizados.
   */
  dpi: z.number().int().positive(),
  gapMm: z.number().int().nonnegative().default(2),
  /**
   * Sprint 31 PR B: densidade térmica (0-15). TSPL `DENSITY n`; ZPL `^MD`
   * (mapeado para n*2 na faixa 0-30); CPCL e ESC/POS ignoram. Default 8
   * é meio-termo seguro para clones baratos.
   */
  density: z.number().int().min(0).max(15).default(8),
  /**
   * Sprint 31 PR B: velocidade de impressão (1-6). TSPL `SPEED n`.
   * Demais protocolos ignoram. Default 4. Velocidade alta sacrifica
   * qualidade em alguns papéis.
   *
   * **Fracionário desde a Fase 8 da #106** (owner, 2026-09-10). O `.int()` que
   * estava aqui impedia o iOS de gravar o que ele mediu: a velocidade boa é por
   * formato de etiqueta e cai *entre* os inteiros — 57×51 mm imprime sem pausa a
   * `SPEED 2.4`, 22 % mais rápido que o default, e 40×40 mm a `SPEED 3.4`. O
   * motivo é físico: a impressora processa o raster a ~26 KB/s e pausa quando o
   * avanço do papel fica mais rápido que isso, então a velocidade **máxima não é
   * a mais rápida** — no lote medido, `SPEED 4` perdeu no total por causa de três
   * pausas. A tabela por formato vive em `PrinterSettings.recommendedSpeed`
   * (iOS); levá-la ao Android é a #374.
   *
   * O TSPL aceita decimal no `SPEED`. Quem lê este campo como inteiro trunca
   * (2.4 vira 2) e imprime um pouco mais devagar — degradação silenciosa, não
   * quebra (#450 — o Android passou a ler decimal em 2026-09-18).
   *
   * **Desde a #374 (2026-09-18) este é o valor do modo MANUAL.** Em `AUTO`
   * (ver `speedMode`) o app ignora este campo ao imprimir e calcula a
   * velocidade pelo formato da etiqueta; o valor derivado **nunca** é gravado
   * aqui — a base global (`onPrinterWritten`) agrega por igualdade exata, e
   * gravar 2.4 num perfil e 3.4 noutro do mesmo modelo diluiria a contagem.
   *
   * Default 2, e não 4: é o que Android, iOS e `onPrinterWritten` sempre
   * usaram; o 4 que ficou aqui até 2026-09-18 nunca foi lido por ninguém.
   */
  speed: z.number().min(1).max(6).default(2),
  /**
   * Como `speed` é decidido (#374). Ausente = documento anterior à decisão;
   * a regra de leitura é `resolvePrinterSpeedMode`, espelhada em Kotlin
   * (`PrinterProfileDto.toDomain`) e Swift (`PrinterWire`). Não use
   * `.default()` aqui: o default correto depende de `speed`, e um default
   * fixo esconderia essa dependência de quem lê o schema.
   */
  speedMode: PrinterSpeedModeSchema.optional(),
  /**
   * Sprint 33: mídia com **marca preta** (black mark) em vez de gap
   * transparente entre etiquetas. Quando `true`, os renderers usam o
   * sensor reflexivo (TSPL `BLINE`, CPCL `BAR-SENSE`, ZPL `^MNM`) em vez
   * do sensor de gap (`GAP`/gap-sense/`^MNY`). ESC/POS ignora (cupom não
   * tem sensor de mídia). Default `false` — a maioria dos rolos é gap.
   */
  blackMark: z.boolean().default(false),
  /**
   * Sprint 33: tamanho da etiqueta (mm) lembrado do wizard/setup avançado.
   * `0` = não definido (cai no template default). Persistido pra wizard e
   * avançado compartilharem o mesmo valor.
   */
  labelWidthMm: z.number().int().nonnegative().default(0),
  labelHeightMm: z.number().int().nonnegative().default(0),
  /**
   * Margem horizontal interna em dots — quanto o texto se afasta da borda
   * esquerda/direita. Default 40 (~5mm a 203 DPI / ~3.4mm a 300 DPI).
   * Sprint 17.
   */
  marginDots: z.number().int().nonnegative().default(40),
  /**
   * TSPL: usar TrueType embutida (TSS16/24/32/48.BF2) em vez de bitmap.
   * Sprint 17. Default true; desligar se a impressora não tiver as TSS
   * gravadas em flash (raro em TSC/Argox modernos).
   */
  useVectorFont: z.boolean().default(true),
  /**
   * Impressora padrão do tenant: no máximo uma com `true`, mantida por
   * `setDefault` (Android `FirestorePrinterRepository`, iOS, Functions).
   * Ausente = não é a padrão, e é assim que o Android (`PrinterProfileDto`)
   * e o iOS (`PrinterWire.swift:53`, `?? false`) já liam.
   *
   * Bug corrigido em 2026-10-08 12:41 +0200 (#884, #795): o campo era
   * obrigatório, e o create do Android parou de gravá-lo (`f9226e6d3`,
   * `PrinterCreatePayload.kt`). Essa omissão é de propósito: o `set` com merge
   * não pode desfazer a padrão de um doc que já existe no servidor. Resultado:
   * a 2ª impressora em diante (o `setDefault` só roda para a 1ª,
   * `PrintersViewModel.kt:236`) nascia sem o campo, e o admin
   * (`firestorePrinters.ts`) e o CRM (`PrintersTab.tsx`) a descartavam no
   * `safeParse` sem aviso. Não voltar para `z.boolean()` obrigatório sem antes
   * fazer todo create gravar o campo.
   */
  isDefault: z.boolean().default(false),
  /**
   * Soft delete do aparelho (`FirestorePrinterRepository.setActive`): `false`
   * some da lista do app, que filtra `active == true`. Faltava aqui e o Zod o
   * descartava — a tela do cliente do CRM (#776) não sabia distinguir
   * impressora desativada. Ausente em doc antigo = ativa (default do DTO).
   */
  active: z.boolean().default(true),
  /**
   * Sprint 31 PR A: timestamp em que o wizard de descoberta empírica
   * confirmou protocolo+DPI+gap. `null` enquanto o profile recém pareado
   * ainda não rodou o wizard — UI mostra "⚠ Não calibrada". PR G usa a
   * transição `null → !null` como gatilho do trigger que populava a
   * knowledge base global (`printerMacs/`, `printerModels/`), desligada na
   * #795 — hoje o campo só alimenta o estado "calibrada" da UI.
   */
  wizardCompletedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * Sprint 31 PR D: timestamp da última impressão bem-sucedida (test print
   * ou bulk). Atualizado best-effort em transação junto com `labelCount`.
   */
  lastUsedAt: z.number().int().nonnegative().optional(),
  /**
   * Sprint 31 PR D: contador acumulado de etiquetas impressas.
   * Incrementado server-side via `FieldValue.increment`.
   */
  labelCount: z.number().int().nonnegative().optional(),
  /**
   * Sprint 31 PR E: notas livres (até 80 chars). Hoje preenchido via
   * wizard ramo "Outra coisa saiu"; futura edição via PrinterEditDialog.
   */
  notes: z.string().max(80).default(''),
  /**
   * Sprint 22 PR E.3: cliente NÃO envia mais `createdAt`/`updatedAt`;
   * trigger `onPrinterWritten` carimba server-side via `Date.now()`.
   * Nullable para tolerar a janela entre o write do cliente e o trigger
   * preencher (em escrita offline pode demorar).
   */
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * Autoria. Opcional porque o schema tolera documentos anteriores ao campo
   * (Sprint 22) — hoje toda impressora em produção tem autor.
   *
   * Deixou de ser só auditoria: na conta de demonstração, compartilhada por
   * vários prospects, o app filtra a lista por `createdByUid`. O painel
   * super-admin é o único lugar que enxerga o conjunto, então precisa mostrar
   * de quem é cada impressora.
   */
  createdByUid: z.string().optional(),
  updatedByUid: z.string().optional(),
}).superRefine((printer, ctx) => {
  // Uma impressora sem NENHUMA das duas chaves não é endereçável: não há como
  // decidir o id do documento, e o próximo cadastro da mesma máquina viraria um
  // segundo documento. É o invariante do caso C6 da §19b da #106 — nunca dois
  // documentos para uma máquina —, e é o único jeito de o schema dizer que
  // `macAddress` virou opcional para dar lugar a `identity`, não para poder
  // faltar.
  if (!printer.macAddress && !printer.identity) {
    ctx.addIssue({
      code: 'custom',
      path: ['identity'],
      message: 'impressora sem macAddress precisa de identity (item B11 da #106)',
    });
  }
});
export type PrinterProfile = z.infer<typeof PrinterProfileSchema>;

export const LabelSchema = z.object({
  id: LabelIdSchema,
  tenantId: TenantIdSchema,
  productId: ProductIdSchema,
  productName: z.string(),
  // Sprint 24/27: a etiqueta carrega um SNAPSHOT imutável da regra de
  // validade escolhida no momento da impressão (a regra mora embutida em
  // `Product.validityRules[]` — não há mais subcoleção tenant-level nem
  // `ruleId`).
  //
  // Sprint 27: `ruleKind` virou texto livre (`Product.validityRules[].name`
  // é texto livre). `ruleOrigin` sumiu — sempre data de produção (campo
  // `openedAtMillis` segue como nome técnico do timestamp). `ruleLabelText`
  // permanece como texto exibido (slot `RULE_LABEL` do template), populado
  // a partir do `name` da regra.
  ruleKind: z.string().min(1).max(100),
  ruleDurationHours: z.number().int().positive(),
  ruleLabelText: z.string(),
  lot: z.string().nullable().optional(),
  openedAtMillis: z.number().int().nonnegative(),
  /**
   * Sprint 22/24: cliente NÃO escreve este campo. Trigger `onLabelWritten`
   * calcula `openedAtMillis + ruleDurationHours * 3_600_000` (snapshot da
   * própria etiqueta — sem read de doc de regra) e escreve de volta.
   * `null`/ausente enquanto o trigger ainda não rodou (offline / write
   * recente) — UI mostra preview client-side + badge "Sincronizando…".
   * `null` permanente quando `ruleMissing == true`.
   * `.default(null)` cobre o caso ausente: o cliente faz `addDoc` sem o
   * campo, Firestore devolve undefined; Zod normaliza para null no parse,
   * mantendo o contrato `number | null` para os consumers.
   */
  expiresAtMillis: z.number().int().nonnegative().nullable().default(null),
  /**
   * Sprint 26 — Firestore TTL policy. Cliente NÃO escreve este campo.
   * Trigger `onLabelWritten` carimba um `Timestamp` em
   * `expiresAtMillis + LABEL_TTL_AFTER_EXPIRY_MS` (12 meses — issue #237;
   * era 90 dias). A Firestore TTL policy no collectionGroup `labels` (campo
   * `ttlAt`) apaga o doc após o timestamp vencer — até 24h de atraso é
   * esperado. Etiquetas com `ruleMissing == true` permanecem sem `ttlAt`
   * (não são apagadas).
   *
   * Tipado como `unknown` aqui porque o valor é um `firebase.firestore.Timestamp`
   * (Android SDK / Web SDK), não um `number` — e o schema é compartilhado pelos
   * dois mundos. Consumidores que precisarem do valor (raro — TTL é higiene de
   * storage, não usado em produto) devem desserializar com a API do SDK.
   */
  ttlAt: z.unknown().nullable().optional(),
  /**
   * Sprint 24: trigger marca `true` se o snapshot da regra na etiqueta
   * for inválido (`ruleDurationHours <= 0` — só acontece com payload
   * corrompido do cliente). Estado terminal — UI exibe warning e usuário
   * precisa refazer.
   */
  ruleMissing: z.boolean().default(false),
  /**
   * Sprint 22 PR E.7: timestamp da CRIAÇÃO do doc (quando addDoc rodou).
   * Distinto de `openedAtMillis` (quando o produto foi aberto — input do
   * form, pode ser data passada). Usado pelo dashboard para o card
   * "Etiquetas hoje" (volume de criação por dia).
   *
   * Cliente NÃO escreve: trigger `onLabelWritten` carimba `Date.now()`
   * em create (`!before && after`). `null`/ausente até o trigger rodar;
   * UI usa fallback `l.createdAtMillis ?? l.openedAtMillis` para etiquetas
   * legadas.
   */
  createdAtMillis: z.number().int().nonnegative().nullable().default(null),
  printedByUid: UserIdSchema,
  printedByName: z.string(),
  // Sprint 27: responsável pela manipulação. Snapshot imutável do `Responsible`
  // escolhido no momento da criação da etiqueta — paralelo ao snapshot de
  // regra (`ruleKind`/`ruleDurationHours`). Desativar/excluir um responsável
  // NÃO altera etiquetas já criadas. Campo obrigatório (1+ char) — `responsibles.md`.
  responsibleId: ResponsibleIdSchema,
  responsibleName: z.string().min(1).max(80),
  /**
   * Fornecedores do produto no momento da criação da etiqueta — SNAPSHOT
   * imutável dos NOMES (#369), paralelo a `responsibleName` e ao snapshot da
   * regra (`ruleKind`). Renomear ou arquivar um fornecedor NÃO altera etiquetas
   * já criadas.
   *
   * Derivado de `Product.supplierIds` (many-to-many, máx. 20) na ORDEM do array
   * do produto; ids que não resolvem para um doc de `tenants/{tid}/suppliers`
   * são descartados. Fornecedor ARQUIVADO (`active: false`) ENTRA — ele
   * permanece no `supplierIds` do produto por design, e a etiqueta retrata o
   * que o produto dizia naquele instante.
   *
   * Sem UI própria: o editor de etiqueta não tem campo de fornecedor, o valor é
   * derivado do produto. Campo OPCIONAL — nada bloqueia salvar e as rules não o
   * mencionam. `.default([])` cobre os docs legados. `max(120)` espelha
   * `SupplierSchema.name`; `max(20)` espelha `ProductSchema.supplierIds`.
   *
   * Guardamos os NOMES, não os ids: o consumidor (renderer + detalhe) quer
   * texto, e a etiqueta impressa tem que sobreviver a rename/arquivamento.
   * Compor a string de impressão só via `composeSupplierField` (fieldCatalog).
   */
  supplierNames: z.array(z.string().min(1).max(120)).max(20).default([]),
  printerId: PrinterIdSchema.nullable().optional(),
  printedAtMillis: z.number().int().nonnegative().nullable(),
  /**
   * Código curto humano-legível impresso ao lado do QR — fallback de baixa
   * quando o QR raspa/borra na cozinha. 6 dígitos numéricos sem separador
   * (ex.: `"471892"`); o cliente formata como `"471-892"` para display. Único
   * entre etiquetas vigentes (`DRAFT`+`PRINTED`) do tenant, reusável após
   * baixa (espaço efetivo cabe nos vigentes simultâneos).
   *
   * **Gerado client-side na criação** (offline-first), nos DOIS clientes:
   * Android (`LabelShortCode.generate`) e web (`apps/app/src/lib/shortCode.ts`
   * via `useLabelOps.createLabel`). NÃO há geração server-side — o trigger não
   * teria o código no instante de uma criação offline. O `.default('')`
   * cobre só docs legados (criados antes da feature) e bordas: nesses o
   * renderer pula o desenho e o lookup por código não encontra.
   */
  shortCode: z.string().regex(/^(\d{6})?$/).default(''),
  /**
   * Total de tentativas (sucessos + falhas). Mantido para compat com rules
   * legadas; novas decisões devem usar `printSuccessCount` e
   * `printFailureCount` (Sprint bug-hunt M-14) que separam o sinal.
   */
  printAttempts: z.number().int().nonnegative().default(0),
  /**
   * Sprint bug-hunt M-14: total de impressões com sucesso. Antes
   * `printAttempts` somava sucessos+falhas e era ambíguo.
   */
  printSuccessCount: z.number().int().nonnegative().default(0),
  /** Sprint bug-hunt M-14: total de tentativas que falharam. */
  printFailureCount: z.number().int().nonnegative().default(0),
  lastPrintError: z.string().nullable().optional(),
  reusedFromLabelId: LabelIdSchema.nullable().optional(),
  /**
   * Quantidade do lote desta etiqueta, na unidade de medida do produto
   * (`Product.unit`). Ex.: produto `unit=GRAM` + etiqueta `quantity=500`
   * → "500 g". Escolhida na criação da etiqueta (cada lote pode ter uma
   * quantidade diferente). Não-negativo. Issue #39: aceita decimais quando
   * a unidade do produto é `KILOGRAM`/`LITER` (ex.: `1.5` → "1,5 kg");
   * `GRAM`/`MILLILITER`/`UNIT`/`PACK`/`BOX` continuam inteiros. A regra
   * por-unidade é validada no cliente (o schema não conhece a unidade do
   * produto aqui); o valor é guardado na própria unidade, sem conversão.
   * `.default(0)` tolera docs legados sem o campo.
   *
   * **Épico #323 (F1):** a partir da baixa parcial, este é o valor
   * **ORIGINAL** — o que foi impresso no papel — e é **imutável após a
   * impressão** (`LabelFieldContract.quantity.mutability`). O saldo que ainda
   * existe é o `remainingQuantity`; somar `quantity` das vigentes passou a
   * superestimar o estoque e é bug.
   */
  quantity: LabelFieldContract.quantity.schema,
  /**
   * Conservação do lote desta etiqueta — escolhida na criação da etiqueta
   * (não herdada do produto). Cada lote do mesmo produto pode estar em
   * conservação diferente (ex.: parte refrigerada, parte congelada).
   */
  storage: StorageSchema,
  /**
   * Tipo de **leitura** ([ReadLabelStatus]) com `.catch()` (#330): um status
   * que esta versão não conhece (escrito por um cliente mais novo) vira
   * `'UNKNOWN'` em vez de reprovar o doc inteiro — antes o `safeParse` falhava
   * e a etiqueta **sumia da lista em silêncio**. `'UNKNOWN'` é inerte: a UI não
   * oferece ação sobre ele. Quem lê também reporta (PostHog
   * `label_status_unknown` em `parseLabelDoc`) — o fallback é rede de
   * segurança, não desculpa para não instrumentar.
   */
  status: ReadLabelStatusSchema.catch(UNKNOWN_LABEL_STATUS_FALLBACK),
  /**
   * Sprint 22 PR E.5: cliente NÃO escreve este campo. Trigger
   * `onLabelWritten` carimba `Date.now()` quando detecta transição
   * `before.status !== after.status`. `null`/ausente enquanto o trigger
   * ainda não rodou (offline / write recente). Cliente cooperativo (cron
   * `scanExpiringLabels`) pode escrever direto — trigger respeita.
   * `.default(null)` cobre o caso ausente: cliente cria sem o campo, doc
   * volta sem a key, Zod normaliza para null no parse e o trigger preenche
   * em seguida.
   */
  statusChangedAtMillis: z.number().int().nonnegative().nullable().default(null),
  // M-36: branded UserId em vez de string solta. Default null
  // (campo opcional que o trigger preenche).
  statusChangedByUid: UserIdSchema.nullable().default(null),
  /**
   * Snapshot do `displayName` de quem deu baixa, preenchido server-side pelo
   * trigger `onLabelWritten` (a partir de `tenants/{tid}/members/{uid}`)
   * quando detecta transição PRINTED → CONSUMED/DISCARDED. Mesmo padrão do
   * `responsibleName`: cliente não escreve; default "" cobre etiquetas
   * vigentes e expiradas pelo cron (`SYSTEM_ACTOR_UID`).
   */
  statusChangedByName: z.string().default(''),
  /**
   * Canal da última transição de status (issue #230) — ver [StatusChangedVia].
   * Escrito pelo CLIENTE (é o único que sabe de qual botão veio) e propagado
   * pelo trigger `onLabelWritten` para `metadata.via` do `activity_log`.
   * As rules validam o valor contra o enum e aceitam a ausência, para o app
   * já publicado continuar dando baixa enquanto a atualização não chega.
   */
  // Auditoria 2026-10-09 (#900, B6): `.catch(null)` como o `BaixaViaSchema`.
  // Sem ele, uma aba com bundle anterior a um canal novo (o `NFC`) descartava
  // a etiqueta inteira no `parseLabelDoc` até recarregar.
  statusChangedVia: z.enum(ALL_STATUS_CHANGED_VIA).nullable().default(null).catch(null),
  /**
   * Carimbo da última transição de status medido no RELÓGIO DO SERVIDOR
   * (issue #230). Escrito EXCLUSIVAMENTE pelo trigger `onLabelWritten`; o
   * cliente não envia (o campo não está no `hasOnly` das rules).
   *
   * Par de [statusChangedAtMillis], que é o relógio do DEVICE e continua sendo
   * o horário real do evento (inclusive offline, onde o write só chega ao
   * trigger horas depois). A diferença entre os dois é o skew do dispositivo —
   * sem esse par, medir skew exige aproximar pelo `activity_log.createdAt`,
   * que é o tempo do trigger, não o da transição.
   */
  statusChangedAtServerMillis: z.number().int().nonnegative().nullable().default(null),

  // ----- Baixa parcial e mista (épico #323, fase F1 — issue #324) -----
  //
  // Os campos abaixo vêm de `LabelFieldContract`, que declara `{ schema,
  // mutability }` no mesmo objeto: o contrato é a fonte única lida pelo teste
  // derivado das rules, pelos guards de cliente e pelo trigger. Não redeclare
  // um `z.*` aqui — divergir do contrato é o bug que ele existe para impedir.
  //
  // Modelo: `baixas[]` é a VERDADE (ledger append-only); os cinco números são
  // PROJEÇÃO que o trigger `onLabelWritten` reconcilia a cada write, direto do
  // ledger efetivo. O cliente grava valor otimista em `consumedQuantity`,
  // `discardedQuantity` e `remainingQuantity` para a UI offline refletir na
  // hora — e o servidor sobrescreve.

  /** Σ dos `CONSUMED` efetivos do ledger. Cache; o trigger é a autoridade. */
  consumedQuantity: LabelFieldContract.consumedQuantity.schema,
  /** Σ dos `DISCARDED` efetivos do ledger. Cache; o trigger é a autoridade. */
  discardedQuantity: LabelFieldContract.discardedQuantity.schema,
  /** Σ dos `EXPIRED` efetivos. Só o servidor gera a entrada e este cache. */
  expiredQuantity: LabelFieldContract.expiredQuantity.schema,
  /**
   * SALDO VIGENTE materializado — o campo mais lido do doc. Estoque, lista de
   * vigentes, agrupamento, scanner, detalhe, alertas e Monitor **leem**, não
   * calculam. Zero em qualquer status fechado (exceto o legado `quantity == 0`).
   */
  remainingQuantity: LabelFieldContract.remainingQuantity.schema,
  /**
   * `max(0, Σ efetivo − quantity)`. Anomalia auditável do race offline: dois
   * cozinheiros baixando do mesmo saco sem rede podem somar mais que o
   * original. O fato é aceito e **sinalizado**, nunca rejeitado — rejeitar um
   * write enfileirado é perda silenciosa (épico §5.1).
   */
  overrunQuantity: LabelFieldContract.overrunQuantity.schema,
  /**
   * O ledger: todos os movimentos e estornos da etiqueta, em array no próprio
   * doc (não subcoleção) para a baixa continuar sendo **um único write
   * offline-first** (`arrayUnion` + `increment`) e o detalhe não precisar de
   * segunda query. Append-only: desfazer é uma linha `UNDO`, nunca remoção.
   */
  baixas: LabelFieldContract.baixas.schema,
  /**
   * `1` = modelo antigo · `2` = ledger parcial. Serve à migração (#331 só toca
   * `< 2`), à auditoria e ao debugging. **Não substitui tolerância**: o código
   * continua defensivo, e a versão nunca vira um ponto rígido que derrube um
   * app. `.default(1)` cobre o doc que ainda não foi migrado.
   */
  labelSchemaVersion: LabelFieldContract.labelSchemaVersion.schema,
  /**
   * `baixaId → horário de INGESTÃO no servidor`. Par de `atMillis`, que é o
   * horário DECLARADO pelo aparelho: offline obriga a decidir pelo declarado,
   * então toda decisão baseada nele precisa ser sinalizável pelo recebido.
   * Escrito só pelo trigger (as rules barram o cliente). É **observabilidade**:
   * nenhuma decisão de vigente, status ou excesso lê este mapa, e chave
   * ausente é tolerada e preenchida no write seguinte.
   */
  movementReceivedAtMillis: LabelFieldContract.movementReceivedAtMillis.schema,
  /**
   * Tag NFC (UID de 7 B, capacidade ≥ a da NTAG213) vinculada à etiqueta (#900/#901, módulo `MOD_NFC_TAGS`) —
   * ver `LabelNfcSchema` em `labelNfc.ts`. Escrito SÓ pelo callable
   * `bindNfcTag`; as rules recusam o cliente. `null` = sem tag.
   */
  nfc: LabelNfcSchema.nullable().default(null),
  /**
   * Posição do aparelho na ÚLTIMA impressão (#996) — ver
   * `LabelDeviceLocationSchema` em `labelLocation.ts`. Escrito pelo cliente
   * nos ramos de impressão das rules (formato validado lá); `null` = impressa
   * por build anterior ao #996 ou nunca impressa.
   */
  printLocation: LabelDeviceLocationSchema.nullable().default(null),
  /**
   * Posição do aparelho na ÚLTIMA gravação NFC (#996). Escrito SÓ pelo
   * callable `bindNfcTag`; as rules recusam o cliente.
   */
  nfcLocation: LabelDeviceLocationSchema.nullable().default(null),
});
export type Label = z.infer<typeof LabelSchema>;

// ---------- Activity log ----------

/**
 * Activity event — shape gravado em `tenants/{tid}/activity_log/{eventId}`.
 * Os campos `createdAt` e `expiresAt` são Firestore Timestamps (necessário
 * para a TTL policy); não estão no schema Zod porque são server-side e o
 * parse seria rígido demais. Use `ActivityEventBody` abaixo ao construir o
 * payload antes de inserir os timestamps.
 */
export const ActivityEventBodySchema = z.object({
  id: ActivityEventIdSchema,
  tenantId: TenantIdSchema,
  scope: ActivityScopeSchema,
  action: ActivityActionSchema,
  entityType: EntityTypeSchema,
  entityId: z.string().nullable(),
  actorUid: z.string(),
  actorDisplayName: z.string(),
  actorRole: TenantRoleSchema.nullable(),
  summary: z.string().max(500),
  diff: z.record(z.string(), z.object({
    before: z.unknown(),
    after: z.unknown(),
  })).nullable().optional(),
  // B-20: metadata limitada a primitivos serializáveis (era `z.unknown()`,
  // aceitava classes/funções/BigInt/Date que o Admin SDK rejeita
  // silenciosamente ao gravar).
  metadata: metadataRecord.nullable().optional(),
});
export type ActivityEventBody = z.infer<typeof ActivityEventBodySchema>;

// ---------- Callable inputs ----------
export const CreateTenantInput = z.object({
  displayName: z.string().min(2).max(80),
  name: z.string().min(2).max(120),
  kind: TenantKindSchema,
  // B-21: ver TenantSchema.locale.
  locale: tenantLocale,
  timeZone: z.string().min(1),
  address: z.string().max(300).optional().default(''),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  placeId: z.string().max(300).nullable().optional(),
  demo: z.boolean().optional().default(false),
});
export type CreateTenantInput = z.infer<typeof CreateTenantInput>;

export const LogActivityInput = z.object({
  tenantId: TenantIdSchema,
  action: ActivityActionSchema,
  entityType: EntityTypeSchema,
  // M-41: cap razoável (qualquer entityId tipo branded cabe em 120
  // chars). Sem cap, payload arbitrário cabia.
  entityId: z.string().min(1).max(120).nullable().optional(),
  summary: z.string().max(500),
  // B-20: ver ActivityEventBodySchema.metadata.
  metadata: metadataRecord.nullable().optional(),
});
export type LogActivityInput = z.infer<typeof LogActivityInput>;

// ---------- Fase 11 — OTP, perfil verificado, onboarding ----------

export const RequestOtpInput = z.object({
  channel: OtpChannelSchema,
  phoneE164: PhoneE164Schema,
});
export type RequestOtpInput = z.infer<typeof RequestOtpInput>;

export const ConfirmOtpInput = z.object({
  channel: OtpChannelSchema,
  phoneE164: PhoneE164Schema,
  code: OtpCodeSchema,
});
export type ConfirmOtpInput = z.infer<typeof ConfirmOtpInput>;

export const RecordDeviceCountryInput = z.object({
  simCountry: z.string().length(2).nullable().optional(),
  networkCountry: z.string().length(2).nullable().optional(),
});
export type RecordDeviceCountryInput = z.infer<typeof RecordDeviceCountryInput>;

/**
 * Subset de roles aceito pelo callable `inviteUserToTenant` (tela `/users`
 * web — OWNER-only). Aceita apenas OWNER e MANAGER; STAFF é criado pela
 * tela Android `CreateStaffScreen` via callable
 * `createStaffWithCredentials` (sem email, com username sintético).
 */
const INVITE_USER_TO_TENANT_ROLES = ['OWNER', 'MANAGER'] as const;
export const InviteUserToTenantRoleSchema = z.enum(INVITE_USER_TO_TENANT_ROLES);
export type InviteUserToTenantRole = z.infer<typeof InviteUserToTenantRoleSchema>;

export const InviteUserToTenantInput = z.object({
  tenantId: TenantIdSchema,
  /** Nome da pessoa — vira o `displayName` inicial (e do Auth, se a conta for criada). */
  displayName: z.string().trim().min(2).max(120),
  email: z.email(),
  /**
   * Telefone do membro em E.164 (sem verificação OTP — confiança no owner). O
   * callable rejeita (`invalid-argument`) se o país do número (resolvido por
   * libphonenumber) não for o `countryCode` do tenant — telefone só do mesmo
   * país que o OWNER fixou no onboarding.
   */
  phoneE164: z.string().regex(/^\+\d{8,20}$/),
  role: InviteUserToTenantRoleSchema,
});
export type InviteUserToTenantInput = z.infer<typeof InviteUserToTenantInput>;

/**
 * Input de `adminSetContactVerification` (issue #257) — o super-admin carimba
 * ou revoga a verificação de contato de um usuário, sem passar por OTP/link.
 *
 * `tenantId` não é decoração: é o contexto que define **qual país** o telefone
 * pode ter (`tenant.countryCode`, imutável) e o que vai no audit. A pessoa pode
 * estar em mais de uma empresa, então quem chama escolhe explicitamente.
 *
 * `phoneE164` é obrigatório em `channel: 'PHONE'` + `verified: true` quando o
 * perfil ainda não tem número — é o caso de suporte que motiva a ferramenta
 * ("não recebo o SMS"). O callable valida o país com `resolveCountryFromPhone`.
 */
export const AdminSetContactVerificationInput = z.object({
  targetUid: UserIdSchema,
  tenantId: TenantIdSchema,
  channel: z.enum(['EMAIL', 'PHONE']),
  verified: z.boolean(),
  phoneE164: PhoneE164Schema.optional(),
  reason: z.string().max(400).optional(),
});
export type AdminSetContactVerificationInput = z.infer<typeof AdminSetContactVerificationInput>;

/**
 * Input de `adminSetUserAccountState` — bloqueia/desbloqueia a conta no
 * Firebase Auth (`disabled`). Reversível e sem apagar nada; não confundir com
 * o encerramento LGPD (`deleteUserAccount`).
 */
export const AdminSetUserAccountStateInput = z.object({
  targetUid: UserIdSchema,
  disabled: z.boolean(),
  reason: z.string().max(400).optional(),
});
export type AdminSetUserAccountStateInput = z.infer<typeof AdminSetUserAccountStateInput>;

/** Input de `adminRevokeUserSessions` — derruba o usuário de todos os aparelhos. */
export const AdminRevokeUserSessionsInput = z.object({
  targetUid: UserIdSchema,
  reason: z.string().max(400).optional(),
});
export type AdminRevokeUserSessionsInput = z.infer<typeof AdminRevokeUserSessionsInput>;

/**
 * Username sintético do STAFF criado via `createStaffWithCredentials`. O
 * callable monta o email no formato `{username}@{tenantId}.local` (domínio
 * `.local` reservado pela RFC 6762 — nunca colide com email real). O
 * usuário vê só o `{username}` na UI; o sufixo é detalhe interno.
 *
 * Regras (`docs/screens/staff-create.md` §4):
 *  - 2–32 caracteres
 *  - Lowercase a–z, dígitos, `.`, `_` e `-`
 *  - Não pode começar/terminar com `.`, `_` ou `-`
 *  - Não pode ter dois separadores em sequência
 */
export const StaffUsernameSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9](?:[a-z0-9]|[._-](?![._-])){0,30}[a-z0-9]$/, {
    message: 'Username inválido — use letras minúsculas, dígitos, ponto, underline ou hífen.',
  });
export type StaffUsername = z.infer<typeof StaffUsernameSchema>;

/**
 * Senha mínima aceita por `setMemberPassword` (OWNER/MANAGER trocando própria
 * senha real). Firebase Auth exige ≥6 caracteres; aqui subimos para ≤72
 * (limite bcrypt) e mínimo 6.
 *
 * NOTA: para STAFF, a credencial é um PIN de 6 dígitos (`StaffPinSchema`) — o
 * PIN é gravado como a própria senha do Firebase Auth via
 * `createStaffWithCredentials` / `setStaffPin`. STAFF nunca recebe senha
 * forte; usa só PIN + face local.
 */
export const StaffPasswordSchema = z.string().min(6).max(72);
export type StaffPassword = z.infer<typeof StaffPasswordSchema>;

/**
 * PIN de 6 dígitos numéricos. É a ÚNICA credencial do STAFF — gravado como
 * password no Firebase Auth pelos callables `createStaffWithCredentials` e
 * `setStaffPin`. STAFF digita o mesmo PIN na tela de bloqueio Android (que
 * por baixo faz `signInWithEmailAndPassword(syntheticEmail, pin)` no 1º
 * acesso e usa o refresh token cifrado pra restaurar sessão nas próximas).
 *
 * Espaço de chaves: 10^6 = 1M combinações. Mitigações:
 *  - Email sintético `{username}@{tenantId}.local` — atacante precisa
 *    conhecer username + tenantId pra montar credential.
 *  - Firebase Auth tem abuse protection por IP/account.
 *  - Tela de bloqueio Android tem back-off local (3/6/9 tentativas →
 *    locks 1min/30min/24h via PinVerifier).
 */
export const StaffPinSchema = z.string().regex(/^\d{6}$/, {
  message: 'PIN deve ter exatamente 6 dígitos.',
});
export type StaffPin = z.infer<typeof StaffPinSchema>;

/**
 * Input do callable `createStaffWithCredentials`. STAFF-only — o callable
 * grava `role = STAFF` no member doc e fixa `displayName = username`
 * (owner ajusta depois pela tela de edição se quiser nome humano).
 *
 * Autoriza qualquer membro ativo do tenant (OWNER/MANAGER/STAFF). Quem
 * cria define o PIN inicial (6 dígitos) e repassa offline para o STAFF; sem
 * email. O PIN vira a senha do Firebase Auth do email sintético —
 * `signInWithEmailAndPassword(syntheticEmail, pin)` no 1º acesso.
 */
export const CreateStaffWithCredentialsInput = z.object({
  tenantId: TenantIdSchema,
  username: StaffUsernameSchema,
  pin: StaffPinSchema,
  /**
   * Nome de exibição opcional — se omitido, vira igual ao `username`. O
   * STAFF pode editar depois pelo próprio perfil.
   */
  displayName: z.string().trim().min(1).max(120).optional(),
});
export type CreateStaffWithCredentialsInput = z.infer<typeof CreateStaffWithCredentialsInput>;

/**
 * Input do callable `setMemberPassword`. Autoriza OWNER/MANAGER do tenant
 * ou o próprio `targetUid === request.auth.uid` (`docs/screens/profile.md`
 * §6 — OWNER/MANAGER muda própria senha real). Para STAFF, use
 * `setStaffPin` em vez deste callable.
 */
export const SetMemberPasswordInput = z.object({
  tenantId: TenantIdSchema,
  targetUid: UserIdSchema,
  newPassword: StaffPasswordSchema,
});
export type SetMemberPasswordInput = z.infer<typeof SetMemberPasswordInput>;

/**
 * Input do callable `updateUserTenantRole` (OWNER, super-admin ou, desde a
 * #776, usuário do CRM com acesso ao cliente).
 *
 * Origem: #776 — 2026-09-29 10:45 +0200. Antes vivia em TRÊS cópias
 * (Functions, admin e — seria — o CRM); duplicação é bug neste repo.
 *
 * Usado por: `apps/functions/src/updateUserTenantRole.ts`, admin
 * `lib/callables.ts`, CRM `lib/callables.ts` (tela do cliente).
 */
export const UpdateUserTenantRoleInput = z.object({
  tenantId: TenantIdSchema,
  targetUid: UserIdSchema,
  newRole: TenantRoleSchema,
});
export type UpdateUserTenantRoleInput = z.infer<typeof UpdateUserTenantRoleInput>;

/**
 * Input do callable `removeUserFromTenant`. Origem: #776 — mesma
 * consolidação do `UpdateUserTenantRoleInput`.
 */
export const RemoveUserFromTenantInput = z.object({
  tenantId: TenantIdSchema,
  targetUid: UserIdSchema,
});
export type RemoveUserFromTenantInput = z.infer<typeof RemoveUserFromTenantInput>;

/**
 * Dispositivo conhecido do usuário — `/users/{uid}/devices/{deviceId}`
 * (issue #214). Escrito só pelo Admin SDK (`reportSignInDevice`), lido pelo
 * titular na tela `/account/sessions`.
 *
 * Não confundir com [DeviceSchema], que é `tenants/{tid}/devices/{deviceId}`:
 * aquele é o aparelho de chão de cozinha compartilhado pela equipe, alimentado
 * pelo heartbeat do Android e escopado ao tenant. Este aqui é por PESSOA, e
 * existe para responder "de onde entraram na minha conta?".
 *
 * `deviceId` no web é um UUID gerado no primeiro acesso e guardado em
 * localStorage; no Android é o `ANDROID_ID`. Limpar o storage do navegador
 * cria um dispositivo novo — e isso dispara um alerta, o que é o
 * comportamento desejado (não temos como distinguir "mesma pessoa limpou o
 * navegador" de "outra pessoa entrou").
 */
export const UserDeviceSchema = z.object({
  deviceId: DeviceIdSchema,
  uid: UserIdSchema,
  platform: z.enum(['android', 'web', 'ios']).default('web'),
  /** Rótulo legível: "Chrome — macOS" ou o modelo do aparelho. */
  deviceLabel: z.string().max(120).default(''),
  userAgent: z.string().max(500).default(''),
  firstSeenAtMillis: z.number().int().nonnegative(),
  lastSeenAtMillis: z.number().int().nonnegative(),
  /** IP visto no último acesso — vem do `rawRequest`, nunca do cliente. */
  lastIp: z.string().max(64).default(''),
  /** `true` quando este registro gerou aviso de dispositivo novo. */
  alerted: z.boolean().default(false),
});
export type UserDevice = z.infer<typeof UserDeviceSchema>;

/**
 * Input do callable `reportSignInDevice`. Chamado pelo cliente logo após um
 * sign-in bem-sucedido. `deviceLabel` é montado no cliente (que conhece o
 * navegador/aparelho); o IP fica a cargo do servidor.
 */
export const ReportSignInDeviceInput = z.object({
  deviceId: z.string().trim().min(8).max(128),
  platform: z.enum(['android', 'web', 'ios']),
  deviceLabel: z.string().trim().min(1).max(120),
  /** Idioma do aviso; cai em `pt-BR` quando ausente. */
  locale: z.string().max(35).optional(),
});
export type ReportSignInDeviceInput = z.infer<typeof ReportSignInDeviceInput>;

/** Input do callable `forgetMyDevice` — remove um dispositivo da própria lista. */
export const ForgetMyDeviceInput = z.object({
  deviceId: z.string().trim().min(8).max(128),
});
export type ForgetMyDeviceInput = z.infer<typeof ForgetMyDeviceInput>;

/**
 * Input do callable `setStaffPin`. Troca o PIN (= senha Firebase Auth) de um
 * STAFF. Autoriza:
 *  - OWNER/MANAGER do tenant trocando PIN de qualquer STAFF do mesmo tenant
 *    (recuperação cooperativa quando o STAFF esquece).
 *  - O próprio STAFF (`targetUid === request.auth.uid`) trocando o próprio
 *    PIN (via tela Perfil no Android).
 *
 * Revoga refresh tokens do STAFF (`auth.revokeRefreshTokens`) — a próxima
 * tentativa de unlock no device cai no fluxo "1º acesso" e exige o PIN novo.
 */
export const SetStaffPinInput = z.object({
  tenantId: TenantIdSchema,
  targetUid: UserIdSchema,
  newPin: StaffPinSchema,
});
export type SetStaffPinInput = z.infer<typeof SetStaffPinInput>;

/**
 * Input do callable `setMemberPhoto`. Define (ou remove) a foto de perfil de
 * um membro. Autoriza:
 *  - OWNER/MANAGER do tenant definindo a foto de qualquer membro do mesmo
 *    tenant (ex.: popular os avatares dos STAFF para o picker de unlock).
 *  - O próprio membro (`targetUid === request.auth.uid`).
 *
 * O cliente envia a imagem JPEG já redimensionada (≤120 KB pelo ImageResizer)
 * em base64 SEM o prefixo `data:`; o Admin SDK grava em
 * `profiles/{targetUid}/avatar.jpg` (mesmo path canônico do upload self),
 * persiste `photoPath`/`photoUrl` no profile e faz fan-out pros members.
 * `imageBase64 = null` remove a foto.
 */
export const SetMemberPhotoInput = z.object({
  tenantId: TenantIdSchema,
  targetUid: UserIdSchema,
  // ~120 KB de JPEG → ~160 KB em base64; o teto de 3 MB cobre o limite de
  // 2 MB da Storage rule de avatar com folga e ainda barra payload abusivo.
  imageBase64: z.string().min(1).max(3_000_000).nullable(),
});
export type SetMemberPhotoInput = z.infer<typeof SetMemberPhotoInput>;

// ---------- Sprint 2 — Concessão e revogação de super-admin ----------
export const SetSuperAdminInput = z.object({
  email: z.email().regex(/@etiquetachef\.com$/, {
    message: 'Email precisa ser do domínio @etiquetachef.com.',
  }),
  reason: z.string().min(1).max(500),
});
export type SetSuperAdminInput = z.infer<typeof SetSuperAdminInput>;

export const RemoveSuperAdminInput = z.object({
  uid: UserIdSchema,
  reason: z.string().min(1).max(500),
});
export type RemoveSuperAdminInput = z.infer<typeof RemoveSuperAdminInput>;

// ---------- Sprint 11 — Sub-roles granulares de super-admin ----------
export const AdminRoleSchema = z.enum(ALL_ADMIN_ROLES);

/**
 * Input para `setAdminRole`. Concede ou troca a role granular de um
 * usuário Workspace etiquetachef.com. Substitui `setSuperAdmin` (que
 * fica como shim → role `ROOT` por compat).
 */
export const SetAdminRoleInput = z.object({
  email: z.email().regex(/@etiquetachef\.com$/, {
    message: 'Email precisa ser do domínio @etiquetachef.com.',
  }),
  role: AdminRoleSchema,
  reason: z.string().min(1).max(500),
});
export type SetAdminRoleInput = z.infer<typeof SetAdminRoleInput>;

/**
 * Input para `removeAdminRole`. Revoga toda permissão admin do usuário
 * (apaga `adminRole` e `superAdmin` claims). Caller não pode remover
 * a si mesmo (lock-out guard).
 */
export const RemoveAdminRoleInput = z.object({
  uid: UserIdSchema,
  reason: z.string().min(1).max(500),
});
export type RemoveAdminRoleInput = z.infer<typeof RemoveAdminRoleInput>;

// ---------- Sprint 1 PR 0 — Audit log ----------
export const AuditActionSchema = z.enum(ALL_AUDIT_ACTIONS);
export const AuditTargetTypeSchema = z.enum(ALL_AUDIT_TARGET_TYPES);

/**
 * Documento `/audit/{eventId}`. Escrita só via Admin SDK (Functions); Rules
 * negam escrita direta. Leitura restrita a super-admin.
 */
export const AuditEventSchema = z.object({
  actorUserId: UserIdSchema,
  actorEmail: z.email(),
  action: AuditActionSchema,
  targetType: AuditTargetTypeSchema,
  targetId: nonEmpty,
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  reason: z.string().min(1).max(500).nullable(),
  // server timestamp setado pelo helper logAuditEvent.
  at: z.unknown(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export type {
  AuditAction,
  AuditTargetType,
} from './enums';

// ---------- Sprint 1 PR 0 — Callables admin (esqueletos) ----------

export type {
  TenantStatus,
  UserStatus,
} from './enums';

/**
 * Input do callable `suspendTenant` (admin).
 *
 * Origem: sem issue — commit ce27b5506, 2026-04-26 15:39 +0200.
 *
 * `reasonCode` (#935, item 5 — 2026-10-13 10:46 +0200) é o código que decide o
 * status: `BILLING_OVERDUE` → `SUSPENDED_BILLING`, demais → `SUSPENDED_ADMIN`.
 * `reason` é a nota livre do operador (vai ao audit). Até 2026-10-13 o admin
 * mandava só o texto livre, e o servidor só reconhecia o código quando o
 * operador digitava exatamente `BILLING_OVERDUE` — na prática toda suspensão
 * manual saía `SUSPENDED_ADMIN`.
 *
 * `reasonCode` é OPCIONAL de propósito: no release o Hosting (admin novo) sai
 * antes das Functions, e um admin antigo continua mandando só `reason`. Sem o
 * código, o servidor mantém o mapeamento antigo (`reason` igual a um código).
 *
 * Usado por: `apps/functions/src/admin/suspendTenant.ts`;
 * `apps/admin/src/lib/callables.ts` (`SuspendDialog`).
 */
export const SuspendTenantInput = z.object({
  tenantId: TenantIdSchema,
  reasonCode: z.enum(ALL_SUSPENSION_REASONS).optional(),
  reason: z.string().min(1).max(500),
});
export type SuspendTenantInput = z.infer<typeof SuspendTenantInput>;

export const ReactivateTenantInput = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().min(1).max(500),
});
export type ReactivateTenantInput = z.infer<typeof ReactivateTenantInput>;

/**
 * Liga/desliga a marca de "tenant de demonstração" (`Tenant.isDemo`).
 * Super-admin only (`setTenantDemoFlag`). Idempotente.
 */
export const SetTenantDemoFlagInput = z.object({
  tenantId: TenantIdSchema,
  isDemo: z.boolean(),
});
export type SetTenantDemoFlagInput = z.infer<typeof SetTenantDemoFlagInput>;

// ---------- Sprint 19 — Soft-delete reversível de tenant ----------
//
// `requestTenantDeletion` encerra a operação do tenant: status=DELETED,
// stop billing (helper `cancelSubscription`), revoga sessões via
// `sessionEpoch`, audita e dispara email LIFECYCLE_TENANT_DELETED ao
// owner. `restoreTenant` reverte (até 30d) — set status=ACTIVE, limpa
// `deletedAt`/`deletedByUid`/`deletionReason` e bumpa `sessionEpoch`
// novamente. Subscription/invoices não voltam — só o acesso operacional.
export const RequestTenantDeletionInputSchema = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().min(1).max(500),
});
export type RequestTenantDeletionInput = z.infer<typeof RequestTenantDeletionInputSchema>;

export const RestoreTenantInputSchema = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().min(1).max(500),
});
export type RestoreTenantInput = z.infer<typeof RestoreTenantInputSchema>;

// Hard-delete imediato (super-admin) de um tenant: apaga `/tenants/{id}` +
// subcoleções via `recursiveDelete` + objetos do Storage, sem passar pela
// janela de retenção de 90 dias do soft-delete.
//
// Por padrão (`force` ausente/false) é permitido SOMENTE quando o tenant não
// tem nenhum pagamento, produto nem etiqueta (guard server-side em
// `hardDeleteEmptyTenant`). Para tenants com dados, o caminho normal é o
// `requestTenantDeletion` (soft-delete + sweep).
//
// `force: true` = **purge forçado** (issue #83): pula o guard de vazio e
// apaga mesmo com dados. Usado para remover fixtures de teste que sobraram no
// Firestore de produção após o go-live e que `requestTenantDeletion` (soft)
// não limpa. A UI exige fricção extra (digitar o tenantId + confirmação
// destrutiva); o servidor audita com `mode: 'force_purge_with_data'`.
export const HardDeleteEmptyTenantInputSchema = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().min(1).max(500),
  force: z.boolean().optional().default(false),
});
export type HardDeleteEmptyTenantInput = z.infer<
  typeof HardDeleteEmptyTenantInputSchema
>;

// ---------- Sprint 13 — LGPD/GDPR data subject rights ----------
//
// Art. 18 LGPD II (acesso) + VII (portabilidade) e GDPR Art. 15/20: o
// titular pode solicitar uma cópia estruturada (JSON) dos próprios
// dados. `requestUserDataExport` empacota tudo em ZIP e envia signed
// URL por email. Cooldown de 1 hora entre exports do mesmo user para
// evitar abuso. Job persiste em `/userDataExports/{jobId}` para
// auditoria/reprocessamento.

/**
 * Formato do export. Sprint 13 entrega só JSON; CSV fica para sprint
 * futura quando houver demanda. Campo opcional para evolução
 * futura sem quebrar contrato.
 */
export const UserDataExportFormatSchema = z.enum(['json']);
export type UserDataExportFormat = z.infer<typeof UserDataExportFormatSchema>;

export const UserDataExportStatusSchema = z.enum([
  'PENDING',
  'READY',
  'FAILED',
]);
export type UserDataExportStatus = z.infer<typeof UserDataExportStatusSchema>;

export const RequestUserDataExportInputSchema = z.object({
  format: UserDataExportFormatSchema.default('json').optional(),
});
export type RequestUserDataExportInput = z.infer<
  typeof RequestUserDataExportInputSchema
>;

export const RequestUserDataExportOutputSchema = z.object({
  jobId: nonEmpty,
  status: UserDataExportStatusSchema,
  /** True quando o request bate em job já existente dentro do cooldown. */
  reused: z.boolean(),
});
export type RequestUserDataExportOutput = z.infer<
  typeof RequestUserDataExportOutputSchema
>;

/**
 * Entrada do callable `setMyAdsConsent` (issue #93 Bloco E). O titular
 * liga/desliga o consentimento de mídia paga do próprio perfil.
 */
export const SetMyAdsConsentInputSchema = z.object({
  ads: z.boolean(),
  source: z.enum(['web', 'android', 'ios']).optional(),
});
export type SetMyAdsConsentInput = z.infer<typeof SetMyAdsConsentInputSchema>;

export const SetMyAdsConsentOutputSchema = z.object({
  ads: z.boolean(),
  updatedAt: z.number().int().nonnegative(),
});
export type SetMyAdsConsentOutput = z.infer<typeof SetMyAdsConsentOutputSchema>;

/**
 * Entrada do callable `registerAppInstanceId` (issue #93 D.4). O cliente
 * Android envia o `app_instance_id` do Firebase uma vez; o backend persiste
 * em `/profiles/{uid}/adsIdentifiers/{appInstanceId}` para usar como
 * `external_id` no match cross-device do Meta CAPI.
 */
export const RegisterAppInstanceIdInputSchema = z.object({
  appInstanceId: z.string().min(1).max(200),
});
export type RegisterAppInstanceIdInput = z.infer<
  typeof RegisterAppInstanceIdInputSchema
>;

export const RegisterAppInstanceIdOutputSchema = z.object({
  registered: z.boolean(),
});
export type RegisterAppInstanceIdOutput = z.infer<
  typeof RegisterAppInstanceIdOutputSchema
>;

/**
 * Doc `/userDataExports/{jobId}`. Persistido para auditoria do
 * fluxo LGPD/GDPR (DPO consegue rastrear quem pediu o quê e quando).
 * Escrita exclusivamente via Admin SDK; leitura para o próprio user
 * + super-admin (via Rules). `downloadUrl` é um signed URL de 7 dias
 * sobre `user-data-exports/{userId}/{jobId}.zip` no bucket default.
 */
export const UserDataExportJobSchema = z.object({
  id: nonEmpty,
  userId: UserIdSchema,
  status: UserDataExportStatusSchema,
  format: UserDataExportFormatSchema,
  // B-21: ver TenantSchema.locale.
  locale: tenantLocale,
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  downloadUrl: z.url().nullable().optional(),
  storagePath: nonEmpty.nullable().optional(),
  expiresAt: z.number().int().nonnegative().nullable().optional(),
  sizeBytes: z.number().int().nonnegative().nullable().optional(),
  recordCounts: z
    .object({
      profile: z.number().int().nonnegative(),
      memberships: z.number().int().nonnegative(),
      labels: z.number().int().nonnegative(),
      audit: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  truncated: z.boolean().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type UserDataExportJob = z.infer<typeof UserDataExportJobSchema>;

// ---------- Sprint 13 PR C — Soft-delete da conta do user (LGPD/GDPR) ----------
//
// `deleteUserAccount` marca o user como `DELETED` e remove o usuário de todos
// os tenants em que era member (members são unidirecionais — a volta não traz
// memberships de volta). Não há restore self-service (#634, decisão do owner
// de 2026-10-28): `restoreUserAccount` recusa sempre, e a volta é a venda nova
// do CRM, que reativa a conta dentro da janela de 30 dias.
// Após `deletedAt + 30d`, o `userRetentionSweep` anonimiza o doc e apaga o
// Auth account fisicamente.

/**
 * Quem desligou o login de uma conta. Ver `UserSchema.accessDisabledBy`.
 *
 * - `SELF`: a própria pessoa pediu (exclusão de conta pelo app).
 * - `PLATFORM_ADMIN`: o suporte bloqueou. Só o suporte desfaz.
 * - `OWNER_REQUEST`: caiu junto porque o DONO do estabelecimento pediu a
 *   exclusão da conta dele — e isso encerra o estabelecimento para todo mundo.
 *   Não é `SELF` (esta pessoa não pediu nada) nem `PLATFORM_ADMIN` (o suporte
 *   não agiu). Também não é reativável pelo dono: ele está desligado junto.
 */
export const AccountDisabledBySchema = z.enum(['SELF', 'PLATFORM_ADMIN', 'OWNER_REQUEST']);
export type AccountDisabledBy = z.infer<typeof AccountDisabledBySchema>;

/**
 * Doc `/users/{uid}`. Sprint 13 PR C introduz a coleção raiz `/users` para
 * suportar lifecycle (status, deletedAt) e anonymization. Distinto de
 * `/profiles/{uid}` (perfil editável pelo titular). Escrita exclusivamente
 * via Admin SDK; titular pode ler o próprio doc para detectar status DELETED.
 */
/**
 * Janela do soft-delete LGPD da conta do titular: 30 dias entre
 * `users/{uid}.deletedAt` e o expurgo do `userRetentionSweep`.
 *
 * Origem: #634 — 2026-10-29 00:39 +0100 (subiu de `apps/functions` para cá na
 * F2, #1089, quando o web passou a mostrar a data do expurgo).
 *
 * Usado por: Functions (`privacy/accountPendingDeletion.ts` → `deleteUserAccount`,
 * `userRetentionSweep`, `provisionTenantForSale`) e web `apps/app`
 * (`routes/PendingDeletionPage.tsx`). Uma constante só: servidor e tela nunca
 * discordam do prazo.
 */
export const USER_SOFT_DELETE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export const UserSchema = z.object({
  id: UserIdSchema,
  email: z.email().or(z.literal('')).default(''),
  displayName: z.string().max(120).default(''),
  status: UserStatusSchema.default('ACTIVE'),
  /**
   * Millis epoch da última transição de `status` para DELETED. Limpo no
   * restore. Sweep `userRetentionSweep` apaga quando `now > deletedAt + 30d`.
   */
  deletedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * Millis epoch quando o doc foi anonimizado pelo `userRetentionSweep`.
   * Após isso `email` vira `deleted-{hash}@anonymized.local` e
   * `displayName` vira `Usuário excluído`.
   */
  anonymizedAt: z.number().int().nonnegative().nullable().optional(),
  /**
   * Millis epoch da última revogação de tokens (soft-delete bumpa este).
   * Cliente compara contra o último visto; difere → desloga.
   */
  sessionEpoch: z.number().int().nonnegative().nullable().optional(),
  /**
   * Login desligado no Firebase Auth (`disabled`). Espelho consultável do
   * estado que vive no Auth, onde nem o cliente nem as rules alcançam.
   *
   * Não confundir com `status: DELETED`, que é o soft-delete LGPD: ali os
   * vínculos com os estabelecimentos são apagados e o doc é anonimizado em 30
   * dias. Aqui nada é apagado — só o acesso para de funcionar.
   */
  accessDisabled: z.boolean().default(false),
  /**
   * QUEM desligou, e é por isto que este campo existe.
   *
   * `disabled` no Auth é global à identidade: vale para todos os
   * estabelecimentos da pessoa. Sem a proveniência, o dono de um deles
   * poderia religar uma conta que o suporte bloqueou por fraude. Com ela, o
   * dono só desfaz `SELF`; `PLATFORM_ADMIN` é do suporte e só o suporte
   * desfaz. Ausência do campo numa conta bloqueada é tratada como recusa
   * (fail-closed), o que cobre as contas bloqueadas antes desta regra existir.
   */
  accessDisabledBy: AccountDisabledBySchema.nullable().optional(),
  accessDisabledAt: z.number().int().nonnegative().nullable().optional(),
  /** Quem executou: o próprio uid em `SELF`, o do super-admin em `PLATFORM_ADMIN`. */
  accessDisabledActorUid: UserIdSchema.nullable().optional(),
  accessDisabledReason: z.string().max(400).nullable().optional(),
  createdAt: z.number().int().nonnegative().optional(),
  updatedAt: z.number().int().nonnegative().optional(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Input do callable `deleteUserAccount`. Confirmação textual obrigatória
 * para evitar clique acidental — `confirmation` precisa ser `'DELETE'`.
 * UID alvo vem de `request.auth.uid` (titular não exclui terceiros).
 */
export const DeleteUserAccountInputSchema = z.object({
  confirmation: z.literal('DELETE'),
});
export type DeleteUserAccountInput = z.infer<typeof DeleteUserAccountInputSchema>;

/**
 * Input do callable `restoreUserAccount` (aposentado na #634: recusa sempre).
 * Fica até o wrapper do web sair (F2 da #634, #1089).
 */
export const RestoreUserAccountInputSchema = z.object({});
export type RestoreUserAccountInput = z.infer<typeof RestoreUserAccountInputSchema>;

/**
 * Output dos callables `deleteUserAccount` / `restoreUserAccount`.
 * `idempotent` cobre o caso "já estava no estado solicitado".
 */
export const DeleteUserAccountOutputSchema = z.object({
  status: UserStatusSchema,
  deletedAt: z.number().int().nonnegative().nullable(),
  scheduledHardDeleteAt: z.number().int().nonnegative().nullable(),
  idempotent: z.boolean(),
});
export type DeleteUserAccountOutput = z.infer<typeof DeleteUserAccountOutputSchema>;

export const RestoreUserAccountOutputSchema = z.object({
  status: UserStatusSchema,
  idempotent: z.boolean(),
});
export type RestoreUserAccountOutput = z.infer<typeof RestoreUserAccountOutputSchema>;

// ---------- Autodesativação de acesso (reversível) ----------
//
// Fluxo IRMÃO do `deleteUserAccount` acima, e deliberadamente separado dele.
// O `delete*` é o corredor LGPD: apaga vínculos e anonimiza em 30 dias. Este
// aqui só desliga o login — os dados e as memberships ficam exatamente como
// estavam, porque pertencem ao estabelecimento para o qual a pessoa trabalha,
// e não à sessão dela. É o que o app iOS oferece.

/**
 * Input do callable `deactivateMyAccount`. Alvo é sempre `request.auth.uid`.
 *
 * A confirmação é `'DEACTIVATE'`, e não `'DELETE'`, justamente porque o rótulo
 * da tela continua dizendo "Excluir minha conta": quem lê o payload num log
 * precisa saber qual dos dois fluxos rodou sem depender do nome da callable.
 */
export const DeactivateMyAccountInput = z.object({
  confirmation: z.literal('DEACTIVATE'),
  reason: z.string().max(400).optional(),
});
export type DeactivateMyAccountInput = z.infer<typeof DeactivateMyAccountInput>;

export const DeactivateMyAccountOutput = z.object({
  deactivated: z.literal(true),
  /**
   * Quantas pessoas perderam o acesso, incluindo quem pediu.
   *
   * Pedir a exclusão da conta sendo dono **encerra o estabelecimento para toda
   * a equipe** — não é uma ação sobre uma identidade só, e a tela precisa poder
   * dizer isso.
   */
  affectedUsers: z.number().int().nonnegative(),
  /** A conta já estava desligada antes desta chamada. */
  idempotent: z.boolean(),
  /**
   * Estabelecimentos que ficaram sem NENHUM dono capaz de entrar.
   *
   * Não bloqueia (decisão do owner, 2026-09-17): serve para avisar o suporte,
   * que é a única mitigação para um estabelecimento órfão cujo billing segue
   * correndo.
   */
  soleOwnerTenantIds: z.array(TenantIdSchema),
});
export type DeactivateMyAccountOutput = z.infer<typeof DeactivateMyAccountOutput>;

/**
 * Input do callable `notifyExcludedAccountAttempt` — o app avisa que alguém
 * tentou criar conta (`SIGNUP`, o Auth respondeu "e-mail já cadastrado") ou
 * entrar (`SIGNIN`, o Auth respondeu "usuário desativado") com o e-mail de uma
 * conta que a própria pessoa excluiu (`deactivateMyAccount`).
 *
 * Origem: #942 — 2026-10-12 17:37 +0200 (owner: quem tenta voltar com o
 * e-mail de conta excluída tem de ver "conta excluída" e receber e-mail, não
 * "e-mail já cadastrado").
 *
 * Sem autenticação: quem chama não consegue entrar, por definição.
 */
export const NotifyExcludedAccountAttemptInput = z.object({
  email: z.email().max(254),
  attempt: z.enum(['SIGNUP', 'SIGNIN']),
});
export type NotifyExcludedAccountAttemptInput = z.infer<typeof NotifyExcludedAccountAttemptInput>;

/**
 * `excluded: true` só para conta desligada pela própria pessoa
 * (`users/{uid}.accessDisabledBy == 'SELF'`). Conta inexistente, ativa ou
 * desligada por dono/admin → `false`, e o app mostra o erro de sempre.
 * `emailSent` é `false` quando o aviso já saiu nas últimas 24 h.
 */
export const NotifyExcludedAccountAttemptOutput = z.object({
  excluded: z.boolean(),
  emailSent: z.boolean(),
});
export type NotifyExcludedAccountAttemptOutput = z.infer<typeof NotifyExcludedAccountAttemptOutput>;

/**
 * Input do callable `getMyAccountPlanInfo` — quem gerencia a assinatura deste
 * estabelecimento, para o destaque da "Conta & Plano".
 *
 * Origem: #942 — 2026-10-13 08:40 +0200 (owner: demo mostra "Conta de
 * demonstração"; tenant de parceiro mostra o nome do parceiro).
 *
 * Usado por: iOS (`FunctionsAccountPlanInfoRepository`), e desde 2026-10-14
 * (#967) web (`usePartnerManagerName`) e Android — a linha "Assinatura
 * gerenciada por {nome}" do cliente do parceiro revendedor.
 */
export const GetMyAccountPlanInfoInput = z.object({
  tenantId: z.string().min(1).max(128),
});
export type GetMyAccountPlanInfoInput = z.infer<typeof GetMyAccountPlanInfoInput>;

/**
 * `isDemo`: `tenants/{tid}.isDemo`. `partnerName`: `crm_partners/{partner_id}.name`
 * quando o tenant tem parceiro (`commercial_mode == PARTNER` ou `partner_id`
 * preenchido); `null` sem parceiro ou com cadastro ausente. O nome vem por
 * callable porque `crm_partners` é fechado ao cliente (só super-admin).
 */
export const GetMyAccountPlanInfoOutput = z.object({
  isDemo: z.boolean(),
  isPartner: z.boolean(),
  partnerName: z.string().nullable(),
});
export type GetMyAccountPlanInfoOutput = z.infer<typeof GetMyAccountPlanInfoOutput>;

/**
 * De quem é o suporte que o cliente vê (#968): o do parceiro do tenant ou o
 * do EtiquetaChef (Remote Config `support_email` / `support_whatsapp_<lang>`).
 */
export const SupportContactSource = {
  PARTNER: 'PARTNER',
  ETIQUETACHEF: 'ETIQUETACHEF',
} as const;
export const SupportContactSourceSchema = z.enum([SupportContactSource.PARTNER, SupportContactSource.ETIQUETACHEF]);
export type SupportContactSource = z.infer<typeof SupportContactSourceSchema>;

/**
 * Input do callable `getTenantSupportContact` — qual suporte o membro deste
 * estabelecimento vê nos apps.
 *
 * Origem: #968 — 2026-10-14 10:48 +0200.
 *
 * Usado por: web `apps/app` (`useTenantSupportContact`), Android
 * (`FunctionsSupportContactRepository`), iOS (`FunctionsSupportContactRepository`).
 */
export const GetTenantSupportContactInput = z.object({
  tenantId: z.string().min(1).max(128),
});
export type GetTenantSupportContactInput = z.infer<typeof GetTenantSupportContactInput>;

/**
 * Saída de `getTenantSupportContact` (#968). Objeto PLANO (e não união
 * discriminada) para o Kotlin e o Swift decodificarem sem polimorfismo.
 *
 * - `source: PARTNER` → `phone`, `whatsapp` (E.164, com `+`) e `email` vêm
 *   preenchidos com o suporte L1 do parceiro (`crm_partners/{id}.supportContact`);
 *   `partnerName` é o nome do parceiro, ou `null` se o cadastro não tem nome.
 * - `source: ETIQUETACHEF` → os quatro campos `null`; o app usa o próprio
 *   Remote Config, como antes da #968.
 *
 * Exemplo: { "source": "PARTNER", "partnerName": "Parceiro 1",
 *   "phone": "+5541999990000", "whatsapp": "+5541999990000",
 *   "email": "suporte@parceiro.example" }
 */
export const GetTenantSupportContactOutput = z.object({
  source: SupportContactSourceSchema,
  partnerName: z.string().nullable(),
  phone: PhoneE164Schema.nullable(),
  whatsapp: PhoneE164Schema.nullable(),
  email: z.string().nullable(),
});
export type GetTenantSupportContactOutput = z.infer<typeof GetTenantSupportContactOutput>;

/**
 * Input do callable `reactivateTenantMemberAccount` — o dono religa o acesso
 * de alguém da casa dele.
 */
export const ReactivateTenantMemberAccountInput = z.object({
  tenantId: TenantIdSchema,
  targetUid: UserIdSchema,
  reason: z.string().max(400).optional(),
});
export type ReactivateTenantMemberAccountInput = z.infer<
  typeof ReactivateTenantMemberAccountInput
>;

export const ReactivateTenantMemberAccountOutput = z.object({
  reactivated: z.literal(true),
  idempotent: z.boolean(),
});
export type ReactivateTenantMemberAccountOutput = z.infer<
  typeof ReactivateTenantMemberAccountOutput
>;

/**
 * Input do callable `getTenantMembersAccountState`.
 *
 * Existe porque `disabled` mora no Firebase Auth e o marcador em
 * `/users/{uid}`, que as rules abrem só para o titular e para o super-admin —
 * o dono não alcança nenhum dos dois direto. Espelhar no doc de membro
 * resolveria a leitura e criaria drift: o `disabled` muda pelo console e pelo
 * painel de ops, e nenhum desses caminhos reescreveria N docs de membro.
 */
export const GetTenantMembersAccountStateInput = z.object({
  tenantId: TenantIdSchema,
});
export type GetTenantMembersAccountStateInput = z.infer<
  typeof GetTenantMembersAccountStateInput
>;

export const TenantMemberAccountStateSchema = z.object({
  uid: UserIdSchema,
  disabled: z.boolean(),
  disabledBy: AccountDisabledBySchema.nullable(),
  disabledAt: z.number().int().nonnegative().nullable(),
  /**
   * O SERVIDOR decide, e o cliente só obedece. Vale `true` apenas quando a
   * conta está desligada E foi a própria pessoa que a desligou — deixar o
   * cliente deduzir isso espalharia a regra de segurança por duas camadas.
   */
  canReactivate: z.boolean(),
});
export type TenantMemberAccountState = z.infer<typeof TenantMemberAccountStateSchema>;

export const GetTenantMembersAccountStateOutput = z.object({
  states: z.array(TenantMemberAccountStateSchema),
});
export type GetTenantMembersAccountStateOutput = z.infer<
  typeof GetTenantMembersAccountStateOutput
>;

// ---------- Sprint 13 PR C — Self-service tenant deletion (OWNER) ----------
//
// `requestSelfTenantDeletion` cria um token OOB e envia email com link
// `/privacy/confirm-tenant-deletion?token=...` (válido 24h). O OWNER abre
// o link e dispara `confirmTenantDeletion`, que reaproveita
// `runRequestTenantDeletion` (caminho super-admin) para o soft-delete real.

export const RequestSelfTenantDeletionInputSchema = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().min(1).max(500).optional(),
});
export type RequestSelfTenantDeletionInput = z.infer<
  typeof RequestSelfTenantDeletionInputSchema
>;

export const RequestSelfTenantDeletionOutputSchema = z.object({
  tenantId: TenantIdSchema,
  expiresAt: z.number().int().nonnegative(),
});
export type RequestSelfTenantDeletionOutput = z.infer<
  typeof RequestSelfTenantDeletionOutputSchema
>;

export const ConfirmTenantDeletionInputSchema = z.object({
  token: nonEmpty.max(128),
});
export type ConfirmTenantDeletionInput = z.infer<typeof ConfirmTenantDeletionInputSchema>;

export const ConfirmTenantDeletionOutputSchema = z.object({
  success: z.boolean(),
  tenantId: TenantIdSchema,
});
export type ConfirmTenantDeletionOutput = z.infer<typeof ConfirmTenantDeletionOutputSchema>;

/**
 * Doc `/tenantDeletionTokens/{tokenId}`. Token OOB de uso único.
 * `tokenId` é hex de 32 bytes random (=64 chars). `used: true` é flag
 * de single-use; a validade de 24 h é conferida pelo `confirmTenantDeletion`
 * contra `expiresAt`. Escrita via Admin SDK; titular lê só o próprio token
 * (rule cobre).
 *
 * TTL — NÃO funciona hoje (#795, FN-6, registrado em 2026-10-03 18:22 +0200):
 * há política `tenantDeletionTokens.expiresAt` em `firestore.indexes.json`,
 * mas o TTL do Firestore só apaga por campo `Timestamp`, e `expiresAt` é
 * número (epoch ms) — os tokens nunca expiram fisicamente. Corrigir exige
 * gravar um campo Timestamp novo (ex.: `expireAt`) e trocar a política, em
 * dois deploys; não foi feito na #795.
 */
export const TenantDeletionTokenSchema = z.object({
  id: nonEmpty,
  tenantId: TenantIdSchema,
  requestedByUid: UserIdSchema,
  createdAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
  used: z.boolean(),
  usedAt: z.number().int().nonnegative().nullable().optional(),
  reason: z.string().max(500).nullable().optional(),
});
export type TenantDeletionToken = z.infer<typeof TenantDeletionTokenSchema>;

// ---------- Billing — plano único + cobrança recorrente (Sprint 16) ----------

export const PlanIdSchema = z.enum(ALL_PLAN_IDS);
export const CurrencySchema = z.enum(ALL_CURRENCIES);
export const BillingCycleSchema = z.enum(ALL_BILLING_CYCLES);
export const CommercialModeSchema = z.enum(ALL_COMMERCIAL_MODES);
export const PricingSourceSchema = z.enum(ALL_PRICING_SOURCES);
export const CheckoutOriginSchema = z.enum(ALL_CHECKOUT_ORIGINS);
export const SubscriptionStatusSchema = z.enum(ALL_SUBSCRIPTION_STATUSES);
export const InvoiceStatusSchema = z.enum(ALL_INVOICE_STATUSES);
export const PaymentStatusSchema = z.enum(ALL_PAYMENT_STATUSES);
export const PaymentMethodTypeSchema = z.enum(ALL_PAYMENT_METHOD_TYPES);
export const PaymentMethodStatusSchema = z.enum(ALL_PAYMENT_METHOD_STATUSES);
/**
 * Sprint 40 — PSP virou CATÁLOGO ABERTO (`/config/psps`): qualquer slug é um
 * PSP válido (decisão owner 2026-06-11 — revoga o "fechado em 4"). Relaxar de
 * enum p/ slug-string é backward-compat: todo valor legado
 * (stripe/paddle/dlocal/ebanx/...) é slug válido. Os valores bem-conhecidos
 * seguem no const `PaymentProvider` (enums.ts) p/ referência no código (guards,
 * registry). A integração real de cobrança continua exigindo `<psp>.ts` +
 * secrets + registry — o catálogo só diz "quais PSPs existem/são selecionáveis".
 */
export const PaymentProviderSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/, {
    message: 'PSP id precisa ser slug minúsculo (a-z, 0-9, _).',
  })
  .max(40);
export const SuspensionReasonSchema = z.enum(ALL_SUSPENSION_REASONS);
export const RefundStatusSchema = z.enum(ALL_REFUND_STATUSES);
export const RefundReasonSchema = z.enum(ALL_REFUND_REASONS);

export type {
  BillingCycle,
  Currency,
  InvoiceStatus,
  PaymentMethodStatus,
  PaymentMethodType,
  PaymentProvider,
  PaymentStatus,
  PlanId,
  RefundReason,
  RefundStatus,
  SubscriptionStatus,
  SuspensionReason,
} from './enums';

/**
 * Singleton `/config/billing/settings`. Editável só pelo super-admin
 * via callable `updateBillingSettings`. Toggles globais e janelas de
 * lembrete/suspensão.
 */
export const BillingSettingsSchema = z.object({
  // Master switch
  billing_email_enabled: z.boolean(),
  // Toggles por template
  invoice_created_email_enabled: z.boolean(),
  invoice_due_soon_email_enabled: z.boolean(),
  invoice_overdue_email_enabled: z.boolean(),
  payment_success_email_enabled: z.boolean(),
  payment_failed_email_enabled: z.boolean(),
  // `.default(true)` é retrocompat: o doc `/config/billing/settings/singleton`
  // já existe em prod sem este campo (Sprint 5). Sem default, o `safeParse` em
  // `getBillingSettings` falharia e descartaria TODA a config do admin (cairia
  // nos defaults). Com default, o parse passa e preserva os demais campos.
  payment_action_required_email_enabled: z.boolean().default(true),
  // Janelas (em dias)
  due_soon_days_before: z.array(z.number().int().positive()).min(1).max(10),
  overdue_reminder_days: z.array(z.number().int().positive()).min(1).max(10),
  suspend_after_days_overdue: z.number().int().positive(),
  next_invoice_days_before_due: z.number().int().nonnegative(),
  // Metadata
  updatedAt: z.number().int().nonnegative(),
  updatedBy: UserIdSchema.nullable(),
});
export type BillingSettings = z.infer<typeof BillingSettingsSchema>;

/**
 * Sprint 35 — Sub-schema para `external_prices[provider]` em
 * `PlanPriceSchema`. Map dinâmico permite embarcar PSP novo (Paddle,
 * dLocal, eBanx) sem mudar shape do schema — só registrar mais uma key.
 * `superseded_ids[]` guarda os Price IDs antigos do provider quando
 * `updatePlanPrice` cria uma nova versão (Stripe archive); útil para
 * resolução histórica de invoices.
 */
export const ProviderPriceRefSchema = z.object({
  monthly_id: nonEmpty.nullable(),
  yearly_id: nonEmpty.nullable(),
  superseded_ids: z.array(nonEmpty).default([]),
});
export type ProviderPriceRef = z.infer<typeof ProviderPriceRefSchema>;

/**
 * Doc `/config/planPrices/{...}` — tabela de preços editável pelo
 * super-admin. Sprint 16: chave `{country}_{cycle}` (mensal + anual por
 * país). Sprint 35: chave canônica vira `{sku}_{currency}` (BASE_BRL,
 * BASE_USD, SUPPLIER_BRL, SUPPLIER_USD; EUR entra na sprint Paddle UE).
 * Campos legacy (`plan_id`, `country_code`, `amount`, `billing_cycle`,
 * `is_default`, `stripe_price_id`) são `optional()` durante a migração:
 * os docs novos `{sku}_{currency}` (gravados por `seed-stripe-products.ts`
 * e `migrate-plan-prices-to-sku-currency.ts`) NÃO os carregam. Mantê-los
 * `required()` derrubava o `safeParse` desses docs em TODO consumidor
 * estrito (admin `subscribePlanPrices`, `addAddonToSubscription`,
 * `migrateSubscriptionsToCurrentPrice`) — a tela `/billing/plan-prices`
 * ficava em branco e os callables lançavam "schema inválido". PR B
 * `migrate-plan-prices-to-sku-currency.ts` agrupa por moeda e marca os
 * antigos com `legacy: true`. Sprint pós-35 promove
 * `sku`/`currency`/`monthly_amount_cents` para `required()` e remove
 * os legacy.
 */
export const PlanPriceSchema = z.object({
  id: nonEmpty,
  /** LEGACY (Sprint 16). Sprint 35 prefere `sku`. `optional()`: os docs
   * novos `{sku}_{currency}` não o carregam (sprint pós-35 promove `sku`
   * a required e remove este). */
  plan_id: PlanIdSchema.optional(),
  /** LEGACY (Sprint 16). Sprint 35 derruba o eixo país do catálogo (vira
   * `currency`); `optional()` — só os docs antigos `(country, cycle)` o têm. */
  country_code: CountrySchema.optional(),
  currency: CurrencySchema,
  /**
   * Sprint 35 — SKU canônico (`BASE`, `SUPPLIER_INTEGRATION`, ...).
   * Optional durante migração — `migrate-plan-prices-to-sku-currency.ts`
   * cria 4 docs novos derivando do `plan_id` legacy (`STANDARD` →
   * `BASE`); só os docs novos têm este campo populado.
   */
  sku: SkuSchema.optional(),
  /**
   * LEGACY (Sprint 16) — `amount` em UNIDADES decimais (catálogo
   * display-side). Sprint 35 usa `monthly_amount_cents` /
   * `yearly_amount_cents` em CENTS (alinhado com Subscription/Invoice).
   * `optional()`: os docs novos usam `monthly_amount_cents`; só os
   * antigos `(country, cycle)` têm `amount` decimal.
   */
  amount: z.number().nonnegative().optional(),
  /** LEGACY (Sprint 16) — eixo "cycle" descrito agora pelos campos
   * `monthly_amount_cents` + `yearly_amount_cents` no mesmo doc.
   * `optional()` — ausente nos docs novos `{sku}_{currency}`. */
  billing_cycle: BillingCycleSchema.optional(),
  /** LEGACY (Sprint 16) — relevante apenas para a transição do catálogo.
   * `optional()` — ausente nos docs novos. */
  is_default: z.boolean().optional(),
  /**
   * Sprint 35 — preço mensal em CENTS (anchor absoluto). Pricing inicial
   * pendente de confirmação do owner antes do PR C rodar o seed Stripe.
   * Optional durante migração para não quebrar docs legacy que só têm
   * `amount` decimal.
   */
  monthly_amount_cents: z.number().int().positive().optional(),
  /**
   * Sprint 35 — modo de cálculo do desconto anual. `DISCOUNT_PCT` =
   * `yearly = monthly × 12 × (1 - pct)`; `MANUAL` = owner digita
   * absoluto. Default `DISCOUNT_PCT` quando o owner não especificar.
   */
  yearly_mode: z.enum(ALL_YEARLY_DISCOUNT_MODES).optional(),
  /**
   * Sprint 35 — quando `yearly_mode === 'DISCOUNT_PCT'`, percentual de
   * desconto sobre 12× mensal (0-80%). Warning visual acima de 50%.
   */
  yearly_discount_pct: z.number().min(0).max(80).nullable().optional(),
  /**
   * Sprint 35 — preço anual em CENTS resolvido (auto via desconto ou
   * manual). Pricing congelado por tenant: alterar este valor afeta só
   * novos signups e renovações pós-aviso de 30 dias (Guard 5).
   */
  yearly_amount_cents: z.number().int().positive().optional(),
  /**
   * Sprint 35 — map dinâmico `provider → { monthly_id, yearly_id,
   * superseded_ids[] }`. PR C seed-stripe-products popula `STRIPE`;
   * sprint Paddle UE adiciona `PADDLE`; embarcar dLocal/eBanx adiciona
   * mais entries sem mudar shape. Optional durante migração — docs
   * legacy só têm `stripe_price_id` no topo.
   *
   * **`partialRecord` é obrigatório** (Sprint 35 PR J fix, 2026-05-26):
   * em Zod v4 o default `z.record(z.enum(...), V)` é STRICT — exige todas
   * as 7 chaves do enum presentes. `seed-stripe-products.ts` popula só
   * `stripe`, então o default falhava `safeParse` silenciosamente em
   * `buildPriceIdToSkuIndex` (handleSubscriptionUpdated PR E) → items[]
   * nunca reconciliava em prod. `partialRecord` aceita qualquer subset
   * de providers — alinha com a semântica original ("provider opcional,
   * shape estável quando presente").
   */
  external_prices: z
    .partialRecord(z.enum(ALL_PAYMENT_PROVIDERS), ProviderPriceRefSchema)
    .optional(),
  /**
   * Sprint 5 — Stripe Price ID (`price_...`) correspondente ao schema
   * single-cycle legacy. Sprint 35 movido para `external_prices.STRIPE`.
   * Mantido opcional durante migração para retrocompat.
   * B-19: regex valida o prefixo `price_*` esperado.
   */
  stripe_price_id: stripePriceId.optional(),
  /**
   * Sprint 35 — marca docs antigos `(country, cycle)` após migração para
   * `(sku, currency)`. PR B `migrate-plan-prices-to-sku-currency.ts` seta
   * `true`; cleanup pós-Sprint apaga docs marcados.
   */
  legacy: z.boolean().optional(),
  created_at: z.number().int().nonnegative(),
  updated_at: z.number().int().nonnegative(),
  updated_by: UserIdSchema.nullable(),
});
export type PlanPrice = z.infer<typeof PlanPriceSchema>;

/**
 * Sprint 35 — Item de uma `Subscription.items[]`. `items[0]` é sempre o
 * BASE **vivo** (obrigatório, garantido pelos callables); demais entradas são
 * add-ons opcionais (`SUPPLIER_INTEGRATION`, ...) e tombstones. `amount_cents`
 * é snapshot no momento da adição (pricing congelado por item — alinha
 * com princípio 3 do CLAUDE.md). `mbg_consumed` evita reembolso múltiplo
 * de MBG no mesmo SKU (mesmo add-on adicionado-removido-readicionado só
 * tem janela MBG na primeira adição). `pending_removal_at` agenda
 * remoção end-of-period via Subscription Schedule (Guard 2).
 *
 * **`items[]` é livro-razão append-only, não espelho do gateway** (#409).
 * Item que sai da Stripe vira tombstone (`removed_at` carimbado, item mantido
 * no array); nada é apagado. Dois contratos dependem disso e quebram em
 * silêncio se alguém "limpar" o array: `mbg_consumed`, que é uma afirmação
 * sobre o SKU ao longo do tempo, e o `seq` da chave de idempotência de
 * `addAddonToSubscription` (#396), que conta os itens do SKU — se ele regredir,
 * uma chave já consumida volta a valer dentro das 24 h de janela da Stripe e a
 * re-adição devolve o item MORTO em vez de criar um novo, com o cliente pagando
 * sem receber. Foi assim que a #409 nasceu: o reconciler de webhook descartava
 * o tombstone. Compactar o array um dia exige, no mesmo commit, tirar o `seq`
 * dessa contagem.
 *
 * Reset do array só é sancionado quando a ASSINATURA é outra (re-checkout em
 * `handleCheckoutCompleted`, reinit de `admin/reconcileSubscriptionFromGateway`)
 * — ali o `external_subscription_id`, que também compõe a chave, muda junto.
 */
export const SubscriptionItemSchema = z.object({
  sku: SkuSchema,
  /** Stripe Subscription Item ID (`si_*`). Populated pelo reconciler de
   * webhook `customer.subscription.updated`.
   *
   * `null` em item `origin: 'GRANT'` (#679 D22): módulo concedido não existe
   * no gateway — e cortesia, demo e parceiro não têm gateway nenhum. */
  external_item_id: nonEmpty.nullable(),
  /** Stripe Price ID (`price_*`) anexado ao item. Snapshot no momento da
   * adição — pricing congelado mesmo se o catálogo mudar.
   *
   * `null` pelo mesmo motivo de `external_item_id`. */
  external_price_id: nonEmpty.nullable(),
  /** Preço cobrado por este item em CENTS. Snapshot no momento da
   * adição; nunca muda mid-cycle (Guard 5 protege mudanças). */
  amount_cents: z.number().int().nonnegative(),
  billing_cycle: BillingCycleSchema,
  added_at: z.number().int().nonnegative(),
  /** True após primeira adição: bloqueia reembolso MBG no caso de
   * remoção→re-adição do mesmo SKU (janela MBG é por primeira adição). */
  mbg_consumed: z.boolean(),
  /** Millis epoch da remoção definitiva; null = ativo. */
  removed_at: z.number().int().nonnegative().nullable(),
  /** Remoção end-of-period agendada (anual sem MBG, Guard 2); null = sem
   * remoção agendada. Quem EXECUTA é o passe 5 do `dailyBillingTick`, ~72 h
   * antes da renovação (#404) — até lá o item segue na Stripe, cobrado e
   * ativo, e o pedido pode ser desfeito sem tocar no gateway (#405). */
  pending_removal_at: z.number().int().nonnegative().nullable(),
  /**
   * Millis epoch em que o item saiu da Stripe pelo passe de remoção agendada
   * (#404). Enquanto `null`, o item ainda está na assinatura e a remoção pode
   * ser desfeita sem custo; depois disso, desfazer exigiria recriar o item e
   * é recusado (`addon_removal_in_progress`).
   *
   * `.optional()` de propósito: os outros campos são required, e um campo novo
   * obrigatório faria `SubscriptionSchema.safeParse` falhar em TODA
   * subscription já gravada — os callables devolvem `internal` nesse caso.
   * Distinto de `removed_at`, que continua significando "já não vale" e é
   * sempre passado: entre o gateway sair e o fim do ciclo pago, o cliente
   * mantém o add-on que pagou.
   */
  gateway_removed_at: z.number().int().nonnegative().nullable().optional(),
  /**
   * De onde veio este item: `GATEWAY` é o que a Stripe lista e cobra;
   * `GRANT` é módulo **concedido** por nós, sem contrapartida no gateway
   * (#679 D22 — o `MOD_PRINT_TSPL` dos tenants que já existiam).
   *
   * `.optional()` pelo mesmo motivo de `gateway_removed_at`: campo novo
   * obrigatório reprovaria toda subscription já gravada. **Ausente = GATEWAY**,
   * e esse default vale só em memória — `where('items.origin','==','GATEWAY')`
   * não casa documento sem o campo, e é por isso que nenhum filtro do servidor
   * pode ser escrito por igualdade aqui (a mesma armadilha C5 do modo comercial).
   *
   * O que o `GRANT` exige de quem mexe no reconciler: ele **não** aparece na
   * lista da Stripe, então todo código que reconstrói `items[]` a partir do
   * gateway tem de reanexá-lo em vez de tratá-lo como órfão. São três pontos,
   * e os três têm teste por mutação: `reconcileItems`, o laço de órfãos e
   * `diffItems` (que chaveava só por `external_item_id`, agora nulo).
   */
  origin: z.enum(ALL_SUBSCRIPTION_ITEM_ORIGINS).optional(),
  /**
   * Item vendido por **parceiro** (#743, F8 da #679 — `origin: 'PARTNER'`):
   * quanto o parceiro nos REPASSA por ciclo deste item, em cents. O
   * `amount_cents` do mesmo item é o que o parceiro cobra do cliente dele —
   * informativo para nós (é sobre ele que o piso D12 incide), porque quem
   * cobra é o parceiro. Soma em `SubscriptionSchema.total_remit_cents`.
   *
   * Só existe em item PARTNER, e nele é obrigatório (superRefine abaixo). O
   * item não tem id de gateway pelo mesmo motivo do GRANT: nós não emitimos
   * cobrança nenhuma a esse tenant.
   */
  remit_cents: z.number().int().nonnegative().optional(),
  /**
   * Por que este item foi concedido, e por quem. Só existe em `origin: 'GRANT'`
   * — é o rastro que responde "de onde saiu este módulo que ninguém comprou?"
   * meses depois, quando a concessão já não estiver na memória de ninguém.
   */
  grant: z
    .object({
      reason: z.string().min(1).max(500),
      granted_at: z.number().int().nonnegative(),
      granted_by: UserIdSchema.nullable(),
    })
    .optional(),
  /**
   * Proporção do ciclo corrente ainda **não paga** (#737, 9.24 da #679).
   *
   * Assinatura em Pix Automático não prorrateia dentro do mandato (o Pix
   * cobra uma vez por ciclo): o módulo adicionado pelo consultor entra na
   * Stripe com `proration_behavior: 'none'` e a proporção vai num Checkout
   * avulso (`mode: 'payment'`). Enquanto este campo existir o item está na
   * assinatura — a fatura seguinte o cobra —, mas **não dá capability**
   * (`computeCapabilities`). O passe 7 do `dailyBillingTick` tira o item se
   * `expires_at` passar sem pagamento.
   *
   * `.optional()` pelo mesmo motivo de `gateway_removed_at`. E quem
   * reconstrói o item a partir do gateway (`reconcileItems`) tem de copiá-lo
   * do item local — a Stripe não sabe dele.
   */
  pending_proration: z
    .object({
      checkout_session_id: nonEmpty,
      amount_cents: z.number().int().positive(),
      currency: CurrencySchema,
      created_at: z.number().int().nonnegative(),
      expires_at: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  /**
   * Reajuste para cima agendado pelo consultor (#737, 9.17 da #679): o valor
   * novo só vale na primeira renovação ≥ 30 dias depois do aviso ao cliente.
   * Quem aplica na Stripe é o passe 8 do `dailyBillingTick`, perto de
   * `effective_at`. Redução não passa por aqui — vale já na próxima fatura.
   *
   * Local, como `pending_proration`: `reconcileItems` preserva.
   */
  scheduled_price: z
    .object({
      amount_cents: z.number().int().nonnegative(),
      effective_at: z.number().int().nonnegative(),
      notified_at: z.number().int().nonnegative(),
      requested_by_uid: UserIdSchema,
    })
    .nullable()
    .optional(),
})
  /**
   * **`origin` e os ids externos dizem a mesma coisa, ou o item não existe.**
   *
   * Toda a cadeia de reconciliação pergunta pelos IDS (`isGatewayItem`), porque
   * campo novo é ausente em todo documento já gravado e os ids existem desde
   * sempre. Isso só é seguro enquanto as duas leituras não puderem divergir —
   * e nada impedia gravar `origin: 'GRANT'` com um `si_*` preenchido, ou ids
   * nulos sem `origin`. No primeiro caso o laço de órfãos sepultaria o item
   * concedido; no segundo, um item local sem gateway seria reanexado para
   * sempre ao lado do verdadeiro, duplicando a cobrança no total.
   *
   * Em vez de escolher qual das duas leituras vence, a invariante proíbe a
   * divergência. `grant{}` entra junto porque um item concedido sem motivo
   * é um módulo que ninguém sabe explicar seis meses depois.
   */
  .superRefine((item, ctx) => {
    const hasIds = item.external_item_id !== null && item.external_price_id !== null;
    const noIds = item.external_item_id === null && item.external_price_id === null;
    if (item.origin !== SubscriptionItemOrigin.PARTNER && item.remit_cents !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: "remit_cents só existe em item com origin: 'PARTNER'",
        path: ['remit_cents'],
      });
    }
    if (item.origin === SubscriptionItemOrigin.PARTNER) {
      // Item de parceiro: sem gateway (não cobramos esse tenant) e com o
      // repasse declarado — sem ele o extrato do mês não tem o que somar.
      if (!noIds) {
        ctx.addIssue({
          code: 'custom',
          message: 'item de parceiro não tem id de gateway: external_item_id e external_price_id devem ser null',
          path: ['external_item_id'],
        });
      }
      if (item.remit_cents === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'item de parceiro exige remit_cents — o valor repassado a nós por ciclo',
          path: ['remit_cents'],
        });
      }
      if (item.grant !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: "grant{} só existe em item com origin: 'GRANT'",
          path: ['grant'],
        });
      }
      return;
    }
    if (item.origin === SubscriptionItemOrigin.IAP_BUNDLE) {
      // #942 (2026-10-12 13:20 +0200) — item do plano all-in-one da App Store:
      // a Apple cobra o plano inteiro, então não há item no NOSSO gateway (ids
      // nulos) nem concessão (sem grant{}). Sem este ramo o doc criado pelo
      // `provisionSelfServiceTenant` reprovava no schema ("item sem id de
      // gateway só é válido com GRANT ou PARTNER") — pego pelo teste da ativação.
      if (!noIds) {
        ctx.addIssue({
          code: 'custom',
          message: 'item da App Store não tem id de gateway: external_item_id e external_price_id devem ser null',
          path: ['external_item_id'],
        });
      }
      if (item.grant !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: "grant{} só existe em item com origin: 'GRANT'",
          path: ['grant'],
        });
      }
      return;
    }
    if (item.origin === SubscriptionItemOrigin.GRANT) {
      if (!noIds) {
        ctx.addIssue({
          code: 'custom',
          message: 'item concedido não tem id de gateway: external_item_id e external_price_id devem ser null',
          path: ['external_item_id'],
        });
      }
      if (item.grant === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'item concedido exige grant{} — quem concedeu, quando e por quê',
          path: ['grant'],
        });
      }
      return;
    }
    if (!hasIds) {
      ctx.addIssue({
        code: 'custom',
        message: "item sem id de gateway só é válido com origin: 'GRANT', 'PARTNER' ou 'IAP_BUNDLE'",
        path: ['origin'],
      });
    }
    if (item.grant !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: "grant{} só existe em item com origin: 'GRANT'",
        path: ['grant'],
      });
    }
  });
export type SubscriptionItem = z.infer<typeof SubscriptionItemSchema>;

/** #679 F7 — ver `SubscriptionSchema.courtesy`. */
export const CourtesySchema = z.object({
  category: z.enum(ALL_LIFETIME_FREE_CATEGORIES),
  reason: z.string().max(500).nullable(),
  applied_by: UserIdSchema.nullable(),
  applied_at: z.number().int().nonnegative(),
  legacy_external_subscription_id: z.string().min(1).nullable().optional(),
});
export type Courtesy = z.infer<typeof CourtesySchema>;

/**
 * Último evento `customer.subscription.*` da Stripe aplicado ao doc de
 * assinatura — a marca d'água que impede evento antigo de sobrescrever estado
 * novo.
 *
 * Origem: #795 (BIL-7) — 2026-10-03 14:40 +0200.
 *
 * Por quê: a Stripe não garante a ordem de entrega, e reentrega (retry depois
 * de um 500, re-dispatch do `sweepStuckWebhookEvents`) chega horas depois. Um
 * `customer.subscription.updated` `past_due` antigo aplicado depois do
 * `active` novo rebaixava o status e suspendia o tenant que já tinha pago.
 *
 * Contrato: `created_s` é o `event.created` da Stripe em **SEGUNDOS** epoch
 * (não ms — é o valor cru do evento, para comparar sem conversão).
 * `subscription_id` amarra a marca a UMA assinatura: o doc é singleton e
 * reutilizado a cada (re)assinatura, e eventos de outra `sub_` não se comparam
 * com esta marca. `event_id` é só rastro.
 *
 * Dados: `{ "subscription_id": "sub_000000000000", "event_id": "evt_000000000000", "created_s": 1790000000 }`
 *
 * Usado por: Functions — escrito e lido por `handleSubscriptionUpdated`
 * (também via `handleSubscriptionCreated`) e escrito por
 * `handleSubscriptionDeleted`. Nenhum cliente lê.
 */
export const LastSubscriptionEventSchema = z.object({
  subscription_id: nonEmpty,
  event_id: nonEmpty,
  created_s: z.number().int().nonnegative(),
});
export type LastSubscriptionEvent = z.infer<typeof LastSubscriptionEventSchema>;

/**
 * Espelho da assinatura da App Store em `tenants/{tid}/billing/subscription.appstore`.
 *
 * Origem: #942 — 2026-10-12 11:56 +0200 (IAP self-service no iOS).
 *
 * Por quê: o contrato congela `amount`/`currency` com o preço que a Apple
 * informou na compra (#970, 2026-10-15; antes, a constante 24900 USD da #942),
 * mas só em BRL/EUR/USD — a moeda local (MXN, JPY…) não cabe na união
 * `Currency` e cai no preço da zona do CRM (`storeReferencePrice`). O valor real fica aqui, cru, para auditoria e
 * relatório, sem ampliar `Currency`.
 *
 * Dados (montado de `JWSTransactionDecodedPayload`, App Store Server API v1):
 *   { "original_transaction_id": "2000000000000001",
 *     "product_id": "com.etiquetachef.app.allinone.monthly",
 *     "environment": "Sandbox",          // ou "Production"
 *     "storefront": "ITA",               // ISO alpha-3, como a Apple manda
 *     "price_milli": 79990,              // milésimos da moeda (Apple `price`)
 *     "currency": "EUR",                 // ISO 4217 do storefront
 *     "offer_type": "FREE_TRIAL",        // ou null fora da oferta
 *     "expires_at": 1791000000000,       // millis
 *     "auto_renew": true,
 *     "updated_at": 1790400000000 }
 *
 * Usado por: Functions `iap/*` (escreve: ativação, webhook, reconciliação);
 * web/Android/iOS Conta & Plano (leitura: "assinatura pela App Store").
 */
export const AppStoreSubscriptionInfoSchema = z.object({
  original_transaction_id: z.string().min(1).max(64),
  product_id: z.string().min(1).max(200),
  environment: z.enum(['Sandbox', 'Production']).catch('Production'),
  storefront: z.string().min(2).max(3).nullable().optional(),
  price_milli: z.number().int().nonnegative().nullable().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).nullable().optional(),
  offer_type: z.enum(['FREE_TRIAL', 'PAID']).nullable().optional().catch(null),
  expires_at: z.number().int().positive().nullable().optional(),
  auto_renew: z.boolean().nullable().optional(),
  updated_at: z.number().int().nonnegative(),
});
export type AppStoreSubscriptionInfo = z.infer<typeof AppStoreSubscriptionInfoSchema>;

/**
 * Espelho da assinatura do Google Play em `tenants/{tid}/billing/subscription.googleplay`.
 *
 * Origem: #969 — 2026-10-14 13:31 +0200 (self-service no Android).
 *
 * Por quê: mesmo motivo do `appstore` — o contrato congela `amount`/`currency`
 * na zona de preço (BRL/EUR/USD), mas o Google cobra o preço regional na moeda
 * local da conta Play. O valor real fica aqui, cru. O `purchase_token` é a
 * chave da Play Developer API (`purchases.subscriptionsv2.get`) — o
 * `external_subscription_id` (`gps_…`) é só o hash dele.
 *
 * Dados (montado de `SubscriptionPurchaseV2`, Android Publisher API v3):
 *   { "purchase_token": "<token opaco da Play>",
 *     "product_id": "allinone_monthly",
 *     "base_plan_id": "monthly",
 *     "latest_order_id": "GPA.0000-0000-0000-00000",
 *     "region_code": "IT",                 // alpha-2, país da conta Play
 *     "state": "SUBSCRIPTION_STATE_ACTIVE",
 *     "price_micros": 99000000,            // micros da moeda (Play `units`+`nanos`)
 *     "currency": "EUR",
 *     "offer_phase": "FREE_TRIAL",         // ou "BASE" / null
 *     "expires_at": 1791000000000,
 *     "auto_renew": true,
 *     "test_purchase": false,
 *     "updated_at": 1790400000000 }        // eventTime do último evento aplicado
 *
 * Usado por: Functions `play/*` (escreve: ativação, RTDN, reconciliação);
 * web/Android Conta & Plano (leitura: "assinatura pelo Google Play").
 */
export const GooglePlaySubscriptionInfoSchema = z.object({
  purchase_token: z.string().min(10).max(4096),
  product_id: z.string().min(1).max(200),
  base_plan_id: z.string().max(200).nullable().optional(),
  latest_order_id: z.string().max(200).nullable().optional(),
  region_code: z.string().regex(/^[A-Z]{2}$/).nullable().optional(),
  state: z.string().max(64),
  price_micros: z.number().int().nonnegative().nullable().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).nullable().optional(),
  offer_phase: z.enum(['FREE_TRIAL', 'BASE']).nullable().optional().catch(null),
  expires_at: z.number().int().positive().nullable().optional(),
  auto_renew: z.boolean().nullable().optional(),
  test_purchase: z.boolean().optional(),
  updated_at: z.number().int().nonnegative(),
});
export type GooglePlaySubscriptionInfo = z.infer<typeof GooglePlaySubscriptionInfoSchema>;

/**
 * Tamanho máximo do motivo de cancelamento da assinatura
 * (`billing/subscription.cancelled_reason`).
 *
 * Origem: #961 — 2026-10-13 17:35 +0200.
 *
 * Por quê: fonte única do limite. O `CancelSubscriptionAdminInput` aceitava
 * 500 caracteres contra 120 do doc, e o servidor cortava o texto em silêncio.
 * Agora o campo do admin para em 120 com contador, e o callable recusa acima.
 *
 * Usado por: `SubscriptionSchema.cancelled_reason`,
 * `CancelSubscriptionAdminInput.reason` (callable `cancelSubscriptionAdmin`) e
 * o campo do `CancelSubscriptionDialog` (`apps/admin`).
 */
export const SUBSCRIPTION_CANCEL_REASON_MAX = 120;

/**
 * Singleton `/tenants/{id}/billing/subscription`. Sprint 35 promoveu o
 * modelo single-item (`plan_id` + `amount` no topo) para multi-item
 * (`items[]` com 1 BASE + N add-ons opcionais). Os campos legacy
 * (`plan_id`, `amount`) permanecem `optional()` durante a janela de
 * migração — PR B `migrate-subscriptions-to-items.ts` preserva o valor
 * cobrado (pricing congelado, princípio 3 do CLAUDE.md), copia `amount`
 * para `total_amount_cents` e materializa `items[0] = { sku: 'BASE', ... }`.
 * Sprint pós-35 promove os campos novos para `required()` e remove os
 * legacy. Detalhes em `docs/sprint-35-pricing-platform-and-addons.md`.
 */
export const SubscriptionSchema = z.object({
  id: z.literal('subscription'),
  tenant_id: TenantIdSchema,
  /** Modo do Stripe: `true` = LIVE (dinheiro real), `false` = sandbox/test.
   * Opcional (registros antigos não têm; o webhook carimba pelo evento). Ver
   * `docs/STRIPE.md` §9. */
  livemode: z.boolean().optional(),
  /** LEGACY (Sprint 16). Sprint 35 prefere derivar do primeiro item de
   * `items[]`; mantido REQUIRED para não quebrar callers existentes
   * (ui-shared/SubscriptionCard, Android, dailyBillingTick). Sprint
   * pós-35 promove `items[]` para required e remove este campo. */
  plan_id: PlanIdSchema,
  status: SubscriptionStatusSchema,
  // Pricing congelado. `amount` em CENTS — alinhado com Invoice e
  // Payment. Se vier de PlanPrice (que é em unidades), converter no
  // caller via `priceUnitsToCents`.
  // #942 — qualquer ISO alpha-2 (assinatura IAP herda o país do storefront).
  country_code: TenantCountryCodeSchema,
  currency: CurrencySchema,
  /** LEGACY single-item amount em cents. Sprint 35 popula
   * `total_amount_cents` em paralelo (derivado de `items[]`); este
   * campo permanece REQUIRED enquanto callers (Android, UI, BI) leem
   * dele. Sprint pós-35 substitui pelos items[]. */
  amount: z.number().int().nonnegative(),
  billing_cycle: BillingCycleSchema,
  /**
   * Sprint 35 — items multi-SKU. `items[0]` é sempre BASE (obrigatório);
   * demais entradas são add-ons opcionais. Validação detalhada em
   * `SubscriptionItemSchema`. Optional durante migração — `migrate-
   * subscriptions-to-items.ts` popula com `[{ sku: 'BASE', ... }]`
   * preservando o pricing legacy.
   */
  items: z.array(SubscriptionItemSchema).min(1).optional(),
  /**
   * Sprint 35 — capabilities derivadas de `items[]` pelo reconciler de
   * webhook. Gate de feature no app (`subscription.capabilities.includes
   * ('SUPPLIER_INTEGRATION')` libera o módulo). Optional durante
   * migração — `migrate-subscriptions-to-items.ts` popula
   * `['BASE_ACTIVE']`.
   *
   * #395: campo DERIVADO, logo aberto como o catálogo de SKU
   * (`CapabilityKeySchema`) — ver a doc de lá.
   */
  capabilities: z.array(CapabilityKeySchema).optional(),
  /**
   * Sprint 35 — soma de `items[].amount_cents`. Pricing congelado: este
   * valor reflete o cobrado HOJE (não o catálogo atual), garantindo que
   * mudanças em `/config/planPrices/` não atinjam tenants existentes.
   * Optional durante migração — preenchido a partir de `amount` legacy.
   */
  total_amount_cents: z.number().int().nonnegative().optional(),
  /**
   * #743 (F8 da #679) — soma de `items[].remit_cents` dos itens de parceiro
   * ativos: o que o parceiro nos repassa por ciclo. Só em
   * `commercial_mode: PARTNER`; é a base do extrato mensal e do card de
   * repasse (o MRR não conta parceiro — `isDirectPaying`).
   */
  total_remit_cents: z.number().int().nonnegative().optional(),
  /**
   * #743 (9.18) — troca de tipo comercial PENDENTE, aplicada pelo webhook no
   * momento certo, nunca pelo callable:
   *
   * - `to: 'PARTNER'` (DIRECT com assinatura paga → parceiro): a assinatura
   *   foi agendada para cancelar no fim do período pago; o
   *   `customer.subscription.deleted` dela aplica o modo PARTNER com os itens
   *   daqui, em vez de cancelar o tenant.
   * - `to: 'DIRECT'` (parceiro → nós): o link de pagamento leva
   *   `mode_transition: PARTNER_TO_DIRECT` na metadata; o primeiro evento
   *   dessa assinatura troca o modo antes do guard `ignored_non_direct`.
   */
  partner_transition: z
    .object({
      to: z.enum([CommercialMode.PARTNER, CommercialMode.DIRECT]),
      partner_id: z.string().min(1).nullable(),
      billing_cycle: BillingCycleSchema,
      items: z.array(
        z.object({
          sku: SkuSchema,
          amount_cents: z.number().int().nonnegative(),
          remit_cents: z.number().int().nonnegative().nullable(),
        }),
      ),
      requested_by: z.string().min(1),
      requested_at: z.number().int().nonnegative(),
      reason: z.string().max(500),
    })
    .nullable()
    .optional()
    .catch(undefined),
  /** #743 — quando o tenant deixou de ser do parceiro (`former_partner_id`). O extrato para aqui. */
  partner_ended_at: z.number().int().nonnegative().nullable().optional(),
  former_partner_id: z.string().min(1).nullable().optional(),
  /**
   * #743 — os itens de parceiro como estavam ao sair dele, com `removed_at`
   * carimbado no fim do vínculo. É daqui que o extrato do mês da transição
   * conta o que o parceiro cobrou até ali: `items[]` não serve, porque a saída
   * tira (DIRECT) ou converte (COURTESY) os itens PARTNER, e o checkout do
   * DIRECT reescreve o array inteiro. Só o servidor lê; voltar a ser de
   * parceiro zera.
   */
  former_partner_items: z.array(SubscriptionItemSchema).nullable().optional(),
  /**
   * #679 §4.5 — tipo comercial do tenant (`CommercialMode`). Ausente =
   * `DIRECT`, resolvido por `commercialModeOf` — nunca por query (C5).
   *
   * `.catch('DIRECT')`: um modo que este bundle não conhece não pode derrubar
   * o parse — no web isso vira ERROR e prende o usuário em "Carregando…"
   * (`firestoreBilling.ts`). Degradar para `DIRECT` é o lado fechado: o pior
   * caso é o gate de pagamento pedir pagamento, nunca liberar sem ele.
   */
  commercial_mode: CommercialModeSchema.optional().catch(CommercialMode.DIRECT),
  /**
   * #679 §4.2 — origem do preço dos itens (`PricingSource`). Descritivo;
   * `.catch(undefined)` pelo mesmo motivo do campo acima.
   */
  pricing_source: PricingSourceSchema.optional().catch(undefined),
  /**
   * #965 — quem paga esta assinatura, quando não é o próprio tenant. Ausente
   * = o tenant paga (todo doc anterior à #965 e toda venda que não é de
   * parceiro `RESELLER`).
   *
   * `{ kind: 'PARTNER', partner_id }`: o parceiro `RESELLER` comprou a
   * licença deste tenant e é o PAGADOR — o cliente do gateway leva os dados
   * fiscais dele (a NFS-e sai em nome dele), e os e-mails de cobrança e de
   * inadimplência vão para o `billingEmail` dele (`resolveBillingRecipient`).
   * O tenant segue `commercial_mode: DIRECT` e passa pelo gate como qualquer
   * venda direta.
   *
   * Congelado na venda (`provisionTenantForSale`): trocar o modelo do parceiro
   * depois não muda quem paga um cliente já vendido.
   *
   * `.catch(undefined)`: um formato desconhecido lê como "o tenant paga" —
   * o pior caso é um e-mail de cobrança ir ao OWNER, nunca liberar sem pagar.
   *
   * Dados: `{ "kind": "PARTNER", "partner_id": "partner-1" }`.
   */
  bill_to: z
    .object({
      kind: z.literal('PARTNER'),
      partner_id: z.string().min(1).max(128),
    })
    .optional()
    .catch(undefined),
  /**
   * #748 — meio de pagamento que o GATEWAY devolveu ao criar a assinatura, não
   * o pedido: no spike A0 uma assinatura pedida como PIX voltou BOLETO sem
   * erro. No Asaas, `pix` é o Pix Automático.
   *
   * `.catch(undefined)`: o campo é informativo (tela de cobrança, alerta de
   * divergência); um meio que este bundle não conhece não pode derrubar a
   * leitura da assinatura inteira, que é o paywall.
   *
   * Origem: #750 — 2026-09-27 12:00 +0200 (A1). Usado por: o webhook do Asaas
   * (grava o `billingType` devolvido, `asaasHandlers.ts`); o contrato gerado
   * do `Subscription` (snapshot copiado para o iOS); o Android não lê o campo.
   */
  payment_method: PaymentMethodTypeSchema.nullable().optional().catch(undefined),
  /**
   * #748 G4 — anual parcelado: 1 = à vista (assinatura anual nativa), 2–12 =
   * cobrança parcelada no cartão, renovada pelo sistema a cada ano com o
   * cartão tokenizado. Ausente = não é anual parcelado.
   *
   * Origem: #750 — 2026-09-27 12:00 +0200 (A1). Gravado pelo webhook do 1º
   * pagamento do parcelado (`bindFirstAsaasPayment`, #755) e mantido pela
   * renovação anual (`asaas/installmentRenewal.ts`, #755).
   */
  annual_installments: z.number().int().min(1).max(12).nullable().optional(),
  /**
   * #748 — id do parcelamento vigente no Asaas (UUID) quando `annual_installments ≥ 2`.
   * UUID, e não `sub_…`, por isso `GatewayObjectIdSchema`.
   *
   * É o que libera o paywall do parcelado (`hasGatewayEntitlement`, #755): o
   * parcelamento não tem assinatura no gateway, então `external_subscription_id`
   * fica nulo enquanto este campo existir. A renovação troca o id pelo do
   * parcelamento do ano seguinte, ou o apaga ao passar para assinatura (troca
   * de ciclo agendada), gravando o `sub_…` no mesmo write.
   */
  installment_plan_id: GatewayObjectIdSchema.nullable().optional(),
  /**
   * Estado da renovação anual do parcelado (#755, A6). `null`/ausente = nada
   * em curso. Gravado pelo passe de renovação do `dailyBillingTick`
   * (`asaas/installmentRenewal.ts`) e limpo quando a renovação conclui.
   *
   * Origem: #755 — 2026-09-28 09:52 +0200.
   *
   * Contrato:
   *  - `period_end`: o `current_period_end` que esta renovação cobre. É a
   *    chave de idempotência: vai no `externalReference`
   *    (`renewal:<tenantId>:<period_end>`) do parcelamento novo, e o passe
   *    consulta o Asaas por ele antes de cobrar de novo.
   *  - `status`: `IN_FLIGHT` enquanto a cobrança está sendo criada (uma
   *    rodada que morra no meio deixa o rastro para a seguinte conferir);
   *    `FAILED` quando o Asaas recusou (400 sem cobrança criada — medido na
   *    A6, `docs/billing/asaas.md` c1/c2).
   *  - `recovery_attempt_id`: a tentativa `/pay` criada para o cliente pagar
   *    com outro cartão depois da recusa.
   *
   * Dados: `{ "period_end": 1790581643340, "status": "FAILED",
   *   "attempted_at": 1790581650000, "error_code": "invalid_object",
   *   "recovery_attempt_id": "00000000-0000-4000-8000-000000000000" }`
   */
  installment_renewal: z
    .object({
      period_end: z.number().int().nonnegative(),
      status: z.enum(['IN_FLIGHT', 'FAILED']),
      attempted_at: z.number().int().nonnegative(),
      error_code: z.string().max(120).nullable(),
      recovery_attempt_id: z.string().min(1).nullable(),
    })
    .nullable()
    .optional()
    .catch(undefined),
  /**
   * Troca de cartão em curso no Asaas (#755, A6). O Asaas não tem portal e o
   * `PUT /subscriptions/{id}/creditCard` exige o cartão cru (escopo PCI), então
   * a troca é por um checkout hospedado que cria uma assinatura NOVA; quando
   * o 1º pagamento dela confirma, o webhook encerra a antiga e troca o
   * `external_subscription_id` (`asaas/cardChange.ts`).
   *
   * Origem: #755 — 2026-09-28 09:52 +0200.
   *
   * Contrato: `kind: PROACTIVE` (assinatura em dia — a nova é prorrogada
   * pelos dias que restavam) ou `DUNNING` (vencida/suspensa — sem
   * prorrogação, a cobrança vencida é apagada com a assinatura antiga).
   * `attempt_id` é a `checkoutAttempts/{uuid}` que carrega o checkout.
   * `.catch(undefined)`: descritivo, não pode derrubar o parse do paywall.
   */
  pending_card_change: z
    .object({
      attempt_id: z.string().min(1),
      kind: z.enum(['PROACTIVE', 'DUNNING']),
      created_at: z.number().int().nonnegative(),
      expires_at: z.number().int().nonnegative(),
    })
    .nullable()
    .optional()
    .catch(undefined),
  /**
   * Bandeira e 4 últimos dígitos do cartão que o gateway cobra (#755, A6),
   * para a tela de meios de pagamento. Nunca o token — esse vive em
   * `/billingSecrets/{tenantId}`, que membro nenhum lê.
   * `.catch(undefined)`: informativo.
   */
  payment_card: z
    .object({ brand: z.string().max(40), last4: z.string().regex(/^\d{4}$/) })
    .nullable()
    .optional()
    .catch(undefined),
  /**
   * #748 G6 — o fiscal acompanha o gateway da venda, não o país: gravado na
   * venda. Ausente = legado, vale o do mercado.
   *
   * Origem: #750 — 2026-09-27 12:00 +0200 (A1). Gravado `ASAAS_NFSE` pelo link
   * do Asaas (`saleLink.ts`, #752). Risco aberto: #752, A3, B-L3 — fica no doc
   * se o link seguinte trocar para o Stripe (⏭️ A5, #754).
   */
  // `z.enum` inline: `FiscalProviderSchema` é declarado mais abaixo (TDZ).
  fiscal_provider: z.enum(ALL_FISCAL_PROVIDERS).nullable().optional().catch(undefined),
  /**
   * #679 F7 (#738) — dados da cortesia quando `commercial_mode: COURTESY` (ou
   * DEMO migrado do cupom). Substitui os `discount.lifetime_free_*`: a
   * cortesia deixa de ser um cupom de 100 % numa assinatura Stripe e passa a
   * ser "não cobramos".
   *
   * `legacy_external_subscription_id`: o `sub_…` da assinatura com cupom que
   * a etapa E3 cancelou. O id continua também em `external_subscription_id`
   * até a E4, porque é o sinal que o gate dos apps antigos lê; este campo é o
   * que permite o rollback (`restoreLegacyCourtesySubscription`) e a E4.
   *
   * `.catch(undefined)`: é descritivo — um formato que este bundle não
   * conhece não pode derrubar o parse do doc de billing.
   */
  courtesy: CourtesySchema.nullable().optional().catch(undefined),
  /**
   * Último checkout concluído neste tenant, espelhado pelo webhook
   * `checkout.session.completed` a partir da PRÓPRIA sessão do Stripe.
   *
   * Existe para o Pixel web e a Meta CAPI reportarem o MESMO número sob o
   * mesmo `event_id`: o cliente não enxerga `session.amount_total` e vinha
   * mandando `amount` (preço de tabela), enquanto o servidor mandava o valor
   * efetivamente pago — num checkout com cupom os dois brigavam. Aqui o valor
   * é o pago, com desconto já aplicado; `session_id` é o que permite ao
   * cliente saber que o valor é DESTE checkout e não de um anterior.
   *
   * Não é fonte da verdade de pricing (isso é `items[]`/`total_amount_cents`)
   * — é o recibo do último pagamento de checkout, para telemetria. Optional:
   * subs criadas antes desta mudança não têm.
   */
  last_checkout: z
    .object({
      session_id: nonEmpty,
      amount_cents: z.number().int().nonnegative(),
      currency: CurrencySchema,
    })
    .optional(),
  /**
   * #795 (BIL-7) — marca d'água de ordem dos eventos de assinatura da Stripe;
   * ver `LastSubscriptionEventSchema`. Só servidor escreve e lê.
   *
   * `.catch(undefined)`: é controle interno do webhook — um formato que este
   * bundle não conhece não pode derrubar o parse do doc de billing nos
   * clientes (o mesmo critério de `courtesy`). Ausente em todo doc anterior a
   * 2026-10-03: o primeiro evento de assinatura depois do deploy carimba.
   */
  last_subscription_event: LastSubscriptionEventSchema.nullable().optional().catch(undefined),
  /**
   * Sprint 35 — FK para `/config/sellerEntities/{id}`. Define qual PJ
   * própria está emitindo a fatura desta subscription. Congelado na
   * criação (mudança de seller exige cancel + re-subscribe). MVP: única
   * seller é `etiquetachef-srl` (P.IVA italiana). Optional durante
   * migração — `migrate-subscriptions-to-items.ts` popula
   * `etiquetachef-srl`.
   */
  seller_entity_id: nonEmpty.optional(),
  // Ciclo vigente
  started_at: z.number().int().nonnegative(),
  /**
   * #679 — o instante da PRIMEIRA ativação paga (primeiro checkout concluído),
   * gravado pelo `handleCheckoutCompleted` e nunca reescrito. É a data do new
   * MRR do `aggregateDailyMetrics`: `created_at` é o dia do provisionamento, e
   * na venda assistida o link é pago dias depois. Ausente nas assinaturas
   * pagas antes do campo (o new MRR cai em `created_at` para elas) e nas que
   * ainda não pagaram.
   */
  first_paid_at: z.number().int().nonnegative().nullable().optional(),
  current_period_start: z.number().int().nonnegative(),
  current_period_end: z.number().int().nonnegative(),
  next_invoice_at: z.number().int().nonnegative().nullable(),
  cancelled_at: z.number().int().nonnegative().nullable(),
  /**
   * Sprint 19 — motivo do cancelamento. Valores típicos:
   * `tenant_deleted` (helper `cancelSubscription` chamado por
   * `requestTenantDeletion`), `support_request`, ou `gateway_*`
   * quando Sprint 5 plugar Stripe.
   */
  cancelled_reason: z.string().max(SUBSCRIPTION_CANCEL_REASON_MAX).optional(),
  /**
   * Sprint 19 — UID de quem disparou o cancelamento (super-admin via
   * callable, ou `__system__` quando Stripe webhook responder).
   */
  cancelled_by: z.string().optional(),
  suspended_at: z.number().int().nonnegative().nullable(),
  reactivated_at: z.number().int().nonnegative().nullable(),
  /**
   * Prorrogação manual do vencimento (super-admin via `postponeRenewalAdmin`):
   * empurra o `current_period_end` no PSP sem cobrar o intervalo (Stripe
   * `trial_end`). `renewal_postponed_from` guarda o vencimento ANTERIOR (millis)
   * — o admin vê quanto foi prorrogado comparando com o `current_period_end`
   * novo; `renewal_postponed_at` é quando a ação rodou. Optional — maioria das
   * subs nunca foi prorrogada.
   */
  renewal_postponed_at: z.number().int().nonnegative().optional(),
  renewal_postponed_from: z.number().int().nonnegative().optional(),
  // Mudança de ciclo agendada
  pending_cycle_change: BillingCycleSchema.nullable(),
  pending_cycle_change_requested_at: z.number().int().nonnegative().nullable(),
  pending_cycle_change_effective_at: z.number().int().nonnegative().nullable(),
  /**
   * Preço de cada item no ciclo agendado em `pending_cycle_change` (#737). Em
   * venda negociada o consultor informa o valor do ciclo novo item a item —
   * piso é por ciclo, e o mensal × 12 não é o anual. Só em
   * `pricing_source: 'CRM_SALE'`; o caminho de catálogo nunca o gravou.
   */
  pending_cycle_change_items: z
    .array(z.object({ sku: SkuSchema, amount_cents: z.number().int().nonnegative() }))
    .nullable()
    .optional(),
  /**
   * Quantas vezes a troca de ciclo agendada do Asaas foi ADIADA para o período
   * seguinte (#795, BIL-9 — 2026-10-03 18:40 +0200). Escrito só pelo passe 10
   * do `dailyBillingTick` (`applyAsaasCycleChange`): cada vez que a data-alvo
   * anda para frente (cliente que paga adiantado, passe que perdeu a janela),
   * `count` sobe; a partir de 2 vira `logger.error` + `BILLING_ANOMALY`, porque
   * um cliente que paga sempre adiantado adiaria a troca para sempre.
   * `requested_at` é o `pending_cycle_change_requested_at` do pedido contado —
   * pedido novo zera a contagem sem depender de quem o grava. Ausente/null =
   * nunca observado. Limpo (null) quando a troca é aplicada.
   */
  pending_cycle_change_postponement: z
    .object({
      requested_at: z.number().int().nonnegative().nullable(),
      last_target: z.number().int().nonnegative(),
      count: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  /**
   * Teto do mandato Pix Automático autorizado, em cents (#737). Não vinha
   * persistido: a F1 calculava `total × PIX_MANDATE_BUFFER` e mandava à
   * Stripe sem guardar. Um módulo novo ou reajuste que faça o total passar do
   * teto exige mandato novo, e a decisão precisa do valor AUTORIZADO, não de
   * uma estimativa pelo total de hoje. Ausente = desconhecido → quem precisa
   * lê o mandato na Stripe. Só existe em assinatura com Pix.
   */
  pix_mandate_amount_cents: z.number().int().positive().nullable().optional(),
  /**
   * Teto do mandato Pix do CICLO NOVO, autorizado para uma troca de ciclo
   * agendada (#737). Não vai para `pix_mandate_amount_cents` na autorização:
   * até a virada quem paga é o mandato antigo, e desfazer a troca não pode
   * deixar gravado um teto de outro `payment_schedule`. O webhook o promove
   * quando a troca vigora.
   */
  pending_cycle_change_mandate_cents: z.number().int().positive().nullable().optional(),
  /**
   * Checkout `mode: 'setup'` aberto para autorizar um mandato Pix novo (#737):
   * troca de ciclo muda o `payment_schedule`, e reajuste acima do teto muda o
   * valor — nos dois casos a mudança só é aplicada quando o setup conclui.
   * `ADDON`: módulo novo sem proporção a cobrar (fim de ciclo) que passou do
   * teto — o módulo já vale, e o mandato novo precisa estar autorizado antes
   * da renovação, que cobra o total com ele.
   */
  pending_mandate_setup: z
    .object({
      checkout_session_id: nonEmpty,
      reason: z.enum(['CYCLE_CHANGE', 'PRICE_INCREASE', 'ADDON']),
      mandate_amount_cents: z.number().int().positive(),
      created_at: z.number().int().nonnegative(),
      expires_at: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  /**
   * Sprint 35 — desconto/cupom/isenção aplicado à subscription. Stripe-
   * native (`subscription.discount.*` espelhado pelos webhooks
   * `customer.discount.created/deleted`). `is_lifetime_free: true`
   * marca isenção via cupom mestre `LIFETIME_FREE` (`percent_off: 100,
   * duration: 'forever'`) — UI omite paywall e opções de mudança quando
   * detecta. Guard 3 limita a 1 cupom por subscription. Optional —
   * maioria das subs não tem desconto.
   */
  discount: z
    .object({
      external_coupon_id: nonEmpty,
      external_promotion_code_id: nonEmpty.nullable(),
      percent_off: z.number().min(0).max(100).nullable(),
      amount_off_cents: z.number().int().nonnegative().nullable(),
      duration: z.enum(ALL_COUPON_DURATIONS),
      duration_in_months: z.number().int().positive().nullable(),
      started_at: z.number().int().positive(),
      end_at: z.number().int().positive().nullable(),
      // Específico de isenção lifetime free
      is_lifetime_free: z.boolean(),
      lifetime_free_category: z.enum(ALL_LIFETIME_FREE_CATEGORIES).nullable(),
      lifetime_free_reason: z.string().max(500).nullable(),
      lifetime_free_applied_by: UserIdSchema.nullable(),
      lifetime_free_applied_at: z.number().int().positive().nullable(),
    })
    .nullable()
    .optional(),
  // Pagamento
  default_payment_method_id: nonEmpty.nullable(),
  /**
   * Sprint 5 — IDs no gateway externo (Stripe `cus_*`/`sub_*`).
   * Populados pelo webhook `customer.subscription.created`/
   * `customer.created`. Permitem cancelar no provider quando o
   * callable de cancelamento self-service roda. Opcionais —
   * assinaturas legadas ou em super-admin manual não têm.
   * B-19: regex `<prefix>_<base62>` (Stripe).
   */
  // #95 — `.nullable()` além de `.optional()`: um doc com `null` literal
  // (não ausente) NÃO pode quebrar o parse do doc inteiro. Antes, `null`
  // derrubava o `safeParse` → o cliente caía em `ERROR` e o `PaymentGate`
  // (web) fazia fail-open, liberando a home sem pagar. Com `.nullable()`,
  // `null` parseia como "sem ID externo" (== unpaid) e o gate barra
  // corretamente. Consumidores checam `!= null` (cobre `undefined` e `null`).
  external_provider: PaymentProviderSchema.nullable().optional(),
  external_customer_id: externalProviderId.nullable().optional(),
  external_subscription_id: externalProviderId.nullable().optional(),
  /**
   * #942 — fim do trial grátis (millis). Presente só enquanto o período
   * corrente é a oferta introdutória da App Store; o webhook de renovação
   * (`DID_RENEW`) o limpa para `null` quando a Apple cobra a primeira vez. Não
   * existe status `TRIALING`: o trial é `ACTIVE` + este campo.
   * #969 — o mesmo para o Google Play (RTDN `SUBSCRIPTION_RENEWED`) e para a
   * Stripe self-service (`trial_end` da assinatura; o `invoice.paid` da 1ª
   * cobrança o limpa).
   */
  trial_ends_at: z.number().int().positive().nullable().optional(),
  /** #942 — espelho da assinatura na App Store; só em `external_provider: 'appstore'`. */
  appstore: AppStoreSubscriptionInfoSchema.nullable().optional(),
  /** #969 — espelho da assinatura no Google Play; só em `external_provider: 'googleplay'`. */
  googleplay: GooglePlaySubscriptionInfoSchema.nullable().optional(),
  // Metadata
  created_at: z.number().int().nonnegative(),
  updated_at: z.number().int().nonnegative(),
  updated_by: UserIdSchema.nullable(),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

/**
 * Segredos de cobrança do tenant, em `/billingSecrets/{tenantId}` (coleção
 * RAIZ, sem leitura nem escrita de cliente — nem super-admin, pela rule).
 *
 * Origem: #755 — 2026-09-28 09:52 +0200 (A6: renovação do anual parcelado).
 *
 * Por quê: a renovação do anual parcelado cobra o ano seguinte só com o
 * `creditCardToken` do Asaas (spike A0 item 7, A6 c3). O token é credencial de
 * cobrança: fora de `billing/**`, que qualquer membro do tenant lê.
 *
 * Contrato: escrito pelo webhook do Asaas quando uma cobrança de cartão
 * confirma com token (`asaasHandlers.ts`), lido pela renovação
 * (`asaas/installmentRenewal.ts`). `asaas_card_token` é UUID.
 *
 * Dados: `{ "tenant_id": "tenant-1",
 *   "asaas_card_token": "00000000-0000-4000-8000-000000000000",
 *   "source_payment_id": "pay_000000000000", "updated_at": 1790581643340 }`
 *
 * Usado por: Functions (webhook e renovação). Nenhum cliente.
 */
export const BillingSecretsSchema = z.object({
  tenant_id: nonEmpty,
  asaas_card_token: z.string().min(1).nullable(),
  source_payment_id: nonEmpty.nullable(),
  /** Quando o pagamento que trouxe o token foi feito — só um mais novo o substitui (#755). */
  card_paid_at: z.number().int().nonnegative().optional(),
  updated_at: z.number().int().nonnegative(),
});
export type BillingSecrets = z.infer<typeof BillingSecretsSchema>;

export const CheckoutAttemptStatusSchema = z.enum(ALL_CHECKOUT_ATTEMPT_STATUSES);

/**
 * #289 — tentativa de checkout, em `/checkoutAttempts/{sessionId}` (coleção
 * RAIZ, escrita só pelo Admin SDK, leitura só super-admin).
 *
 * Por que não vive em `billing/subscription`: o `createCheckoutSession` (o
 * callable do autosserviço, que saiu do servidor na #714) registrava
 * explicitamente que reservar estado `pending` naquele doc colidiria com o
 * princípio "tenant nasce sem subscription". Uma tentativa não paga não é uma
 * assinatura, e não pode parecer uma para o `PaymentGate`.
 *
 * Por que existe: até 2026-08 uma Checkout Session abandonada não deixava
 * rastro nenhum — sem invoice, o `invoice.payment_failed` nunca dispara, e a
 * sessão morria em 24h. Dois dos três primeiros checkouts de produção foram
 * perdidos em silêncio.
 *
 * Campos opcionais são `.nullable().optional()` pela lição do #95: um `null`
 * literal não pode derrubar o parse do doc inteiro.
 *
 * Venda pelo Asaas (#752, A3): a tentativa É o link — `saleLink.ts` a cria
 * (`/checkoutAttempts/{uuid}`, 7 dias) antes de qualquer objeto no gateway;
 * `choosePayOption` grava o objeto da escolha; `bindFirstAsaasPayment` a
 * fecha (`COMPLETED`); o passe 9 do `dailyBillingTick` a vence.
 *
 * Dados — tentativa do Asaas com boleto escolhido (anonimizado; cents, epoch ms):
 *   {
 *     "id": "00000000-0000-4000-8000-000000000000", "provider": "asaas",
 *     "tenant_id": "tenant-1", "livemode": false, "status": "OPEN",
 *     "url": "https://app.etiquetachef.com/pt/pay/00000000-0000-4000-8000-000000000000",
 *     "amount_cents": 24890, "currency": "BRL", "billing_cycle": "quarterly",
 *     "payment_method_types": ["card", "pix", "boleto"], "pay_lang": "pt",
 *     "payment_option": { "method": "boleto", "installments": 1 },
 *     "gateway_object_id": "sub_000000000000",
 *     "gateway_object_ids": ["00000000-0000-4000-8000-000000000001", "sub_000000000000"],
 *     "gateway_url": "https://sandbox.asaas.com/i/000000000000",
 *     "gateway_expires_at": 1790290800000, "payment_method_created": "boleto",
 *     "choice_lock_until": null, "choice_lock_owner": null, "pix_qr": null,
 *     "expires_at": 1790600000000
 *   }
 * Armadilha: no Asaas `id` NÃO é id do gateway; o do gateway é `gateway_object_id`.
 *
 * Histórico (campos do Asaas):
 *  - 2026-09-27 12:00 +0200 — (#750) `id` aceita UUID; `provider`; `payment_option`.
 *  - 2026-09-27 14:28 +0200 — (#752) `gateway_object_id`, `gateway_url`,
 *    `gateway_expires_at`, `payment_method_created`, `pay_lang`.
 *  - 2026-09-27 14:54 +0200 — (#752) `choice_lock_until`, `pix_qr`.
 *  - 2026-09-27 15:48 +0200 — (#752, auditoria A3) `gateway_object_ids`,
 *    `choice_lock_owner`, `close_kept_paid_at`.
 */
export const CheckoutAttemptSchema = z.object({
  /**
   * Stripe Checkout Session id (`cs_live_*` / `cs_test_*`) ou, no Asaas, o id
   * do objeto que o cliente paga (checkout/parcelamento: UUID). #748.
   *
   * Na venda pelo Asaas da A3 (#752) o id é o NOSSO UUID v4
   * (`crypto.randomUUID`, gerado em `saleLink.ts`): é o `{attemptId}` da
   * página `/{lang}/pay/{attemptId}`, e existe antes de qualquer objeto no
   * Asaas. O objeto do gateway vai em `gateway_object_id`.
   */
  id: GatewayObjectIdSchema,
  /**
   * #748 — gateway da tentativa. `.default('stripe')`: todo doc anterior à
   * #748 nasceu no Stripe. É o primeiro filtro da página pública: tentativa
   * que não é `asaas` responde `UNAVAILABLE` (`readPayAttemptView`).
   */
  provider: PaymentProviderSchema.default('stripe'),
  /**
   * #748 — o que o cliente escolheu na página `/pay`: meio e, no anual, o
   * número de parcelas (1 = à vista). Ausente = Stripe (o Checkout escolhe).
   * `installments` ≥ 2 só no anual + cartão (#755, A6): o servidor recusa o
   * resto em `choosePayOption` (`installments_not_offered`).
   */
  payment_option: z
    .object({
      method: PaymentMethodTypeSchema,
      installments: z.number().int().min(1).max(12),
    })
    .nullable()
    .optional(),
  /**
   * #752 — Asaas: o objeto criado para a opção escolhida em `/pay`. UUID do
   * checkout (cartão) ou da autorização de Pix Automático, `sub_…` da
   * assinatura de boleto. É por ele que o webhook acha o tenant do cartão: a
   * assinatura criada pelo checkout leva `checkoutSession` = este id, e o
   * cliente dela só existe depois do pagamento (spike A3, `asaas.md`).
   * Gravado ANTES de a URL chegar ao cliente.
   */
  gateway_object_id: GatewayObjectIdSchema.nullable().optional(),
  /**
   * #752 (auditoria) — TODOS os objetos do gateway que esta tentativa já criou,
   * inclusive os que o cliente trocou depois. O `gateway_object_id` é só o
   * atual: um cartão pago antes de o cliente voltar e escolher outro meio tem
   * de continuar achando a tentativa pelo `checkoutSession` (`array-contains`).
   */
  // Auditoria 2026-09-27 15:48 +0200 (#752, A3, B-H1/W-H1): o cliente pagava o
  // checkout do cartão, voltava à `/pay` e escolhia boleto antes do webhook;
  // o `gateway_object_id` era sobrescrito e o pagamento do cartão virava
  // `ignored_unbound`, cobrando todo ciclo sem dono. Causa: só o objeto ATUAL
  // era gravado. Não desfazer o histórico nem a busca `array-contains`
  // (`findAsaasAttemptByGatewayObject`).
  // Teto 40 = o dobro do teto de 20 escolhas por tentativa (`MAX_CHOICES`),
  // com folga para retomadas e ids legados; quem grava deduplica e guarda os
  // 40 mais novos, então o doc não cresce sem limite.
  gateway_object_ids: z.array(GatewayObjectIdSchema).max(40).nullable().optional(),
  /**
   * #752 (auditoria) — o dono da trava de escolha: só quem a tomou a solta.
   * Sem dono, uma escolha lenta que perdeu a trava por prazo soltaria a trava
   * de OUTRA escolha em curso, e duas criariam objetos no Asaas ao mesmo tempo.
   */
  // Auditoria 2026-09-27 15:48 +0200 (#752, A3, B-M1): a trava tinha 45 s,
  // menos que a pior escolha, e era solta sem conferir o dono. Causa: trava
  // sem identidade. Não tirar o dono nem encurtar `CHOICE_LOCK_MS` (150 s)
  // abaixo do timeout da callable (120 s).
  choice_lock_owner: z.string().max(64).nullable().optional(),
  /**
   * #752 (auditoria) — o fechamento da tentativa encontrou um pagamento em
   * andamento (`kept_paid`) e deixou o objeto no Asaas: o webhook é quem fecha.
   * O passe de expiração não a revisita.
   */
  // Auditoria 2026-09-27 15:48 +0200 (#752, A3, B-M8/W-M4): o fechamento que
  // falhava não era revisto, e `kept_paid`/objeto inexistente voltavam ao
  // passe 9 todo dia. Não desfazer: o passe voltaria a reprocessar sem fim.
  close_kept_paid_at: z.number().int().nonnegative().nullable().optional(),
  /** #752 — para onde `/pay` manda o cliente (checkout, `invoiceUrl`). Pix não tem. */
  gateway_url: z.string().nullable().optional(),
  /**
   * #752 — até quando o objeto do gateway vale (o checkout expira em ≤ 24 h).
   * No boleto é o FIM do dia do vencimento (BRT), e a tentativa só vence
   * depois dele + 5 dias de compensação.
   */
  // Auditoria 2026-09-27 15:48 +0200 (#752, A3, B-H3/W-M3/F-M4): boleto pago e
  // ainda não compensado era tratado como não pago, e o escolhido no 5º–7º dia
  // vencia depois do link — o passe 9 e o fechamento o apagavam. Não voltar a
  // vencer o boleto pelo `expires_at` do link.
  gateway_expires_at: z.number().int().nonnegative().nullable().optional(),
  /**
   * #752 — o meio que o gateway DE FATO criou (spike A0: uma assinatura pedida
   * em PIX voltou BOLETO). Gravado pela escolha e comparado com o pedido: a
   * divergência sai no log `asaasPay_method_diverged` (`payOption.ts`). A
   * página NÃO lê este campo — ela segue o passo devolvido pela escolha
   * (#752, A3, F-L8, 2026-09-27 15:49 +0200: o texto antigo dizia que a
   * página o mostrava).
   */
  payment_method_created: PaymentMethodTypeSchema.nullable().optional(),
  /**
   * #752 — idioma das URLs públicas (`/{lang}/pay/…`), derivado do tenant
   * (`payLangFromTenantLocale`) na geração do link. Congelado: as URLs de
   * retorno do checkout criadas depois usam este, mesmo que o tenant mude de
   * idioma no meio.
   */
  pay_lang: PayLangSchema.nullable().optional(),
  /**
   * #752 — trava curta da escolha em `/pay` (epoch ms). Criar o objeto no
   * Asaas é uma chamada externa, fora de transação; dois cliques simultâneos
   * criariam duas cobranças. Quem chega com a trava viva recebe "tente de
   * novo" em vez de criar a segunda (`choice_in_progress`). A trava dura mais
   * que o timeout da callable (`CHOICE_LOCK_MS` > 120 s, conferido no boot de
   * `payCallables.ts`), para não expirar com a escolha ainda em voo.
   */
  choice_lock_until: z.number().int().nonnegative().nullable().optional(),
  /**
   * #752 — o QR do Pix Automático criado para esta tentativa. Guardado para a
   * página retomar (recarregar, consultar o status) sem pedir de novo ao
   * Asaas a cada poll. Só vale até `gateway_expires_at`.
   */
  pix_qr: z
    .object({ payload: z.string().min(1), encoded_image: z.string().min(1) })
    .nullable()
    .optional(),
  tenant_id: TenantIdSchema,
  livemode: z.boolean(),
  status: CheckoutAttemptStatusSchema,
  /**
   * `session.url` do Stripe, VERBATIM. Nunca reconstruir a partir do id: a URL
   * carrega um fragmento `#fid...` obrigatório e sem ele o link não abre.
   */
  url: z.string().nullable().optional(),
  /**
   * `after_expiration.recovery.url` — só existe depois que a sessão expira,
   * vale 30 dias e gera uma cópia da sessão abandonada.
   */
  recovery_url: z.string().nullable().optional(),
  recovery_expires_at: z.number().int().nonnegative().nullable().optional(),
  /**
   * `session.recovered_from` — id da sessão que gerou esta pelo
   * `recovery_url`. Preenchido SÓ nesse caminho, e é o que distingue
   * "nasceu fora do nosso callable, legitimamente sem fase `OPEN`" de
   * "o write de criação falhou em silêncio" (#313). As duas hipóteses
   * deixam a mesma marca — `billing_cycle` ausente — e sem este campo a
   * diferença só se resolve consultando a Stripe à mão.
   */
  recovered_from: z.string().nullable().optional(),
  amount_cents: z.number().int().nonnegative().nullable().optional(),
  currency: CurrencySchema.nullable().optional(),
  billing_cycle: BillingCycleSchema.nullable().optional(),
  payment_method_types: z.array(z.string()).nullable().optional(),
  /**
   * #679 §4.2 — quem gerou a sessão. `.default('CLIENT')` (C9): os documentos
   * anteriores não têm o campo e todos nasceram do self-service ou do admin —
   * `CLIENT` é o default de leitura porque o admin é minoria e o campo só
   * separa a venda assistida do resto.
   */
  origin: CheckoutOriginSchema.default('CLIENT'),
  /**
   * #679 §4.2 — composição cobrada nesta sessão, em cents inteiros desde a
   * origem. Só a venda assistida grava; o self-service cobra o BASE de lista.
   */
  items: z
    .array(z.object({ sku: SkuSchema, amount_cents: z.number().int().nonnegative() }))
    .nullable()
    .optional(),
  /**
   * #679 §4.3-3 — hash de `(itens, preços, ciclo)`. Entra no reuso da sessão
   * aberta e na chave de idempotência: sem ele, o segundo link de uma venda
   * renegociada devolveria a sessão antiga, com os valores antigos.
   */
  items_hash: z.string().max(128).nullable().optional(),
  /** #679 §4.2 — cópia de `tenant.sales_origin` no momento do link. */
  sales_origin: TenantSalesOriginSchema.nullable().optional(),
  /**
   * #679 9.1 — o piso vigente quando o link foi gerado, só nas entradas que a
   * venda usou (`null` = sem piso, o que teria recusado a venda). Retrato: uma
   * mudança de piso depois não reescreve a história de uma venda já feita.
   */
  /**
   * #679 F1 — a venda foi renegociada e ESTA sessão deixou de valer: o id da
   * sessão nova. A sessão antiga é expirada na Stripe, e o webhook de
   * expiração não manda a recuperação dela ao cliente (é o preço antigo).
   */
  superseded_by: z.string().nullable().optional(),
  floor_snapshot: z
    .object({
      items: z.record(z.string(), z.number().int().nonnegative().nullable()),
      package: z.number().int().nonnegative().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * `last_payment_error.code` do PaymentIntent. Para Pix expirado é
   * `payment_intent_payment_attempt_expired` — NÃO confiar no `outcome` do
   * charge, que reporta `issuer_declined`/`generic_decline` mesmo sem banco
   * nenhum ter recusado.
   */
  last_failure_code: z.string().max(120).nullable().optional(),
  /** Quantas vezes o cliente gerou um meio de pagamento nesta sessão. */
  attempt_count: z.number().int().nonnegative().nullable().optional(),
  /** `session.expires_at` (epoch ms). */
  expires_at: z.number().int().nonnegative().nullable().optional(),
  /**
   * Quando a SESSÃO nasceu (`session.created`), não quando gravamos o
   * documento. A diferença importa porque nem todo documento nasce do nosso
   * callable: o webhook de expiração também cria, e usar o relógio de quem
   * escreve datava a tentativa pela morte dela — `created_at` posterior ao
   * `expires_at` em 3 dos 4 documentos reais (#313).
   */
  created_at: z.number().int().nonnegative(),
  updated_at: z.number().int().nonnegative(),
  updated_by: z.string().nullable(),
});
export type CheckoutAttempt = z.infer<typeof CheckoutAttemptSchema>;

/**
 * Sprint 35 PR E[sub-3] — linha de fatura. Stripe emite
 * `invoice.lines.data[]` com 1 entrada por SubscriptionItem (+ extras
 * pra proration adjustments). Capturar essa lista permite à tela
 * `InvoiceDetailPage` (PR F) listar "BASE €19,97" + "SUPPLIER_INTEGRATION
 * €4,99" em vez de só o total.
 *
 * `sku` é o SKU canônico mapeado via `external_price_id` → catálogo
 * `/config/planPrices/items/`. Pode ser `null` quando a linha é
 * proration adjustment (Stripe não associa proration a SKU específico
 * sempre — ex: cancelamento parcial mid-cycle).
 */
export const InvoiceLineSchema = z.object({
  /** Stripe line item ID (`il_*`). */
  external_line_id: nonEmpty,
  /** SKU mapeado a partir de `external_price_id`; null quando proration
   * adjustment sem associação a item específico. */
  sku: SkuSchema.nullable(),
  external_price_id: nonEmpty.nullable(),
  /** Amount em CENTS (smallest currency unit). Negativo pra credits/
   * proration adjustments que reduzem o subtotal. */
  amount_cents: z.number().int(),
  currency: CurrencySchema,
  /** Descritor humano (Stripe `description`, ex.: "1 × BASE (mensal)
   * after 7 days"). Já vem traduzido pelo Stripe na moeda da invoice. */
  description: z.string().max(500).nullable(),
  period_start: z.number().int().nonnegative(),
  period_end: z.number().int().nonnegative(),
  /** True quando Stripe marcou `proration: true` (credit/charge
   * adicional gerado por mudança mid-cycle: addAddon, removeAddon
   * mensal, plan price change). */
  proration: z.boolean(),
  quantity: z.number().int().positive(),
});
export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

/**
 * NFS-e de uma fatura, espelhada do Asaas em `tenants/{tid}/invoices/{pay_…}.nfse`.
 *
 * Origem: #754 — 2026-09-27 22:24 +0200 (A5 do Asaas).
 *
 * Por quê: a nota é pedida por pagamento, com valor próprio (regra do owner,
 * 2026-09-27): com split pago, `netValue − Σ split` (o que sobra para o
 * EtiquetaChef depois do split e da taxa do Asaas); sem split, o valor cheio.
 * Por isso `value_cents` ≠ `total_amount` da fatura, de propósito.
 *
 * Contrato: escrito só pelo Admin SDK (`billing/asaas/nfse.ts` ao pedir a
 * nota; `reconcileAsaasInvoice` a cada `INVOICE_*`). Clientes só leem.
 *
 * Usado por: grava — Functions `billing/asaas/nfse.ts` (`syncAsaasNfse`) e
 * `billing/webhooks/asaas/asaasHandlers.ts` (`reconcileAsaasInvoice`); lê —
 * web `packages/ui-shared/src/billing/InvoiceTable.tsx` (botão "Baixar NFS-e",
 * via `nfsePdfUrl`), `apps/app` (`InvoicesPage`, `RecentInvoicesSection`),
 * `apps/admin` (`components/InvoiceTable.tsx`, `routes/InvoicesPage.tsx`), e
 * todo `InvoiceSchema.safeParse` do servidor (ver `InvoiceSchema`). Android e
 * iOS não leem (não mostram nota fiscal hoje).
 *
 * Dados — exemplo real do sandbox, anonimizado (capturado 2026-09-27, API v3,
 * cobrança de R$ 199,90 no cartão com split de 20 %):
 *   {
 *     "provider_invoice_id": "inv_000000000000",   // id da nota no Asaas
 *     "status": "AUTHORIZED",
 *     "value_cents": 15476,                        // 193,44 (net) − 38,68 (split)
 *     "number": "565266",                          // número da NFS-e na prefeitura
 *     "pdf_url": "https://sandbox.asaas.com/file/public/download/<token>",
 *     "xml_url": "https://sandbox.asaas.com/file/public/download/<token>",
 *     "error": null,
 *     "updated_at": 1790540000000                  // ms, relógio do servidor
 *   }
 * `pdf_url`/`xml_url` são PÚBLICOS (abrem sem token, medido) — o cliente
 * abre direto, nada vai para o Storage. Só existem a partir de `AUTHORIZED`.
 *
 * Falhas conhecidas: status novo do Asaas cai em `UNKNOWN` na leitura
 * (`.catch`), para a tela de faturas não quebrar.
 *
 * Issues: #754 (A5), #748 (G6).
 */
export const InvoiceNfseSchema = z.object({
  /** Id da nota no Asaas (`inv_…`). `null` quando o nosso pedido falhou. */
  provider_invoice_id: z.string().min(1).nullable(),
  status: z.enum(ALL_NFSE_STATUSES).catch(NfseStatus.UNKNOWN),
  /** Valor da nota em centavos — ver a regra no docblock. */
  value_cents: z.number().int().nonnegative(),
  number: z.string().nullable().optional(),
  pdf_url: z.url().nullable().optional(),
  xml_url: z.url().nullable().optional(),
  /** Motivo do erro (Asaas `statusDescription` ou a falha do nosso pedido), até 500 chars. */
  error: z.string().max(500).nullable().optional(),
  updated_at: z.number().int().nonnegative(),
});
export type InvoiceNfse = z.infer<typeof InvoiceNfseSchema>;

/**
 * Link do PDF da NFS-e que a tela pode oferecer, ou `null`.
 *
 * Origem: #754 — 2026-09-27 22:33 +0200 (A5 do Asaas).
 *
 * Por quê: o botão "Baixar NFS-e" só faz sentido com a nota VALENDO
 * (`NFSE_VALID_STATUSES`: autorizada, cancelando ou com cancelamento recusado)
 * — antes disso o Asaas não tem PDF, e depois de cancelada o PDF é de uma nota
 * sem valor. Uma regra só para as três telas, em vez de três condições que
 * divergem.
 *
 * Contrato: puro. Recebe o `nfse` da fatura (ou nada).
 *
 * Usado por: `packages/ui-shared/src/billing/InvoiceTable.tsx` (visibilidade),
 * `apps/app` (`InvoicesPage`, `RecentInvoicesSection` — abrir) e `apps/admin`
 * (`components/InvoiceTable.tsx` — visibilidade; `routes/InvoicesPage.tsx` —
 * abrir).
 */
export function nfsePdfUrl(nfse: InvoiceNfse | undefined): string | null {
  if (!nfse || !NFSE_VALID_STATUSES.has(nfse.status)) return null;
  return nfse.pdf_url ?? null;
}

/**
 * Status em que a NFS-e VALE como documento fiscal — o PDF pode ser oferecido.
 *
 * Origem: #754 — 2026-09-28 07:59 +0200 (auditoria da A5, M1).
 *
 * Por quê: a nota vale de `AUTHORIZED` até `CANCELED`. Durante
 * `PROCESSING_CANCELLATION` ela ainda não foi cancelada, e com
 * `CANCELLATION_DENIED` a prefeitura recusou o cancelamento — a nota segue
 * valendo e o cliente tem direito ao PDF.
 */
const NFSE_VALID_STATUSES: ReadonlySet<NfseStatus> = new Set([
  NfseStatus.AUTHORIZED,
  NfseStatus.PROCESSING_CANCELLATION,
  NfseStatus.CANCELLATION_DENIED,
]);

/**
 * Fatura: doc `tenants/{tenantId}/invoices/{invoiceId}` — uma por cobrança.
 *
 * Origem: Sprint 5 (sem issue) — schema com a forma da Stripe Invoice, para o
 * gateway entrar sem mudar a forma.
 *
 * Contrato: escrita só pelo Admin SDK (webhooks do Stripe e do Asaas via
 * `billing/webhooks/gatewayDocs.ts`; `billing/asaas/nfse.ts` grava `nfse`).
 * No Asaas o id é o da cobrança (`pay_…`).
 *
 * Usado por: web `apps/app` (`lib/firestoreBilling.ts`) e `apps/admin`
 * (`lib/firestoreTenantDetail.ts`); Functions que fazem `safeParse` da fatura
 * (`dailyBillingTick`, `onInvoiceWritten`, `computeSubscriptionReceivables`,
 * `resendInvoiceEmail`) — um campo que não parseia descarta a fatura inteira
 * nesses leitores, por isso os campos novos toleram valor desconhecido.
 *
 * Histórico:
 *  - 2026-09-28 07:59 +0200 — (#754) `nfse` (NFS-e do Asaas); saem `nfe_xml_url`/`nfe_access_key`
 *    do emissor antigo; caminho deste docblock corrigido.
 */
export const InvoiceSchema = z.object({
  id: nonEmpty,
  /** Sequência humana da fatura. Dois prefixos, dois contadores separados
   * (`/config/billing/counters/{invoices,invoices_test}`): `INV-` é a
   * sequência FISCAL, exclusiva de `livemode: true` — precisa ser contínua
   * (Receita Federal / SDI italiano). `TEST-` é do Stripe sandbox, que roda
   * contra este mesmo projeto (não há staging) e não pode furar a fiscal.
   * Série por região (#960, owner 2026-10-28, #1076): o contador mora no banco
   * do tenant; `southamerica-east1` sem sufixo (`INV-000042`), `europe-west1`
   * com `-EU` (`INV-EU-000042`, `TEST-EU-000001`).
   * Ver `apps/functions/src/lib/billing/invoiceNumber.ts`. */
  invoice_number: z.string().regex(/^(?:INV|TEST)(?:-EU)?-\d{6,}$/),
  tenant_id: TenantIdSchema,
  customer_id: TenantIdSchema,
  /** Modo do Stripe: `true` = LIVE (dinheiro real), `false` = sandbox/test.
   * Opcional (registros antigos não têm). Ver `docs/STRIPE.md` §9. */
  livemode: z.boolean().optional(),
  subscription_id: nonEmpty,
  status: InvoiceStatusSchema,
  // Moeda + valores. CR-01 do sprint-bug-hunt: TODOS em CENTS
  // (smallest currency unit, convenção Stripe). Catálogo
  // (PlanPricing.amount) está em unidades decimais — converter via
  // `priceUnitsToCents` ao gravar aqui.
  currency: CurrencySchema,
  subtotal_amount: z.number().int().nonnegative(),
  tax_amount: z.number().int().nonnegative(),
  discount_amount: z.number().int().nonnegative(),
  total_amount: z.number().int().nonnegative(),
  amount_paid: z.number().int().nonnegative(),
  amount_due: z.number().int().nonnegative(),
  // Janela de cobrança
  period_start: z.number().int().nonnegative(),
  period_end: z.number().int().nonnegative(),
  // Datas
  issued_at: z.number().int().nonnegative(),
  due_at: z.number().int().nonnegative(),
  paid_at: z.number().int().nonnegative().nullable(),
  cancelled_at: z.number().int().nonnegative().nullable(),
  refunded_at: z.number().int().nonnegative().nullable(),
  /**
   * Sprint 19 — millis epoch quando a fatura foi marcada `VOID` pelo
   * helper `cancelSubscription` (encerramento de tenant). Distinto de
   * `cancelled_at` (cancelamento manual) e `refunded_at` (estorno).
   */
  void_at: z.number().int().nonnegative().optional(),
  /**
   * Sprint 19 — motivo do void. Tipicamente `tenant_deleted`.
   */
  void_reason: z.string().max(120).optional(),
  // Documentos
  pdf_url: z.url().nullable(),
  receipt_url: z.url().nullable(),
  /**
   * NFS-e desta fatura, emitida pelo Asaas (#754). Ausente em fatura de outro
   * gateway — só pagamento do Asaas gera nota. Ver {@link InvoiceNfseSchema}.
   * (Os campos `nfe_xml_url`/`nfe_access_key` da Sprint 5 saíram com o emissor
   * antigo em 2026-09-27, #754; nunca foram gravados em produção.)
   */
  nfse: InvoiceNfseSchema.optional(),
  /**
   * Sprint 5 — IDs no gateway externo. `external_invoice_id` é o
   * Stripe `in_*`. Populated pelo webhook
   * `invoice.created`/`invoice.finalized`.
   * B-19: regex `<prefix>_<base62>`.
   */
  external_provider: PaymentProviderSchema.optional(),
  external_invoice_id: externalProviderId.optional(),
  // Reminders enviados (idempotência local — emailLogs é fonte da verdade)
  due_soon_reminders_sent: z.array(z.number().int().positive()),
  overdue_reminders_sent: z.array(z.number().int().positive()),
  /**
   * Sprint 35 PR E[sub-3] — line items capturados de
   * `stripeInvoice.lines.data` no webhook `invoice.created`. Tela
   * `InvoiceDetailPage` (PR F) renderiza tabela. Optional durante
   * migração — invoices legacy criadas antes do refator não têm.
   */
  lines: z.array(InvoiceLineSchema).optional(),
  // Metadata
  created_at: z.number().int().nonnegative(),
  updated_at: z.number().int().nonnegative(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * Doc `/tenants/{id}/billing/payments/{paymentId}`. Cada pagamento
 * (manual via super-admin nesta sprint; gateway na Sprint 5).
 */
export const PaymentSchema = z.object({
  id: nonEmpty,
  tenant_id: TenantIdSchema,
  customer_id: TenantIdSchema,
  /** Modo do Stripe: `true` = LIVE (dinheiro real), `false` = sandbox/test.
   * Opcional (registros antigos não têm). Ver `docs/STRIPE.md` §9. */
  livemode: z.boolean().optional(),
  invoice_id: nonEmpty,
  status: PaymentStatusSchema,
  // CR-01: amount em CENTS (smallest currency unit) — fonte ÚNICA do valor,
  // exatamente como o gateway envia (ex.: Stripe manda 180000 = R$ 1.800,00).
  // O display converte via priceCentsToUnits/formatCurrency; NÃO duplicamos o
  // valor numa string decimal (o antigo `amount_str` foi removido — duplicação
  // gerava confusão e foi gravada errada por webhooks: cents cru como string).
  currency: CurrencySchema,
  amount: z.number().int().nonnegative(),
  refunded_amount: z.number().int().nonnegative(),
  // Método e provider
  payment_method_id: nonEmpty.nullable(),
  payment_method_type: PaymentMethodTypeSchema,
  provider: PaymentProviderSchema.nullable(),
  provider_transaction_id: nonEmpty.nullable(),
  /**
   * O que este pagamento quita (#737). `ADDON_PRORATION` é a proporção
   * avulsa de um módulo adicionado pelo consultor numa assinatura Pix — não
   * é renovação, e por decisão do owner (2026-09-25) não gera parcela de
   * comissão nem consome `installmentsCap` no CRM. Ausente ou desconhecido =
   * `SUBSCRIPTION`, que é tudo o que existia antes.
   */
  purpose: z.enum(['SUBSCRIPTION', 'ADDON_PRORATION']).optional().catch('SUBSCRIPTION'),
  /**
   * #755 (A6) — parcela do anual parcelado do Asaas: número (1..N), total de
   * parcelas e total do ano, gravados pelo webhook. As N parcelas confirmam
   * juntas; quem notifica o cliente (`onPaymentWritten`) só fala da 1ª, e a
   * comissão do CRM sobre elas é a #764. Ausentes fora do parcelado.
   */
  installment_number: z.number().int().positive().nullable().optional().catch(undefined),
  installment_count: z.number().int().positive().nullable().optional().catch(undefined),
  installment_total_cents: z.number().int().nonnegative().nullable().optional().catch(undefined),
  // Datas
  paid_at: z.number().int().nonnegative().nullable(),
  failed_at: z.number().int().nonnegative().nullable(),
  failed_reason: z.string().nullable(),
  refunded_at: z.number().int().nonnegative().nullable(),
  /**
   * Sprint 5 — IDs no gateway externo. `external_payment_id` é o
   * Stripe PaymentIntent `pi_*`. Populated pelo webhook
   * `payment_intent.succeeded`/`charge.succeeded`.
   * B-19: regex `<prefix>_<base62>`.
   */
  external_payment_id: externalProviderId.optional(),
  external_charge_id: externalProviderId.optional(),
  /**
   * Chargeback (#740) — escritos por `handleChargeDispute` (`charge.dispute.*`).
   * Aditivos: ausentes em todo pagamento que nunca foi disputado.
   *
   * `disputes` é a verdade: uma entrada por disputa (`{dp_id}` → status cru do
   * Stripe, valor e `event.created` da última entrega), porque uma cobrança
   * pode ter mais de uma disputa e elas se sobrescreviam no formato plano.
   * Os quatro campos planos abaixo são DERIVADOS dele por
   * `summarizePaymentDisputes` (`paymentDisputes.ts`) e continuam gravados
   * para quem já os lia:
   *
   * - `disputed_amount` (cents): soma das disputas abertas e perdidas, com teto
   *   no `amount`. O CRM trata como estorno das comissões —
   *   `max(refunded_amount, disputed_amount)`, nunca a soma.
   * - `dispute_status`: o status AGREGADO — o que ainda mexe no dinheiro vence
   *   (aberta > perdida > consulta prévia/prevenida > ganha).
   * - `dispute_id`: a disputa que deu o status agregado.
   * - `dispute_updated_at`: a entrega mais recente entre as disputas.
   *
   * `status` e `refunded_amount` do payment NÃO mudam com a disputa: estorno de
   * verdade continua vindo só de `charge.refunded`. Payment gravado antes do
   * mapa (só os campos planos) é lido como uma disputa só.
   */
  disputes: z.record(externalProviderId, PaymentDisputeEntrySchema).optional(),
  disputed_amount: z.number().int().nonnegative().optional(),
  dispute_status: z.string().min(1).optional(),
  dispute_id: externalProviderId.optional(),
  dispute_updated_at: z.number().int().nonnegative().optional(),
  // Metadata
  created_at: z.number().int().nonnegative(),
  created_by: nonEmpty,
  updated_at: z.number().int().nonnegative(),
});
export type Payment = z.infer<typeof PaymentSchema>;

/**
 * Doc `/tenants/{id}/paymentMethods/{methodId}` — irmã de `invoices`/`payments`,
 * NÃO sob `billing/` (`firestore.rules`, `match /paymentMethods/{methodId}`).
 * Schema desenhado mas **não populado** na Sprint 16 — Sprint 5 ativa via
 * gateway; hoje a coleção segue sem escritor.
 *
 * Comentário corrigido em 2026-10-03 14:02 +0200 (#795, WEB-1): dizia
 * `/tenants/{id}/billing/paymentMethods/{methodId}`, caminho que não existe
 * (5 segmentos não formam documento) e que o `apps/app` copiou — a
 * `/account/payment-methods` lançava ao montar para todo OWNER.
 *
 * Usado por: `apps/app` `PaymentMethodsPage` (lista read-only), `apps/admin`
 * `subscribePaymentMethods` (`lib/firestoreTenantDetail.ts`); Android espelha
 * em `domain/billing/PaymentMethod.kt`.
 */
export const PaymentMethodSchema = z.object({
  id: nonEmpty,
  tenant_id: TenantIdSchema,
  type: PaymentMethodTypeSchema,
  status: PaymentMethodStatusSchema,
  is_default: z.boolean(),
  card_brand: z.string().nullable(),
  card_last4: z.string().regex(/^\d{4}$/).nullable(),
  card_exp_month: z.number().int().min(1).max(12).nullable(),
  card_exp_year: z.number().int().min(2025).max(2100).nullable(),
  provider: PaymentProviderSchema,
  provider_method_id: nonEmpty,
  created_at: z.number().int().nonnegative(),
  updated_at: z.number().int().nonnegative(),
});
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/**
 * Sprint 5 — Doc `/tenants/{id}/billing/refunds/{refundId}`. Cada refund
 * (estorno) emitido. Pode ser parcial (`amount < payment.amount`) ou
 * total. Reason `MBG` é o estorno automático ao encerrar conta dentro
 * da janela; demais são manuais via super-admin ou chargeback.
 *
 * Cria registro localmente assim que o refund é solicitado (status
 * `PENDING`); webhook `charge.refunded` confirma para `SUCCEEDED` e
 * marca o `Payment` correspondente como `REFUNDED`/`PARTIALLY_REFUNDED`.
 */
export const RefundSchema = z.object({
  id: nonEmpty,
  tenant_id: TenantIdSchema,
  payment_id: nonEmpty,
  invoice_id: nonEmpty,
  status: RefundStatusSchema,
  reason: RefundReasonSchema,
  // CR-01: amount em CENTS (sempre <= payment.amount, ambos em cents).
  currency: CurrencySchema,
  amount: z.number().int().nonnegative(),
  // Provider externo
  provider: PaymentProviderSchema,
  external_refund_id: nonEmpty.optional(),
  // Datas
  requested_at: z.number().int().nonnegative(),
  succeeded_at: z.number().int().nonnegative().nullable(),
  failed_at: z.number().int().nonnegative().nullable(),
  failed_reason: z.string().max(500).nullable(),
  // Quem solicitou (super-admin UID, ou `__system__` para MBG automático)
  requested_by: nonEmpty,
  // Metadata
  created_at: z.number().int().nonnegative(),
  updated_at: z.number().int().nonnegative(),
});
export type Refund = z.infer<typeof RefundSchema>;

/**
 * Bucket mensal de uso `/tenants/{id}/billing/usage/months/{yyyymm}`.
 * `labelsPrinted` continua sendo incrementado pelo trigger
 * `recordLabelPrinted` para fins de BI (Sprint 4). Sprint 16 removeu
 * limite e `lastSyncTruncated`.
 */
export const UsageCountersSchema = z.object({
  yyyymm: z.string().regex(/^\d{6}$/),
  labelsPrinted: z.number().int().nonnegative(),
});
export type UsageCounters = z.infer<typeof UsageCountersSchema>;

// ---------- Billing — inputs de callables (Sprint 16) ----------

/**
 * Id de requisição gerado pelo CLIENTE quando o diálogo de uma ação de
 * dinheiro abre (`crypto.randomUUID()`), reusado em todo retry dela.
 *
 * Origem: #795 (1º nível, itens 2 e 3) — 2026-10-07 12:55 +0200.
 *
 * Por quê: clique duplo e retry depois de timeout chegavam ao servidor como
 * pedidos novos — o pagamento manual era gravado duas vezes e o estorno ia ao
 * gateway com uma chave de idempotência nova. Com o id estável, o servidor
 * deriva dele o id do documento (`create()` recusa o repetido) e a chave do
 * gateway. Mesmo formato de `PortalIdempotencyKeySchema`
 * (`inboundWebhooks.ts`): só caracteres seguros para id de doc e header.
 *
 * Usado por: `RecordManualPaymentInput` e `RequestRefundInput` (abaixo).
 */
export const ClientRequestIdSchema = z
  .string()
  .min(8)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, 'invalid_request_id');

export const RecordManualPaymentInput = z.object({
  tenantId: TenantIdSchema,
  invoiceId: nonEmpty,
  /** #795 (1º nível item 3): idempotência do clique duplo / retry. */
  requestId: ClientRequestIdSchema,
  // CR-01: amount em CENTS (admin web converte na borda).
  amount: z.number().int().nonnegative(),
  currency: CurrencySchema,
  paid_at: z.number().int().nonnegative().optional(),
  notes: z.string().max(500).optional(),
});
export type RecordManualPaymentInput = z.infer<typeof RecordManualPaymentInput>;

export const CancelInvoiceInput = z.object({
  tenantId: TenantIdSchema,
  invoiceId: nonEmpty,
  reason: z.string().min(1).max(500),
});
export type CancelInvoiceInput = z.infer<typeof CancelInvoiceInput>;

export const ResendInvoiceEmailInput = z.object({
  tenantId: TenantIdSchema,
  invoiceId: nonEmpty,
});
export type ResendInvoiceEmailInput = z.infer<typeof ResendInvoiceEmailInput>;

/**
 * Callable `getInvoiceDocumentUrl` (#776): link ATUAL do documento de uma
 * fatura, pedido na hora do clique. O `pdf_url` gravado pelo webhook do Stripe
 * expira; o servidor relê a fatura no gateway. `source`: `GATEWAY` = link
 * novo da Stripe; `STORED` = o link gravado (Asaas, ou Stripe indisponível).
 * `url: null` = a fatura não tem documento.
 */
export const GetInvoiceDocumentUrlInput = z.object({
  tenantId: TenantIdSchema,
  invoiceId: nonEmpty,
});
export type GetInvoiceDocumentUrlInput = z.infer<typeof GetInvoiceDocumentUrlInput>;
export const GetInvoiceDocumentUrlOutput = z.object({
  url: z.url().nullable(),
  source: z.enum(['GATEWAY', 'STORED']),
});
export type GetInvoiceDocumentUrlOutput = z.infer<typeof GetInvoiceDocumentUrlOutput>;

export const UpdateBillingSettingsInput = z.object({
  billing_email_enabled: z.boolean().optional(),
  invoice_created_email_enabled: z.boolean().optional(),
  invoice_due_soon_email_enabled: z.boolean().optional(),
  invoice_overdue_email_enabled: z.boolean().optional(),
  payment_success_email_enabled: z.boolean().optional(),
  payment_failed_email_enabled: z.boolean().optional(),
  payment_action_required_email_enabled: z.boolean().optional(),
  due_soon_days_before: z.array(z.number().int().positive()).min(1).max(10).optional(),
  overdue_reminder_days: z.array(z.number().int().positive()).min(1).max(10).optional(),
  suspend_after_days_overdue: z.number().int().positive().optional(),
  next_invoice_days_before_due: z.number().int().nonnegative().optional(),
});
export type UpdateBillingSettingsInput = z.infer<typeof UpdateBillingSettingsInput>;

export const UpdatePlanPriceInput = z.object({
  country_code: CountrySchema,
  billing_cycle: BillingCycleSchema,
  amount: z.number().nonnegative(),
  currency: CurrencySchema.optional(),
});
export type UpdatePlanPriceInput = z.infer<typeof UpdatePlanPriceInput>;

/**
 * Sprint 35 PR D[sub-7] + F — input do callable `updatePlanPriceForSku`.
 * Modelo novo `(sku, currency)` sem o eixo cycle no docId. `yearly_mode`
 * DISCOUNT_PCT calcula `yearly_amount_cents = monthly × 12 × (1 - pct)`;
 * MANUAL exige `yearly_amount_cents` explícito.
 */
export const UpdatePlanPriceForSkuInput = z
  .object({
    sku: SkuSchema,
    currency: CurrencySchema,
    monthly_amount_cents: z.number().int().positive(),
    yearly_mode: z.enum(ALL_YEARLY_DISCOUNT_MODES).default('DISCOUNT_PCT'),
    yearly_discount_pct: z.number().min(0).max(80).optional(),
    yearly_amount_cents: z.number().int().positive().optional(),
    reason: z.string().trim().min(1).max(500).default('admin update'),
  })
  .superRefine((data, ctx) => {
    if (data.yearly_mode === 'DISCOUNT_PCT' && data.yearly_discount_pct === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['yearly_discount_pct'],
        message: 'yearly_mode=DISCOUNT_PCT exige yearly_discount_pct.',
      });
    }
    if (data.yearly_mode === 'MANUAL' && data.yearly_amount_cents === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['yearly_amount_cents'],
        message: 'yearly_mode=MANUAL exige yearly_amount_cents.',
      });
    }
  });
export type UpdatePlanPriceForSkuInput = z.infer<typeof UpdatePlanPriceForSkuInput>;

export const SetDefaultPaymentMethodInput = z.object({
  tenantId: TenantIdSchema,
  methodId: nonEmpty,
});
export type SetDefaultPaymentMethodInput = z.infer<typeof SetDefaultPaymentMethodInput>;

export const RemovePaymentMethodInput = z.object({
  tenantId: TenantIdSchema,
  methodId: nonEmpty,
});
export type RemovePaymentMethodInput = z.infer<typeof RemovePaymentMethodInput>;

/**
 * Sprint 35 PR G5 — input do callable `setMarketConfig`. Espelha o
 * schema server-side em `apps/functions/src/admin/setMarketConfig.ts`.
 * Cliente revalida no submit antes de chamar; server revalida e
 * aplica Guards 6/7/8.
 */
/**
 * Sprint 35 PR G3 — input do callable `applyLifetimeFree`. Espelha
 * `apps/functions/src/admin/applyLifetimeFree.ts`. Cliente revalida no
 * submit; server revalida (categoria OTHER exige `reason`).
 */
export const ApplyLifetimeFreeInput = z
  .object({
    tenantId: TenantIdSchema,
    category: z.enum(ALL_LIFETIME_FREE_CATEGORIES),
    reason: z.string().trim().max(500).default(''),
  })
  .superRefine((data, ctx) => {
    if (data.category === 'OTHER' && data.reason.trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'reason obrigatório quando category=OTHER.',
      });
    }
    // #679 F7: demo é tipo comercial próprio (`commercial_mode: DEMO`, pelo
    // interruptor de demo), não "cortesia de categoria DEMO" — que nasceria
    // COURTESY sem `isDemo` e o CRM recusaria como tenant de demonstração.
    if (data.category === 'DEMO') {
      ctx.addIssue({
        code: 'custom',
        path: ['category'],
        message: 'Demonstração não é categoria de cortesia: use o interruptor de demo.',
      });
    }
  });
export type ApplyLifetimeFreeInput = z.infer<typeof ApplyLifetimeFreeInput>;

/**
 * Issue #120 — input do callable admin `createCourtesyTenant`. Espelha
 * `apps/functions/src/admin/createCourtesyTenant.ts`.
 *
 * Coleta os mesmos campos que `CompleteOnboardingInput`, exceto:
 * - `displayName`/owner: o OWNER é o próprio super-admin chamador
 *   (`request.auth`), então não vem do cliente;
 * - `billingCycle`: fixo `monthly` no callable (cortesia é 100% off forever,
 *   o ciclo só dita a cadência de faturas zeradas);
 * - `latitude`/`longitude`/`placeId`/`addressCountryCode`: o admin digita
 *   endereço livre, sem Google Places — gravados como `null`.
 *
 * Ganha `phoneE164` (gravado em `members/{uid}.phoneE164`), que o onboarding
 * não envia porque lá o telefone do fundador já vive em `/profiles/{uid}`.
 *
 * `category`/`reason` da cortesia entram aqui desde a F7 da #679 (#738): o
 * tenant nasce `commercial_mode: COURTESY`, sem Stripe, num callable só — o
 * admin deixou de encadear o `applyLifetimeFree` depois da criação. Mesma
 * regra do `ApplyLifetimeFreeInput`: categoria OTHER exige motivo.
 */
export const CreateCourtesyTenantInput = z
  .object({
    tenantName: z.string().trim().min(2).max(120),
    tenantKind: TenantKindSchema,
    countryCode: CountrySchema,
    taxId: z.string().min(3).max(40),
    phoneE164: PhoneE164Schema,
    // B-21: ver TenantSchema.locale.
    locale: tenantLocale,
    timeZone: z.string().min(1),
    address: z.string().max(300).default(''),
    category: z.enum(ALL_LIFETIME_FREE_CATEGORIES),
    reason: z.string().trim().max(500).default(''),
  })
  .superRefine((data, ctx) => {
    if (data.category === 'OTHER' && data.reason.trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'reason obrigatório quando category=OTHER.',
      });
    }
    // #679 F7: demo é tipo comercial próprio (`commercial_mode: DEMO`, pelo
    // interruptor de demo), não "cortesia de categoria DEMO" — que nasceria
    // COURTESY sem `isDemo` e o CRM recusaria como tenant de demonstração.
    if (data.category === 'DEMO') {
      ctx.addIssue({
        code: 'custom',
        path: ['category'],
        message: 'Demonstração não é categoria de cortesia: use o interruptor de demo.',
      });
    }
  });
export type CreateCourtesyTenantInput = z.infer<typeof CreateCourtesyTenantInput>;

/**
 * Sprint 35 PR G4 — input do callable `removeLifetimeFree`. Desde a F7 da
 * #679 o tenant volta a DIRECT sem assinatura e cai no paywall; quem cobra é o
 * link novo da Contratação. `collection_mode` (`INVOICE_IMMEDIATELY` nunca foi
 * implementado) saiu.
 */
export const RemoveLifetimeFreeInput = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().trim().min(1).max(500),
});
export type RemoveLifetimeFreeInput = z.infer<typeof RemoveLifetimeFreeInput>;

export const SetMarketConfigInput = z.object({
  country: z.string().regex(/^[A-Z]{2}$/, {
    message: 'country precisa ser ISO-3166 alpha-2 (2 letras maiúsculas).',
  }),
  saleable: z.boolean(),
  // PSP é slug do catálogo aberto (/config/psps) — Sprint 40.
  payment_provider: PaymentProviderSchema.nullable().optional(),
  fiscal_provider: z.enum(ALL_FISCAL_PROVIDERS).optional(),
  /**
   * #756 (A7) — modo do PSP ativo (`live`/`sandbox`) e os PSPs que a
   * Contratação pode escolher. Ausentes = preserva o valor do doc (merge).
   * Regras (ativo na lista, Asaas só no BR) validadas no `setMarketConfig`.
   */
  payment_mode: z.enum(ALL_PAYMENT_MODES).optional(),
  allowed_payment_providers: z.array(PaymentProviderSchema).min(1).max(10).optional(),
  // Legado desde #958 (2026-10-13): o país não escolhe entidade — a PJ sai do
  // PSP (`findSellerEntityForProvider`). Aceito só para um admin antigo não
  // receber `invalid-argument`; o `setMarketConfig` ignora e grava `null`.
  seller_entity_id: nonEmpty.nullable().optional(),
  reason: z.string().trim().min(1).max(500),
  // Sprint 40 — catálogo dinâmico. Todos opcionais: ausente = preserva o
  // valor atual do doc (merge). Espelha os campos opcionais de
  // `MarketConfigSchema`. `tax_id_regex` é re-validada no servidor (precisa
  // compilar). Ver doc do MarketConfigSchema p/ semântica de cada campo.
  name: z.string().trim().min(1).max(80).optional(),
  phone_country_code: z.string().regex(/^\+\d{1,4}$/).optional(),
  currency: CurrencySchema.optional(),
  language: TenantLocaleSchema.optional(),
  timezones: z.array(z.string().min(1).max(60)).min(1).optional(),
  tax_id_name: z.string().trim().min(1).max(40).optional(),
  tax_id_regex: z.string().min(1).max(400).optional(),
  tax_id_placeholder: z.string().max(60).optional(),
  tax_id_required: z.boolean().optional(),
  otp_enabled: z.boolean().optional(),
  active: z.boolean().optional(),
  /**
   * #1078 (F2 da #960) — região onde nascem os tenants novos do país quando a chave
   * `config/regions.europeWest1Enabled` está ligada. Ausente = preserva; `null` = apaga o
   * override (volta ao padrão do grupo, `defaultRegionForCountry`). Mudar exige super-admin ROOT.
   */
  region: TenantRegionSchema.nullable().optional(),
});
export type SetMarketConfigInput = z.infer<typeof SetMarketConfigInput>;

// ---------- Billing — inputs Sprint 5 (gateway + paywall) ----------

// ---------- #289 — link de pagamento no console de super-admin ----------

/**
 * Input do callable `adminCreateCheckoutLink`. **Não** aceita `success_url`/
 * `cancel_url` (como fazia a callable cliente `createCheckoutSession`, que
 * saiu na #679 F4): quem gera é o super-admin, e deixar um input privilegiado
 * escolher o destino do redirect reabriria o vetor de phishing que o
 * `assertAllowedCheckoutRedirect` fechou (§3.1.11). As URLs são fixas no
 * servidor.
 */
export const AdminCreateCheckoutLinkInput = z.object({
  tenantId: TenantIdSchema,
  // Só os ciclos com preço de lista (#679 D13): o checkout de catálogo
  // cobraria o preço MENSAL num trimestral, com mandato Pix trimestral.
  billing_cycle: z.enum(['monthly', 'yearly']),
});
export type AdminCreateCheckoutLinkInput = z.infer<typeof AdminCreateCheckoutLinkInput>;

export const AdminCreateCheckoutLinkOutput = z.object({
  /** `session.url` do gateway, VERBATIM — nunca reconstruída a partir do id. */
  url: z.url(),
  session_id: nonEmpty,
  /** `session.expires_at` em epoch ms; `null` se o gateway não informou. */
  expires_at: z.number().int().nonnegative().nullable(),
});
export type AdminCreateCheckoutLinkOutput = z.infer<typeof AdminCreateCheckoutLinkOutput>;

export const AdminGetCheckoutLinksInput = z.object({
  tenantId: TenantIdSchema,
});
export type AdminGetCheckoutLinksInput = z.infer<typeof AdminGetCheckoutLinksInput>;

/**
 * Uma tentativa de checkout como o console a enxerga: o doc de
 * `/checkoutAttempts` enriquecido com o estado REAL consultado no gateway.
 *
 * `is_live_link` é o campo que a UI usa para decidir entre "copiar" e "gerar
 * novo", e não é derivável só do Firestore: um doc `OPEN` com `url` gravada
 * pode já ter expirado sem o webhook ter chegado, e nesse caso o link copiado
 * não abriria nada.
 */
export const AdminCheckoutLinkSchema = z.object({
  session_id: nonEmpty,
  status: CheckoutAttemptStatusSchema,
  livemode: z.boolean(),
  /** URL da sessão enquanto ela vive (máx. 24h). */
  url: z.string().nullable(),
  /** `after_expiration.recovery.url` — nasce na expiração, vale 30 dias. */
  recovery_url: z.string().nullable(),
  recovery_expires_at: z.number().int().nonnegative().nullable(),
  expires_at: z.number().int().nonnegative().nullable(),
  amount_cents: z.number().int().nonnegative().nullable(),
  currency: CurrencySchema.nullable(),
  billing_cycle: BillingCycleSchema.nullable(),
  payment_method_types: z.array(z.string()).nullable(),
  last_failure_code: z.string().nullable(),
  attempt_count: z.number().int().nonnegative().nullable(),
  created_at: z.number().int().nonnegative(),
  /** Há link utilizável AGORA (sessão viva, ou recuperação não vencida). */
  is_live_link: z.boolean(),
  /** O link que a UI deve oferecer, já escolhido pelo servidor. */
  live_url: z.string().nullable(),
  /**
   * `STRIPE` = sessão que existe no gateway mas não no nosso Firestore, ou
   * seja, criada fora do nosso fluxo (Payment Link manual no Dashboard).
   */
  source: z.enum(['FIRESTORE', 'GATEWAY']),
  /**
   * PSP que emitiu o link (`stripe`, `asaas`, …) — #776 (owner, 2026-09-29:
   * "em links de pagamento informar o PSP"). Tentativa gravada: o `provider`
   * do `CheckoutAttempt`; sessão só no gateway: sempre `stripe` (é o único
   * listado lá). `.default('stripe')`: resposta de Function anterior à #776.
   */
  provider: PaymentProviderSchema.default('stripe'),
});
export type AdminCheckoutLink = z.infer<typeof AdminCheckoutLinkSchema>;

export const AdminGetCheckoutLinksOutput = z.object({
  links: z.array(AdminCheckoutLinkSchema),
  /**
   * `true` quando a consulta ao gateway falhou e a lista veio só do Firestore.
   * A tela avisa em vez de apresentar dado possivelmente velho como verdade.
   */
  stale: z.boolean(),
});
export type AdminGetCheckoutLinksOutput = z.infer<typeof AdminGetCheckoutLinksOutput>;

export const AdminSendCheckoutLinkEmailInput = z.object({
  tenantId: TenantIdSchema,
  sessionId: nonEmpty,
});
export type AdminSendCheckoutLinkEmailInput = z.infer<typeof AdminSendCheckoutLinkEmailInput>;

export const AdminSendCheckoutLinkEmailOutput = z.object({
  /** Para quem foi (o OWNER do tenant), para o console confirmar na tela. */
  email: z.string(),
  /** `true` = mandamos a URL de recuperação; `false` = a da sessão viva. */
  used_recovery_url: z.boolean(),
});
export type AdminSendCheckoutLinkEmailOutput = z.infer<typeof AdminSendCheckoutLinkEmailOutput>;

/**
 * Sprint 5 — input do callable `createCustomerPortalSession`. Cliente
 * OWNER pede o link para o portal de cliente do Stripe (gerenciar
 * cartões, baixar invoices, ver histórico).
 */
export const CreateCustomerPortalSessionInput = z.object({
  tenantId: TenantIdSchema,
  return_url: z.url(),
});
export type CreateCustomerPortalSessionInput = z.infer<typeof CreateCustomerPortalSessionInput>;

export const CreateCustomerPortalSessionOutput = z.object({
  url: z.url(),
  provider: PaymentProviderSchema,
});
export type CreateCustomerPortalSessionOutput = z.infer<typeof CreateCustomerPortalSessionOutput>;

/**
 * Entrada da callable `createAsaasCardUpdateLink` — o "portal" do Asaas, que
 * não tem portal (#755, A6).
 *
 * Origem: #755 — 2026-09-28 10:56 +0200.
 *
 * Usado por: `apps/functions/src/billing/asaas/cardChange.ts` (servidor);
 * `PaymentMethodsPage.tsx` e `SuspendedPage.tsx` (web `apps/app`).
 *
 * Dados: `{ "tenantId": "BR-00000000000000" }`.
 */
export const CreateAsaasCardUpdateLinkInput = z.object({ tenantId: TenantIdSchema });
export type CreateAsaasCardUpdateLinkInput = z.infer<typeof CreateAsaasCardUpdateLinkInput>;

/**
 * Saída de `createAsaasCardUpdateLink`: para onde mandar o cliente.
 *
 * Origem: #755 — 2026-09-28 10:56 +0200.
 *
 * Contrato: `kind` diz o que a página abre —
 *  - `CARD_CHECKOUT`: checkout hospedado do Asaas com o cartão novo, que
 *    substitui a assinatura quando pago (em dia: prorrogada pelos dias que
 *    restavam; em atraso: a antiga, com a dívida, sai);
 *  - `OVERDUE_INVOICE`: a fatura vencida (boleto/Pix), para quitar;
 *  - `RENEWAL_RECOVERY`: o link `/pay` da renovação recusada do anual parcelado.
 * `url` é sempre `https://*.asaas.com` ou a nossa `/pay` — a página confere.
 *
 * Dados: `{ "kind": "CARD_CHECKOUT", "url": "https://sandbox.asaas.com/checkoutSession/show/<uuid>" }`.
 */
export const CreateAsaasCardUpdateLinkOutput = z.object({
  kind: z.enum(['CARD_CHECKOUT', 'OVERDUE_INVOICE', 'RENEWAL_RECOVERY']),
  url: z.url(),
});
export type CreateAsaasCardUpdateLinkOutput = z.infer<typeof CreateAsaasCardUpdateLinkOutput>;

/**
 * Sprint 5 — input do callable `requestRefund` (super-admin only).
 * Emite refund manual (parcial ou total) via gateway. Refund MBG
 * automático **não** passa por aqui — é disparado pelo
 * `requestTenantDeletion` quando origem é `tenant_deleted` e
 * dentro da janela.
 */
export const RequestRefundInput = z.object({
  tenantId: TenantIdSchema,
  paymentId: nonEmpty,
  /** #795 (1º nível item 2): chave estável do estorno — retry não estorna 2×. */
  requestId: ClientRequestIdSchema,
  /** Valor a estornar em CENTS. Se ausente, full refund. */
  amount: z.number().int().positive().optional(),
  /**
   * Motivo do refund. Super-admin pode escolher qualquer motivo do catálogo
   * (inclusive `MBG`/`DISPUTE`, antes só gerados pelo sistema) ao emitir um
   * refund manual no admin.
   */
  reason: RefundReasonSchema,
  notes: z.string().max(1000).optional(),
});
export type RequestRefundInput = z.infer<typeof RequestRefundInput>;

/**
 * Input do callable `cancelSubscriptionAdmin` (super-admin). Cancela a
 * assinatura no gateway (Stripe ou Asaas) no fim do período pago; o webhook
 * do gateway corta o acesso (#961). Corte imediato só sem período a preservar.
 *
 * `reason` limitado a {@link SUBSCRIPTION_CANCEL_REASON_MAX} (#961): é o que
 * cabe em `cancelled_reason`. Até 2026-10-13 aceitava 500, e o texto era
 * gravado num campo de 120.
 */
export const CancelSubscriptionAdminInput = z.object({
  tenantId: TenantIdSchema,
  reason: z.string().min(1).max(SUBSCRIPTION_CANCEL_REASON_MAX),
  /**
   * #961 — `true` encerra já no gateway e corta o acesso (paywall). Só o
   * "reembolsar e cancelar também" do admin manda (owner, 2026-10-13: quem foi
   * reembolsado não tem período pago a preservar). Ausente/`false` = fim do
   * período pago.
   */
  immediate: z.boolean().optional(),
});
export type CancelSubscriptionAdminInput = z.infer<typeof CancelSubscriptionAdminInput>;

/**
 * Input do callable `postponeRenewalAdmin` (super-admin). Prorroga o próximo
 * vencimento da assinatura no gateway (Stripe `trial_end` + `proration_behavior:
 * none`): a próxima cobrança passa para `newDueAt` SEM cobrar o intervalo
 * (extensão gratuita). É só adiar — `newDueAt` tem que ser futuro E posterior
 * ao `current_period_end` atual (validado no callable, que conhece a sub).
 */
export const PostponeRenewalAdminInput = z.object({
  tenantId: TenantIdSchema,
  /** Nova data da próxima cobrança, em millis epoch (UTC). */
  newDueAt: z.number().int().positive(),
  reason: z.string().max(500).optional(),
});
export type PostponeRenewalAdminInput = z.infer<typeof PostponeRenewalAdminInput>;

/**
 * Input do callable `resolveTimezone`. Resolve fuso horário do tenant a
 * partir de lat/lon (geralmente capturado por GPS no Android ou pelo
 * placeId no web). Server faz lookup determinístico e grava em
 * `tenants/{id}.timeZone`.
 */
export const ResolveTimezoneInput = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  tenantId: TenantIdSchema,
});
export type ResolveTimezoneInput = z.infer<typeof ResolveTimezoneInput>;

/**
 * Sprint 21 PR C.2 — input do callable `updateTenantTimeZone`. OWNER-only,
 * audita em `AuditAction.TENANT_TIMEZONE_UPDATED`. Diferente de
 * `resolveTimezone` (que infere e grava), aqui o cliente envia o IANA
 * já escolhido (vindo do dropdown) ou já confirmado (vindo do botão
 * "Confirmar" no modo geo). `source` registra como o valor foi
 * escolhido pra facilitar suporte/auditoria.
 *
 * Mudança de fuso só afeta etiquetas criadas DEPOIS — `validUntilUtc` de
 * etiquetas existentes é fixado no momento de criação.
 */
/**
 * `true` quando o runtime (`Intl`) aceita o identificador como fuso.
 * Origem: #795 — 2026-10-06 11:40 +0200. Usado por: `UpdateTenantTimeZoneInput`.
 */
function isIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export const UpdateTenantTimeZoneInput = z.object({
  tenantId: TenantIdSchema,
  /**
   * IANA TZ string (ex.: 'America/Sao_Paulo', 'Europe/Rome').
   *
   * Bug corrigido em 2026-10-06 11:40 +0200 (#795, Functions): só validava o
   * tamanho, e um valor que o `Intl` recusa derrubava os schedulers que
   * formatam hora pelo fuso do tenant (`dailyExpiryDigest`). O refine usa o
   * próprio `Intl` do runtime como validador, igual a `isValidTimeZone` em
   * `apps/functions/src/lib/timeZone.ts`.
   */
  timeZone: z
    .string()
    .min(1)
    .max(64)
    .refine(isIanaTimeZone, { message: 'Fuso horário IANA inválido.' }),
  /**
   * Origem da escolha:
   * - `geo`: usuário usou geolocation + `resolveTimezone` e clicou Confirmar.
   * - `manual`: usuário escolheu no dropdown `timeZonesForCountry`.
   */
  source: z.enum(['geo', 'manual']),
});
export type UpdateTenantTimeZoneInput = z.infer<typeof UpdateTenantTimeZoneInput>;

/**
 * Input do callable `updateTenantBasics` — edição do nome e/ou endereço do
 * estabelecimento pelo OWNER em `/settings → Estabelecimento` (web e Android).
 *
 * `countryCode` do tenant é imutável (faz parte do `tenantId`); por isso o
 * novo endereço precisa estar no mesmo país: o cliente envia
 * `addressCountryCode` extraído do place escolhido e a callable rejeita se
 * divergir. `placeId`/`latitude`/`longitude` são obrigatórios juntos com
 * `address` (sem place não há coords nem país a validar). Pelo menos um de
 * {name, address} deve mudar — a callable trata no-op como sucesso.
 *
 * Mudar o endereço NÃO altera o fuso automaticamente (use `updateTenantTimeZone`).
 */
export const UpdateTenantBasicsInput = z.object({
  tenantId: TenantIdSchema,
  name: z.string().min(2).max(120).optional(),
  address: z.string().max(300).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  placeId: z.string().optional(),
  /** País (ISO 3166-1 alpha-2) do place escolhido — deve igualar `countryCode` do tenant. */
  addressCountryCode: z.string().regex(/^[A-Z]{2}$/).optional(),
});
export type UpdateTenantBasicsInput = z.infer<typeof UpdateTenantBasicsInput>;

// ---------- Label Template inputs (Sprint 19) ----------

/**
 * Body de criação de template (Sprint 34). `id`/`tenantId`/timestamps/audit
 * ficam a cargo do server. `includedFields` é o subconjunto de campos ligados
 * (catálogo `TEMPLATE_FIELD_KINDS`); pode ser vazio (etiqueta só com QR + logo
 * fixos). Ordem/estilo vêm do catálogo, não deste array.
 */
export const CreateLabelTemplateInput = z.object({
  tenantId: TenantIdSchema,
  name: z.string().min(1).max(60),
  // Faixa dos rolos vendidos (ver `labelLayout.ts`). Só na ESCRITA: o
  // `LabelTemplateSchema` acima segue permissivo porque também valida a leitura,
  // e modelo legado fora da faixa tem de continuar sendo lido e impresso.
  widthMm: z.number().int().min(MIN_TEMPLATE_WIDTH_MM).max(MAX_TEMPLATE_WIDTH_MM),
  heightMm: z.number().int().min(MIN_TEMPLATE_HEIGHT_MM).max(MAX_TEMPLATE_HEIGHT_MM),
  gapMm: z.number().int().nonnegative().default(2),
  rowGapDots: z.number().int().nonnegative().max(40).default(4),
  verticalFit: VerticalFitSchema.default('FILL'),
  includedFields: z.array(TemplateFieldKindSchema).max(TEMPLATE_FIELD_KINDS.length).default([]),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
});
export type CreateLabelTemplateInput = z.infer<typeof CreateLabelTemplateInput>;

export const UpdateLabelTemplateInput = z.object({
  tenantId: TenantIdSchema,
  templateId: LabelTemplateIdSchema,
  name: z.string().min(1).max(60).optional(),
  widthMm: z.number().int().min(MIN_TEMPLATE_WIDTH_MM).max(MAX_TEMPLATE_WIDTH_MM).optional(),
  heightMm: z.number().int().min(MIN_TEMPLATE_HEIGHT_MM).max(MAX_TEMPLATE_HEIGHT_MM).optional(),
  gapMm: z.number().int().nonnegative().optional(),
  rowGapDots: z.number().int().nonnegative().max(40).optional(),
  verticalFit: VerticalFitSchema.optional(),
  includedFields: z.array(TemplateFieldKindSchema).max(TEMPLATE_FIELD_KINDS.length).optional(),
  active: z.boolean().optional(),
  isDefault: z.boolean().optional(),
});
export type UpdateLabelTemplateInput = z.infer<typeof UpdateLabelTemplateInput>;

export const SetDefaultLabelTemplateInput = z.object({
  tenantId: TenantIdSchema,
  templateId: LabelTemplateIdSchema,
});
export type SetDefaultLabelTemplateInput = z.infer<typeof SetDefaultLabelTemplateInput>;

export const DeleteLabelTemplateInput = z.object({
  tenantId: TenantIdSchema,
  templateId: LabelTemplateIdSchema,
});
export type DeleteLabelTemplateInput = z.infer<typeof DeleteLabelTemplateInput>;

export const SeedDefaultLabelTemplatesForExistingTenantInput = z.object({
  tenantId: TenantIdSchema,
});
export type SeedDefaultLabelTemplatesForExistingTenantInput = z.infer<
  typeof SeedDefaultLabelTemplatesForExistingTenantInput
>;

// ---------- Label CRUD inputs ----------
//
// Sprint 22: a Sprint 21 PR A introduziu os callables `createLabel`,
// `markLabelPrinted`, `expireLabel` e `deleteLabel` (com schemas
// `CreateLabelInput`/`MarkLabelPrintedInput`/`ExpireLabelInput`/
// `DeleteLabelInput`). Foi revertido — clientes voltam a escrever direto
// no Firestore (paridade com Android) e a lógica derivada migra para
// triggers (`onLabelWritten`). Os schemas foram removidos junto com os
// callables; manter apenas referência aqui para histórico.

// ---------- Product inputs (Sprint 21 — fonte da verdade em callable) ----------

/**
 * Body de criação de produto. `id`/timestamps/audit ficam a cargo do server.
 * `validityRules` é a fonte única das regras do produto (Sprint 24/27);
 * exatamente uma regra tem `isDefault = true` — usada para pré-selecionar
 * a regra na criação da etiqueta. Sprint 27: campo `defaultRuleKind` foi
 * removido (substituído pela flag por regra).
 *
 * OWNER+MANAGER apenas (rules já gateavam via `isManager`).
 */
export const CreateProductInput = z.object({
  tenantId: TenantIdSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(1000).default(''),
  category: ProductCategorySchema,
  sku: z.string().max(60).default(''),
  barcode: z.string().max(60).nullable().optional(),
  ingredients: z.array(z.string().max(120)).max(80).default([]),
  allergens: z.array(AllergenSchema).default([]),
  unit: UnitOfMeasureSchema,
  imagePath: z.string().max(500).nullable().optional(),
  imageUrl: z.string().max(1000).nullable().optional(),
  // Produto exige ≥ 1 regra de validade (decisão do owner, 2026-05-12): sem
  // regra não há como gerar etiqueta — estado inválido. `min(1)` aqui é a
  // borda de servidor; a UI (web/Android) já bloqueia o envio vazio.
  validityRules: z
    .array(ProductValidityRuleSchema)
    .min(1)
    .max(20)
    .refine((rules) => rules.filter((r) => r.isDefault).length === 1, {
      message: 'exatamente uma regra deve ter isDefault = true',
    }),
  // Estoque mínimo opcional na criação (`docs/screens/stock.md`). `null`
  // ou omitido = sem alerta de estoque baixo. Pós-criação muda via
  // escrita direta no doc do produto pelo OWNER/MANAGER — o trigger
  // `onProductWritten` grava audit `PRODUCT_MIN_STOCK_UPDATED`.
  minStock: z.number().int().min(0).max(1_000_000).nullable().optional(),
  active: z.boolean().default(true),
});
export type CreateProductInput = z.infer<typeof CreateProductInput>;

export const CreateProductOutput = z.object({
  productId: ProductIdSchema,
});
export type CreateProductOutput = z.infer<typeof CreateProductOutput>;

/**
 * Patch de update de produto. Todos os campos opcionais — só os enviados
 * são persistidos. Server valida diff cross-document (rule existe e
 * compatível com storage) quando relevante e grava audit log com
 * before/after.
 *
 * `active=false` aqui implementa o soft-delete (paridade com a UX de
 * "Excluir" no app, que é soft via `setActive(false)`).
 */
export const UpdateProductInput = z.object({
  tenantId: TenantIdSchema,
  productId: ProductIdSchema,
  patch: z
    .object({
      name: z.string().min(1).max(120).optional(),
      description: z.string().max(1000).optional(),
      category: ProductCategorySchema.optional(),
      sku: z.string().max(60).optional(),
      barcode: z.string().max(60).nullable().optional(),
      ingredients: z.array(z.string().max(120)).max(80).optional(),
      allergens: z.array(AllergenSchema).optional(),
      unit: UnitOfMeasureSchema.optional(),
      imagePath: z.string().max(500).nullable().optional(),
      imageUrl: z.string().max(1000).nullable().optional(),
      // Se enviado, não pode esvaziar as regras — produto sempre tem ≥ 1
      // e exatamente 1 `isDefault = true`.
      validityRules: z
        .array(ProductValidityRuleSchema)
        .min(1)
        .max(20)
        .refine((rules) => rules.filter((r) => r.isDefault).length === 1, {
          message: 'exatamente uma regra deve ter isDefault = true',
        })
        .optional(),
      active: z.boolean().optional(),
    })
    .refine((p) => Object.keys(p).length > 0, {
      message: 'patch precisa ter ao menos um campo',
    }),
});
export type UpdateProductInput = z.infer<typeof UpdateProductInput>;

/**
 * Hard-delete de produto. Apenas OWNER+MANAGER. Não cascateia em labels
 * históricas (`Label.productId` continua apontando para o doc deletado;
 * `productName` denormalizado já cobre a UI).
 *
 * Para "esconder" produto da listagem use `updateProduct` com
 * `patch.active=false` (soft delete).
 */
export const DeleteProductInput = z.object({
  tenantId: TenantIdSchema,
  productId: ProductIdSchema,
});
export type DeleteProductInput = z.infer<typeof DeleteProductInput>;

// ---------- Suppliers (issue #193 — docs/screens/suppliers.md) ----------
//
// Cadastro de fornecedores do tenant, coleção `tenants/{tid}/suppliers/{id}`.
// Padrão client-write como products: OWNER/MANAGER escrevem direto no doc
// (offline-first no Android); trigger `onSupplierWritten` carimba timestamps
// e grava audit `SUPPLIER_*`. Hard-delete proibido — `Product.supplierIds`
// referencia o doc; arquivar via `active=false` (toggle OWNER-only).
//
// Telefone/WhatsApp: E.164 com país SEMPRE igual a `tenant.countryCode`
// (regra do projeto — a UI trava o dial code; telefone de outro país é bug).

export const SupplierSchema = z.object({
  id: SupplierIdSchema,
  tenantId: TenantIdSchema,
  name: z.string().min(2).max(120),
  phoneE164: PhoneE164Schema,
  whatsappE164: PhoneE164Schema.nullable().default(null),
  email: z.email().max(254).nullable().default(null),
  // UI normaliza prefixando `https://` antes do submit quando ausente.
  // Bug corrigido em 2026-10-07 14:05 +0200 (#795, 1º nível item 8): era
  // `z.url()` puro, que aceita `javascript:`, `data:` e `vbscript:` — um
  // membro gravava (direto pelo SDK; os formulários prefixam `https://`) um
  // link que rodava script no navegador de quem clicasse, inclusive o OWNER.
  // Agora só http/https, como o `link` do broadcast (#767). Doc legado com
  // outro esquema falha o parse e sai da lista com `suppliers_doc_parse_failed`
  // (web) — preferível a renderizar o link. Espelhos: rule de `suppliers`
  // (`isSafeSupplierWebsite`) e o render nos três clientes.
  website: z.url({ protocol: /^https?$/ }).max(500).nullable().default(null),
  // Nome do representante/contato comercial.
  contactName: z.string().max(120).default(''),
  active: z.boolean(),
  // Cliente escreve sem timestamps; trigger `onSupplierWritten` carimba
  // server-side (mesma janela nula tolerada de `ProductSchema`).
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  createdByUid: UserIdSchema.optional(),
  updatedByUid: UserIdSchema.optional(),
});
export type Supplier = z.infer<typeof SupplierSchema>;

// ---------- Responsibles (Sprint 27 — docs/screens/responsibles.md) ----------
//
// Cadastro nominal de responsáveis pela manipulação dos perecíveis (cozinheiro,
// bartender, atendente etc.). Não é um usuário do tenant — é só um rótulo que
// vai para a etiqueta. OWNER-only. Toda mutação via callable Admin SDK
// (server-only writes); rules de `/tenants/{tid}/responsibles` permitem só
// read=isMember.
//
// Invariante mantido pelos callables: se existe ≥1 responsável com
// `active == true`, exatamente 1 deles tem `isDefault == true`. Promoção
// automática elege outro `active` (menor `name` asc) quando o default é
// desativado/excluído.

export const ResponsibleSchema = z.object({
  id: ResponsibleIdSchema,
  tenantId: TenantIdSchema,
  name: z.string().min(2).max(80),
  active: z.boolean(),
  isDefault: z.boolean(),
  createdAtMillis: z.number().int().positive(),
  createdByUid: UserIdSchema,
  // Quantas etiquetas (vigentes + histórico) referenciam este responsável.
  // Mantido por trigger `onLabelWritten` em Firestore via FieldValue.increment(±1).
  // Cliente nunca escreve; rules bloqueiam (`allow update: if false`).
  usageCount: z.number().int().min(0).default(0),
});
export type Responsible = z.infer<typeof ResponsibleSchema>;

export const CreateResponsibleInputSchema = z.object({
  tenantId: TenantIdSchema,
  name: z.string().min(2).max(80),
});
export type CreateResponsibleInput = z.infer<typeof CreateResponsibleInputSchema>;

export const CreateResponsibleOutputSchema = z.object({
  responsibleId: ResponsibleIdSchema,
  createdAtMillis: z.number().int().positive(),
  becameDefault: z.boolean(),
});
export type CreateResponsibleOutput = z.infer<typeof CreateResponsibleOutputSchema>;

export const SetResponsibleActiveInputSchema = z.object({
  tenantId: TenantIdSchema,
  responsibleId: ResponsibleIdSchema,
  active: z.boolean(),
});
export type SetResponsibleActiveInput = z.infer<typeof SetResponsibleActiveInputSchema>;

export const SetResponsibleActiveOutputSchema = z.object({
  ok: z.literal(true),
  promotedDefaultId: ResponsibleIdSchema.nullable(),
});
export type SetResponsibleActiveOutput = z.infer<typeof SetResponsibleActiveOutputSchema>;

export const SetResponsibleDefaultInputSchema = z.object({
  tenantId: TenantIdSchema,
  responsibleId: ResponsibleIdSchema,
});
export type SetResponsibleDefaultInput = z.infer<typeof SetResponsibleDefaultInputSchema>;

export const SetResponsibleDefaultOutputSchema = z.object({
  ok: z.literal(true),
});
export type SetResponsibleDefaultOutput = z.infer<typeof SetResponsibleDefaultOutputSchema>;

export const DeleteResponsibleInputSchema = z.object({
  tenantId: TenantIdSchema,
  responsibleId: ResponsibleIdSchema,
});
export type DeleteResponsibleInput = z.infer<typeof DeleteResponsibleInputSchema>;

export const DeleteResponsibleOutputSchema = z.object({
  ok: z.literal(true),
  promotedDefaultId: ResponsibleIdSchema.nullable(),
});
export type DeleteResponsibleOutput = z.infer<typeof DeleteResponsibleOutputSchema>;

// ---------- Tenant Daily Ops (PR 6 do plano `docs/screens/monitor.md`) ----------
//
// Agregação server-side mantida por `onLabelWritten` em
// `tenants/{tid}/daily_ops/{yyyy-MM-dd}`. Contadores incrementais por
// responsável e totais do dia. Documento criado/atualizado via
// `set({...}, { merge: true })` com `FieldValue.increment(...)`. Janela do
// dia calculada no **fuso do tenant** (`tenant.timeZone`) — não em UTC,
// pra alinhar com o que o front exibe como "hoje".
//
// Lido pela aba "Responsáveis" do Monitor quando `period === '30d'` —
// rompe o cap de 200 docs do `subscribeHistorico`.
//
// **Append-only**: trigger NUNCA decrementa. Apagar etiqueta não desfaz a
// agregação histórica (a etiqueta JÁ aconteceu no passado). Editar
// etiqueta também não — `responsibleId` é snapshot imutável e os timestamps
// (`createdAtMillis`, `printedAtMillis`, `statusChangedAtMillis`) só são
// preenchidos uma vez (idempotência do trigger).

const ResponsibleDailyCountsSchema = z.object({
  /** Snapshot do nome do responsável no momento da escrita (sobrescrito em
   * cada update — Sprint 27 garante imutabilidade do nome após criar). */
  responsibleName: z.string().max(80),
  created: z.number().int().nonnegative().default(0),
  printed: z.number().int().nonnegative().default(0),
  dropped: z.number().int().nonnegative().default(0),
  /** Sprint 30 PR 30-B — breakdown granular do `dropped` por status terminal.
   * `consumed + discarded + expired === dropped` (definição). Mantidos em
   * paralelo pra retrocompat com consumers antigos. */
  consumed: z.number().int().nonnegative().default(0),
  discarded: z.number().int().nonnegative().default(0),
  expired: z.number().int().nonnegative().default(0),
  /** Épico #323 (F1) — fechamento por DOIS OU MAIS desfechos na mesma
   * etiqueta. Entra em `dropped` como os outros três: a definição passa a ser
   * `dropped === consumed + discarded + expired + mixed`. Um contador próprio
   * (e não diluído no `consumed`) porque "quantas etiquetas terminaram
   * misturadas" é a pergunta que mede a adoção da baixa parcial. */
  mixed: z.number().int().nonnegative().default(0),
  /** Sprint 30 PR 30-D — última atividade do responsável NESTE dia
   * (máximo entre createdAtMillis/printedAtMillis/statusChangedAtMillis).
   * Cliente lê o max sobre os dias do período pra preencher
   * `lastActivityMillis` da tabela Responsáveis. Atualizado em transação
   * (read-write) pra preservar o max idempotente sob retries. */
  lastActivityMillis: z.number().int().nonnegative().optional(),
});
export type ResponsibleDailyCounts = z.infer<typeof ResponsibleDailyCountsSchema>;

/**
 * Sprint 35 — agregação diária por **ator** (quem executou a baixa), fonte da
 * aba "Baixadas" do Monitor. Distinta de `ResponsibleDailyCountsSchema`: lá a
 * chave é o `responsibleId` da etiqueta (de quem é o item perecível); aqui a
 * chave é o `statusChangedByUid` (quem deu a baixa). Só transições terminais
 * (CONSUMED/DISCARDED/EXPIRED) entram — `created`/`printed` não têm ator
 * relevante pra esta aba. O `SYSTEM_ACTOR_UID` (vencimento via cron
 * `scanExpiringLabels`) entra como bucket próprio → a UI o renderiza como
 * "Sistema (automático)", dando visibilidade de quanto venceu sem ninguém dar
 * baixa (= desperdício).
 */
const ActorDailyCountsSchema = z.object({
  /** Snapshot do displayName do member ator no momento da escrita. Vazio para
   * `SYSTEM_ACTOR_UID` (UI localiza "Sistema (automático)"). */
  actorName: z.string().max(80).default(''),
  /** `dropped` = consumed + discarded + expired (definição; mantido em paralelo
   * ao breakdown pra retrocompat com a aba Responsáveis). */
  dropped: z.number().int().nonnegative().default(0),
  consumed: z.number().int().nonnegative().default(0),
  discarded: z.number().int().nonnegative().default(0),
  expired: z.number().int().nonnegative().default(0),
  /** Épico #323 (F1) — fechamento por DOIS OU MAIS desfechos na mesma
   * etiqueta. Entra em `dropped` como os outros três: a definição passa a ser
   * `dropped === consumed + discarded + expired + mixed`. Um contador próprio
   * (e não diluído no `consumed`) porque "quantas etiquetas terminaram
   * misturadas" é a pergunta que mede a adoção da baixa parcial. */
  mixed: z.number().int().nonnegative().default(0),
  /** Última baixa deste ator NESTE dia (max `statusChangedAtMillis`).
   * Atualizado em transação (read-write) pra preservar o max sob retries. */
  lastActivityMillis: z.number().int().nonnegative().optional(),
});
export type ActorDailyCounts = z.infer<typeof ActorDailyCountsSchema>;

const DailyOpsTotalsSchema = z.object({
  created: z.number().int().nonnegative().default(0),
  printed: z.number().int().nonnegative().default(0),
  dropped: z.number().int().nonnegative().default(0),
  /** Sprint 30 PR 30-B — breakdown granular (ver `ResponsibleDailyCountsSchema`). */
  consumed: z.number().int().nonnegative().default(0),
  discarded: z.number().int().nonnegative().default(0),
  expired: z.number().int().nonnegative().default(0),
  /** Épico #323 (F1) — fechamento por DOIS OU MAIS desfechos na mesma
   * etiqueta. Entra em `dropped` como os outros três: a definição passa a ser
   * `dropped === consumed + discarded + expired + mixed`. Um contador próprio
   * (e não diluído no `consumed`) porque "quantas etiquetas terminaram
   * misturadas" é a pergunta que mede a adoção da baixa parcial. */
  mixed: z.number().int().nonnegative().default(0),
});
export type DailyOpsTotals = z.infer<typeof DailyOpsTotalsSchema>;

const EMPTY_DAILY_OPS_TOTALS: DailyOpsTotals = {
  created: 0,
  printed: 0,
  dropped: 0,
  consumed: 0,
  discarded: 0,
  expired: 0,
  mixed: 0,
};

/**
 * Sprint 30 PR 30-C — agregação por hora do dia (no fuso do tenant).
 * Chave é `HH` (2 dígitos, '00'..'23'). Permite ao Monitor calcular:
 * - `hourly` (últimas 24h): lê doc de hoje + doc de ontem, rola 24h.
 * - `weekdayHeat` (14d × hora): lê 14 docs, indexa `(weekday, hour)`.
 * - `printedByDay` (14d): soma `byHour[*].printed` por doc (ou usa
 *   `totals.printed` direto — mesmo resultado).
 */
const HourCountsSchema = z.object({
  created: z.number().int().nonnegative().default(0),
  printed: z.number().int().nonnegative().default(0),
  dropped: z.number().int().nonnegative().default(0),
});
export type HourCounts = z.infer<typeof HourCountsSchema>;

/**
 * Sprint 30 PR 30-E — agregação por produto. Chave é `productId`. Permite
 * ao Monitor calcular o "Top produtos" (ranking 7d) sem o cap de 200 do
 * histórico.
 */
export const ProductDailyCountsSchema = z.object({
  /** Snapshot do nome do produto no momento da escrita; pode ficar stale
   * se o produto for renomeado (Sprint 22 já tratava similar com
   * `responsibleName`). Cliente usa sempre o nome mais recente entre os
   * dias agregados. */
  productName: z.string().max(120),
  created: z.number().int().nonnegative().default(0),
  printed: z.number().int().nonnegative().default(0),

  // ----- Quantidade por produto (épico #323 §3.4, fase F1) -----
  //
  // A leitura de perdas deixa de ser "12 etiquetas descartadas" e passa a ser
  // "30 kg do produto X descartados". Só **por produto**: unidades diferentes
  // não somam, então nada disso existe em `totals` (o que é "do tenant"
  // continua em contagem).
  //
  // Os quatro vêm do diff do ledger `baixas[]`, no dia do `atMillis` de cada
  // entrada — não do status. Um estorno desconta no dia da entrada estornada.

  /** Σ `quantity` das etiquetas impressas no dia (denominador do % de perda). */
  printedQty: z.number().nonnegative().default(0),
  /** Σ das entradas `CONSUMED` do ledger, no dia de cada `atMillis`. */
  consumedQty: z.number().nonnegative().default(0),
  /** Σ das entradas `DISCARDED`. */
  discardedQty: z.number().nonnegative().default(0),
  /** Σ das entradas `EXPIRED` (geradas pelo cron), no dia do vencimento. */
  expiredQty: z.number().nonnegative().default(0),
  /**
   * Snapshot da `Product.unit` no momento da escrita — sem ela os números
   * acima são adimensionais e "30" não diz se são quilos ou caixas. `null` em
   * doc gravado antes desta fase; `.catch(null)` porque unidade nova escrita
   * por uma versão futura não pode reprovar o agregado inteiro.
   */
  unit: UnitOfMeasureSchema.nullable().catch(null).default(null),
  /**
   * `migrationRunId` da última execução do backfill (#331) que creditou
   * quantidade neste produto/dia. Serve para provar qual execução produziu um
   * número histórico se aparecer divergência.
   */
  lastMigrationRunId: z.string().max(60).nullable().default(null),
});
export type ProductDailyCounts = z.infer<typeof ProductDailyCountsSchema>;

export const TenantDailyOpsSchema = z.object({
  /** `yyyy-MM-dd` no fuso do tenant — também é o `documentId`. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  tenantId: TenantIdSchema,
  /** Mapa `responsibleId → counts`. `byResponsible` cresce com o número de
   * responsáveis ativos no dia (≪ 100 mesmo nos maiores tenants). */
  byResponsible: z.record(z.string(), ResponsibleDailyCountsSchema).default({}),
  /** Sprint 35 — mapa `statusChangedByUid → counts` das baixas (fonte da aba
   * "Baixadas"). Inclui o bucket `SYSTEM_ACTOR_UID` para vencimentos via cron. */
  byActor: z.record(z.string(), ActorDailyCountsSchema).default({}),
  /** Sprint 30 PR 30-C — bucketização por hora do dia ('00'..'23'). */
  byHour: z
    .record(z.string().regex(/^([01]\d|2[0-3])$/), HourCountsSchema)
    .default({}),
  /** Sprint 30 PR 30-E — agregação por produto (top produtos 7d). */
  byProduct: z.record(z.string(), ProductDailyCountsSchema).default({}),
  totals: DailyOpsTotalsSchema.default(EMPTY_DAILY_OPS_TOTALS),
  /** Última atualização do doc — debug/observabilidade. */
  updatedAtMillis: z.number().int().nonnegative(),
});
export type TenantDailyOps = z.infer<typeof TenantDailyOpsSchema>;

// ---------- Sprint 35 — Pricing platform schemas (add-ons, promotions, markets, sellers, audit) ----------

/** Schemas Zod dos enums Sprint 35. Os types (`Capability`,
 * `LifetimeFreeCategory`, ...) já vêm de `./enums` — re-exportados pelo
 * wildcard em `index.ts`. Convenção do projeto: declarar o type 1×
 * (perto do `const` enum) e o `Schema` aqui para uso em Zod.
 *
 * **PR G1 (2026-05-25)**: `SkuSchema` deixou de ser `z.enum(ALL_SKUS)`
 * e virou regex livre (`^[A-Z][A-Z0-9_]{2,30}$`) — super-admin cria
 * add-ons com SKU novo via `/billing/addons` sem deploy de código.
 * Quem precisar checar SKU *bem conhecido* em código (ex.: guard
 * "BASE não é add-on opcional") compara com `Sku.BASE` (constante
 * exportada por `enums.ts`). Quem precisar varrer os SKUs ativos em
 * runtime lê `/config/addons/items/*` (ou itera `subscription.items[]`).
 * A declaração do `SkuSchema` ficou no topo do arquivo (perto de
 * `nonEmpty`) por restrição de TDZ — outros schemas o usam antes
 * deste bloco.
 */
export const CapabilitySchema = z.enum(ALL_CAPABILITIES);
export const LifetimeFreeCategorySchema = z.enum(ALL_LIFETIME_FREE_CATEGORIES);
export const FiscalProviderSchema = z.enum(ALL_FISCAL_PROVIDERS);
export const SellerEntityStatusSchema = z.enum(ALL_SELLER_ENTITY_STATUSES);
export const YearlyDiscountModeSchema = z.enum(ALL_YEARLY_DISCOUNT_MODES);
export const PromotionScopeSchema = z.enum(ALL_PROMOTION_SCOPES);
export const CouponDurationSchema = z.enum(ALL_COUPON_DURATIONS);
export const AuditActorTypeSchema = z.enum(ALL_AUDIT_ACTOR_TYPES);
export const AuditBillingActionSchema = z.enum(ALL_AUDIT_BILLING_ACTIONS);

export type {
  AuditActorType,
  AuditBillingAction,
  Capability,
  CouponDuration,
  FiscalProvider,
  LifetimeFreeCategory,
  PromotionScope,
  SellerEntityStatus,
  Sku,
  YearlyDiscountMode,
} from './enums';

/**
 * Code ISO-3166 alpha-2 livre para schemas do MVP onde `country` não é
 * o subset `Country` saleable hoje. Habilitar país novo é configuração
 * em runtime (`/config/markets/{code}`), não release de app — adicionar
 * país ao enum `Country` é decisão de produto separada.
 */
export const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, {
  message: 'countryCode precisa ser ISO-3166 alpha-2 (2 letras maiúsculas).',
});

/** Valor de `enabled_countries` que significa "todos os países" (#954). */
export const ALL_COUNTRIES_WILDCARD = '*';

/**
 * Países em que um add-on pode ser vendido: `["*"]` (todos, inclusive países
 * que ainda não existem em lista nenhuma) **ou** uma lista de ISO alpha-2.
 *
 * Origem: #954 — 2026-10-13 09:27 +0200.
 *
 * Por quê: "todos" escrito como lista explícita obrigava a migrar os docs a
 * cada país novo e gerava um audit por país (a primeira versão da #954 gravou
 * 4.179 eventos para 25 add-ons). Com o curinga, "todos" é um registro só. Quem
 * exclui país de verdade é o console da Google Play, não este campo (owner,
 * 2026-10-13).
 *
 * Contrato: `[]` = em nenhum país (venda desligada por país); `["*"]` = todos;
 * `["BR","IT"]` = só estes. `"*"` misturado com códigos (`["*","BR"]`) é
 * recusado — ambíguo, e quem lê teria de decidir qual vale.
 *
 * Usado por: `AddonConfigSchema`, `CreateAddonInputSchema`,
 * `SetAddonEnabledCountriesInputSchema`, `ModuleCatalogImportSchema`. A leitura
 * "vende neste país?" é sempre `addonAvailableIn` — nunca `.includes(country)`,
 * que dá `false` para `["*"]`.
 *
 * Falhas conhecidas: ver `addonAvailableIn` — gravar `["*"]` antes de os
 * consumidores usarem o helper tira o módulo da venda assistida.
 */
export const AddonCountriesSchema = z
  .array(z.union([z.literal(ALL_COUNTRIES_WILDCARD), CountryCodeSchema]))
  .superRefine((list, ctx) => {
    if (list.includes(ALL_COUNTRIES_WILDCARD) && list.length > 1) {
      ctx.addIssue({
        code: 'custom',
        message: '"*" (todos os países) não se mistura com códigos de país.',
      });
    }
  });

/**
 * O add-on pode ser vendido em `country`? Única regra de disponibilidade por
 * país do catálogo de add-ons.
 *
 * Origem: #954 — 2026-10-13 09:27 +0200.
 *
 * Contrato: função pura. `["*"]` → sempre `true`; lista → `includes`; `[]` →
 * `false`.
 *
 * Usado por: Functions `lib/billing/assistedSaleGuards.ts` (Guard de venda
 * assistida); CRM `features/subscription/SaleSubscriptionPage.tsx` e
 * `features/checkout/saleDraft.ts`. (O admin `addonRow.ts` só testa se a
 * lista É o curinga, para exibir "Todos os países".)
 *
 * Falhas conhecidas: antes da #954 os quatro consumidores faziam
 * `enabled_countries.includes(country)`. Com `["*"]` gravado isso dá `false`
 * em todo país e o módulo some da Contratação — por isso a migração dos docs
 * para `["*"]` só roda depois de este helper estar publicado.
 */
export function addonAvailableIn(enabled: readonly string[], country: string): boolean {
  return enabled.includes(ALL_COUNTRIES_WILDCARD) || enabled.includes(country);
}

// Sprint 35 — i18n strict 4 locales obrigatórios (alinha com lint CR-13).
const I18nQuadSchema = z.object({
  'pt-BR': z.string().trim().min(2).max(200),
  'en': z.string().trim().min(2).max(200),
  'es': z.string().trim().min(2).max(200),
  'it': z.string().trim().min(2).max(200),
});

/**
 * Doc `/config/addons/{sku}` — descritor de add-on opcional do plano
 * (Sprint 35). `BASE` não é add-on (não tem doc aqui). `enabled_countries`
 * controla a venda por país no Guard da venda assistida
 * (`assistedSaleGuards.ts`, via `addonAvailableIn`). Comentário corrigido na
 * auditoria de 2026-10-13 (#954): citava o callable `addAddonToSubscription`
 * (Guard 4), removido em c187157d9 (2026-09-24, #714). i18n strict nos 4
 * locales — paridade obrigatória no commit
 * (lint CR-13). `required_capabilities` defaultа `['BASE_ACTIVE']`
 * porque add-on exige plano base ativo.
 */
export const AddonConfigSchema = z.object({
  sku: SkuSchema,
  name_i18n: I18nQuadSchema,
  description_i18n: I18nQuadSchema,
  /** `["*"]` = todos os países; senão lista de ISO-3166 alpha-2 livre —
   * habilitar país novo não exige mudar enum. `[]` = add-on não disponível em
   * nenhum mercado (soft disable). Ler sempre com `addonAvailableIn` (#954). */
  enabled_countries: AddonCountriesSchema,
  required_capabilities: z.array(CapabilitySchema).default([Capability.BASE_ACTIVE]),
  available_since: z.number().int().positive(),
  /** **Venda pausada** (#679 D20): preenchido por `disableAddon` ou por
   * `createAddon` com `paused_reason`, limpo por `enableAddon`. Reversível —
   * não é exclusão. Pausar só impede venda nova; quem já contratou mantém o
   * item e a cobrança. Na operação aparece como "Pode ser vendido: não".
   * "Descontinuado" não é estado do sistema: é intenção humana, escrita em
   * `disabled_reason`. */
  disabled_at: z.number().int().positive().nullable().optional(),
  disabled_reason: z.string().max(500).nullable().optional(),
  created_at: z.number().int().positive(),
  updated_at: z.number().int().positive(),
  updated_by: UserIdSchema.nullable(),
});
export type AddonConfig = z.infer<typeof AddonConfigSchema>;

/**
 * Sprint 35 PR G1 — input do callable `createAddon` (super-admin only).
 * Cria 2 Stripe Products (`prod_<sku>_monthly`/`_yearly`) idempotente
 * + doc `/config/addons/items/{sku}`. Não recebe `available_since`/
 * `created_at`/`updated_at`/`updated_by` (server preenche).
 * `enabled_countries` aceita `[]` (add-on sem país nenhum) e `["*"]` (todos,
 * o padrão desde a #954). Preço não é
 * daqui: desde a #679 F3 o piso e o preço de lista moram em
 * `crm_appConfig/pricingFloors`. `required_capabilities` default
 * `['BASE_ACTIVE']`.
 */
export const CreateAddonInputSchema = z.object({
  sku: SkuSchema,
  name_i18n: I18nQuadSchema,
  description_i18n: I18nQuadSchema,
  // #954: o padrão é "todos os países" — o add-on novo nasce vendável em
  // qualquer país, e quem restringe é o console da Play. Era `[]`.
  enabled_countries: AddonCountriesSchema.default([ALL_COUNTRIES_WILDCARD]),
  required_capabilities: z.array(CapabilitySchema).default([Capability.BASE_ACTIVE]),
  /** #679 D26 — quando presente, o add-on **nasce com a venda pausada**
   * (`disabled_at` = agora, `disabled_reason` = este texto). Evita a janela
   * em que um módulo ainda não pronto ficaria vendável entre o `createAddon`
   * e o `disableAddon`. Mesmos limites do motivo de `disableAddon`. */
  paused_reason: z.string().trim().min(10).max(500).optional(),
});
export type CreateAddonInput = z.infer<typeof CreateAddonInputSchema>;

/**
 * Sprint 35 PR G1 — input do callable `updateAddon`. Não muda `sku`
 * (lock pós-criação). Todos os campos opcionais — só passe o que mudar.
 *
 * `enabled_countries` (#935, item 8 — 2026-10-13 11:05 +0200): a edição do
 * admin grava nome, descrição, capabilities E países numa escrita só, com o
 * mesmo audit por país do `setAddonEnabledCountries`. Antes eram duas
 * chamadas: se a segunda falhasse, metade da edição ficava gravada.
 * Opcional, e o servidor ecoa `countries_applied: true` (ver
 * `UpdateAddonOutput`): um servidor antigo descarta o campo em silêncio (Zod
 * sem `.strict()`), e o admin novo, sem o eco, cai de volta na 2ª chamada.
 */
export const UpdateAddonInputSchema = z.object({
  sku: SkuSchema,
  name_i18n: I18nQuadSchema.optional(),
  description_i18n: I18nQuadSchema.optional(),
  required_capabilities: z.array(CapabilitySchema).optional(),
  enabled_countries: AddonCountriesSchema.optional(),
});
export type UpdateAddonInput = z.infer<typeof UpdateAddonInputSchema>;

/**
 * Saída do `updateAddon`. `countries_applied` só vem `true` quando o input
 * trouxe `enabled_countries` e o servidor os gravou (#935, item 8); `added`/
 * `removed` são os países que viraram evento de audit (`"*"` conta como um).
 */
export type UpdateAddonOutput = {
  readonly sku: string;
  readonly updated: true;
  readonly countries_applied?: boolean;
  readonly added?: readonly string[];
  readonly removed?: readonly string[];
};

/**
 * Sprint 35 PR G1 — input do callable `setAddonEnabledCountries`.
 * Substitui `enabled_countries` por completo; server calcula diff
 * (adds/removes) pra audit log granular (`ADDON_ENABLED_COUNTRY`/
 * `ADDON_DISABLED_COUNTRY` por país). Transição que envolve `["*"]` grava UM
 * evento com `country: "*"` (#954), não um por país.
 */
export const SetAddonEnabledCountriesInputSchema = z.object({
  sku: SkuSchema,
  countries: AddonCountriesSchema,
});
export type SetAddonEnabledCountriesInput = z.infer<
  typeof SetAddonEnabledCountriesInputSchema
>;

/**
 * Sprint 35 PR G1 — input do callable `disableAddon`, que **pausa a venda**
 * (#679 D20). Não cancela subscriptions ativas com esse add-on; apenas impede
 * venda nova (o guard de vendabilidade da venda assistida consulta
 * `disabled_at`; o `addAddonToSubscription` que também consultava saiu em
 * c187157d9, #714). O caminho de volta é `enableAddon`.
 */
export const DisableAddonInputSchema = z.object({
  sku: SkuSchema,
  reason: z.string().trim().min(10).max(500),
});
export type DisableAddonInput = z.infer<typeof DisableAddonInputSchema>;

/**
 * #679 D20 — input do callable `enableAddon`: retoma a venda de um add-on
 * pausado. Motivo obrigatório, como no `disableAddon`, porque retomar a venda
 * é o que torna o módulo comprável no CRM (junto do piso).
 */
export const EnableAddonInputSchema = z.object({
  sku: SkuSchema,
  reason: z.string().trim().min(10).max(500),
});
export type EnableAddonInput = z.infer<typeof EnableAddonInputSchema>;

/**
 * #679 F3 — arquivo de importação do catálogo de módulos
 * (`docs/catalog/modules-679.json`), lido pela tela `/billing/addons/import`
 * do admin. Cada módulo vira **uma** chamada de `createAddon`; a importação não
 * escreve no Firestore por conta própria, então Stripe Products e audit são os
 * mesmos da criação manual.
 *
 * `enabled_countries` é do arquivo, não do módulo. Desde a #954 o padrão (e o
 * do `docs/catalog/modules-679.json`) é `["*"]` — todos os países, como o
 * add-on criado à mão (owner, 2026-10-13). `'SALEABLE'` continua aceito para
 * arquivo antigo: o admin resolve, no momento da importação, para os mercados
 * com `saleable: true` em `/config/markets` (owner, 2026-09-24).
 */
export const ModuleCatalogImportSchema = z
  .strictObject({
    version: z.literal(1),
    // #954: ausente = `["*"]` (todos os países), o mesmo padrão do `createAddon`.
    enabled_countries: z
      .union([z.literal('SALEABLE'), AddonCountriesSchema])
      .default([ALL_COUNTRIES_WILDCARD]),
    modules: z
      .array(
        // `.strict()` porque o arquivo é digitado à mão: sem ele, um
        // `pause_reason` (sem o "d") seria descartado em silêncio e o módulo
        // nasceria vendável, contra a D26 — e um `enabled_countries` no módulo
        // sumiria sem aviso.
        CreateAddonInputSchema.omit({ enabled_countries: true })
          .extend({
            sku: SkuSchema.refine((sku) => sku !== Sku.BASE, {
              message: 'BASE é o plano obrigatório — não é módulo.',
            }),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();
    data.modules.forEach((m, i) => {
      if (seen.has(m.sku)) {
        ctx.addIssue({
          code: 'custom',
          path: ['modules', i, 'sku'],
          message: `SKU repetido no arquivo: ${m.sku}`,
        });
      }
      seen.add(m.sku);
    });
  });
export type ModuleCatalogImport = z.infer<typeof ModuleCatalogImportSchema>;

/**
 * Doc `/promotions/{id}` — espelha cupom Stripe + promo code(s) anexos.
 * Sprint 35. Criação super-admin only via `/billing/promotions`.
 * `applies_to_products[]` carrega Stripe `prod_*` IDs (cycle-specific),
 * permitindo cupom anual-only via `applies_to.products: ['prod_*_yearly']`.
 * Guard 3 limita 1 cupom por subscription. `LIFETIME_FREE_TEMPLATE` é o
 * cupom mestre 100% off forever reusado pelo callable `applyLifetimeFree`.
 */
export const PromotionSchema = z.object({
  id: nonEmpty,
  external_coupon_id: nonEmpty,
  external_promotion_code_id: nonEmpty.nullable(),
  /** Texto do código (null = nominal, super-admin aplica direto na
   * subscription sem código público). */
  code: z.string().nullable(),
  scope: PromotionScopeSchema,
  /** PSP de origem do cupom. Default `stripe`: todos os cupons atuais são
   * Stripe e docs antigos sem o campo lêem como STRIPE. Carregado pelo
   * `syncCoupons` e usado pela lista do admin pra agrupar por PSP (Paddle/
   * dLocal/EBANX entram em sprints futuras). */
  psp: PaymentProviderSchema.default('stripe'),
  /** Modo do objeto no PSP: `true` = produção (live), `false` = sandbox/
   * teste. Carimbado a partir de `coupon.livemode` da Stripe no create/sync.
   * `null` = doc antigo ainda não sincronizado (lista mostra "—"). A lista
   * do admin exibe badge Produção/Sandbox por cupom. */
  livemode: z.boolean().nullable().default(null),
  /** Vazio é permitido: cupom sincronizado direto do PSP pode valer pra
   * conta toda (sem restrição de produto). Cupom criado pelo admin sempre
   * tem ≥1 (validado em `CreateCouponInputSchema`). */
  applies_to_skus: z.array(SkuSchema),
  /** Stripe Product IDs (`prod_*`) — escopo cycle via convenção
   * `prod_<sku>_monthly` / `prod_<sku>_yearly`. */
  applies_to_products: z.array(nonEmpty),
  percent_off: z.number().min(0).max(100).nullable(),
  amount_off_cents: z.number().int().nonnegative().nullable(),
  /** Moeda do `amount_off_cents` (obrigatória quando `amount_off_cents != null`;
   * `null` quando o desconto é `percent_off`). Espelha o `currency` que o
   * Stripe Coupon carrega quando criado com `amount_off`. */
  amount_off_currency: CurrencySchema.nullable(),
  duration: CouponDurationSchema,
  duration_in_months: z.number().int().positive().nullable(),
  max_redemptions: z.number().int().positive().nullable(),
  times_redeemed: z.number().int().nonnegative().default(0),
  expires_at: z.number().int().positive().nullable(),
  reason: z.string().max(500),
  created_by: UserIdSchema,
  created_at: z.number().int().positive(),
  /** Soft-delete via `deleteCoupon`: marca local quando Stripe permite
   * apagar (sem redemptions ativas) ou quando super-admin arquiva pra
   * tirar da listagem sem mexer no Stripe (cupom com redemptions). */
  deleted_at: z.number().int().positive().nullable().optional(),
  deleted_reason: z.string().max(500).nullable().optional(),
});
export type Promotion = z.infer<typeof PromotionSchema>;

/**
 * Sprint 35 PR H1 — filtros canônicos da tela `/audit/billing` + payload
 * do callable `exportAuditBilling`. Mesma forma usada cliente-side para
 * compor a query Firestore. Range obrigatório (`from`/`to`) limita o
 * scan; default da UI é últimos 30 dias.
 *
 * Restrições:
 * - Janela máxima de 365 dias (limita custo de scan + tamanho do CSV).
 * - Pelo menos um filtro semântico (`tenantId`/`action`/`actorType`/
 *   `externalEventId`) NÃO é exigido — caso super-admin queira ver tudo
 *   do range. Hard cap de 50K linhas no callable evita CSV gigantesco.
 */
export const AuditBillingFiltersSchema = z
  .object({
    from: z.number().int().positive(),
    to: z.number().int().positive(),
    tenantId: nonEmpty.nullable(),
    action: AuditBillingActionSchema.nullable(),
    actorType: AuditActorTypeSchema.nullable(),
    externalEventId: z
      .string()
      .trim()
      .regex(/^[a-z]+_[A-Za-z0-9_]+$/, {
        message: 'externalEventId precisa casar /^[a-z]+_[A-Za-z0-9_]+$/ (ex.: evt_abc123).',
      })
      .nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.to < data.from) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: '`to` precisa ser >= `from`.',
      });
    }
    const oneYearMs = 365 * 24 * 60 * 60 * 1000;
    if (data.to - data.from > oneYearMs) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'janela máxima é 365 dias.',
      });
    }
  });
export type AuditBillingFilters = z.infer<typeof AuditBillingFiltersSchema>;

/**
 * Sprint 35 PR H1 — input do callable `exportAuditBilling`. Sem
 * `pageSize`/`cursor`: callable lê tudo do range filtrado e gera CSV
 * em memória (limite hard 50K linhas; ultrapassou → `resource-exhausted`).
 * UI redireciona pra `Reduza o filtro`.
 */
export const ExportAuditBillingInputSchema = AuditBillingFiltersSchema;
export type ExportAuditBillingInput = z.infer<typeof ExportAuditBillingInputSchema>;

/**
 * Doc `/config/markets/items/{country}` — Sprint 35. Super-admin liga/desliga
 * mercado via `/billing/markets` sem deploy. `payment_provider` fica `null`
 * quando `saleable: false`; callable `setMarketConfig` o exige quando
 * `saleable: true`, e recusa (`psp_without_seller`) PSP sem entidade
 * vendedora ATIVA para o modo (#958). Guards 6/7/8
 * bloqueiam combinações inválidas (UE→Paddle obrigatório; IT exige
 * Paddle MoR; não-UE não pode usar Paddle).
 *
 * Seed inicial (PR B): BR/US/AR/MX/CL/PY saleable com Stripe +
 * Stripe-hosted invoice + `etiquetachef-srl`; UE bloqueada com motivo
 * "Aguardando integração Paddle (sprint Expansão UE)".
 */
export const MarketConfigSchema = z.object({
  country: CountryCodeSchema,
  saleable: z.boolean(),
  payment_provider: PaymentProviderSchema.nullable(),
  /**
   * #748 G2 — gateways que a Contratação pode escolher neste país;
   * `payment_provider` é o padrão e tem de estar na lista — a validação entra
   * no `setMarketConfig` com a virada do BR (A7, #756); até lá o campo não é
   * lido. Ausente = só o padrão (comportamento anterior à #748).
   *
   * Origem: #750 — 2026-09-27 12:00 +0200 (A1).
   *
   * Desde a A3 (#752) o campo É lido pela venda: `saleGatewayOptions`
   * (`apps/functions/src/lib/billing/saleGateway.ts`) monta dele os gateways
   * da Contratação. O que ainda falta é a validação no `setMarketConfig`.
   */
  allowed_payment_providers: z.array(PaymentProviderSchema).min(1).optional(),
  /**
   * Modo do PSP ativo: `live` ou `sandbox` (ensaio — só tenants de teste
   * vendem). Ausente = `live` (todo doc anterior à #756); leia sempre por
   * `marketPaymentMode()` (`marketPsp.ts`), nunca `?? 'live'` espalhado.
   *
   * Origem: #756 — 2026-10-01 11:33 +0200 (A7: o admin escolhe por país
   * STRIPE / STRIPE SANDBOX / ASAAS / ASAAS SANDBOX). As regras de coerência
   * (ativo na lista de permitidos, Asaas só no BR) são validadas no
   * `setMarketConfig`, não aqui: a leitura é tolerante para um doc antigo não
   * derrubar o checkout do país.
   */
  payment_mode: z.enum(ALL_PAYMENT_MODES).optional(),
  fiscal_provider: FiscalProviderSchema,
  /**
   * Legado desde #958 (2026-10-13 13:07 +0200): o país não tem mais entidade —
   * a PJ que vende e emite nota sai do PSP (`findSellerEntityForProvider`,
   * `apps/functions/src/lib/billing/asaasSellerEntity.ts`). O `setMarketConfig`
   * grava `null` e nada lê este campo; docs antigos ainda trazem o id. Não
   * voltar a ler: era o vínculo que travava o documento fiscal da entidade.
   */
  seller_entity_id: nonEmpty.nullable(),
  blocked_reason: z.string().max(500).nullable(),
  // 2026-06-08 — meios de pagamento oferecidos no checkout deste país
  // (`payment_method_types` do PSP). Opcional p/ compat com docs antigos;
  // ausente → caller usa o fallback in-code (BR → card+pix; EUR/USD →
  // card+amazon_pay). Cada método precisa estar habilitado no Dashboard do PSP.
  payment_methods: z.array(z.enum(ALL_PAYMENT_METHOD_TYPES)).optional(),
  // -------------------------------------------------------------------------
  // Sprint 40 — catálogo dinâmico do país. Permite cadastrar país novo (ex.:
  // UE com Paddle) pelo admin SEM release do app: web + Android consomem estes
  // campos do Firestore. TODOS opcionais/nullable p/ backward-compat — doc
  // antigo (só campos de PSP) continua válido. Quando um campo está ausente, o
  // cliente cai no default COMPILADO em packages/types (`countryDefaults`,
  // `phone`, `taxid`, `currencyForCountry`) — por isso BR/MX/US/CA/IT nunca
  // quebram. País 100% novo depende inteiramente do que está aqui.
  //
  // `payments_enabled` é representado pelo campo `saleable` acima (não há
  // boolean duplicado — fonte única). `psp` é `payment_provider` acima.
  // -------------------------------------------------------------------------
  /** Nome de exibição default. Cliente prefere i18n `country.<code>` se existir. */
  name: z.string().trim().min(1).max(80).optional(),
  /** Código DDI internacional, ex.: '+55', '+39'. */
  phone_country_code: z.string().regex(/^\+\d{1,4}$/).optional(),
  /** Moeda ISO-4217 do país (lista fechada BRL/EUR/USD). */
  currency: CurrencySchema.optional(),
  /** Locale default do produto (lista fechada — alinha com packages/i18n). */
  language: TenantLocaleSchema.optional(),
  /**
   * Fusos IANA do país (ex.: ['America/Sao_Paulo', ...]), em ordem de uso —
   * o primeiro é o default. Países conhecidos (BR/MX/US/CA/IT) mantêm a lista
   * COMPILADA em `countryDefaults`; isto sobrescreve/habilita país NOVO sem
   * release. `setMarketConfig` rejeita zona IANA inválida.
   */
  timezones: z.array(z.string().min(1).max(60)).min(1).optional(),
  /** Nome do documento fiscal, ex.: 'CNPJ', 'P.IVA', 'VAT Number'. */
  tax_id_name: z.string().trim().min(1).max(40).optional(),
  /**
   * Regex de FORMATO do tax-id (sem máscara) p/ checagem leve no cliente e
   * validação de país NOVO no servidor. Países conhecidos (BR/MX/US/CA/IT)
   * mantêm o validador algorítmico compilado; a regex é só hint de formato.
   * Deve compilar — `setMarketConfig` rejeita regex inválida.
   */
  tax_id_regex: z.string().min(1).max(400).optional(),
  /** Placeholder/máscara do campo tax-id, ex.: '00.000.000/0000-00'. */
  tax_id_placeholder: z.string().max(60).optional(),
  /** Se o tax-id é obrigatório no onboarding deste país. */
  tax_id_required: z.boolean().optional(),
  /** País aparece no seletor de OTP (verificação de telefone). */
  otp_enabled: z.boolean().optional(),
  /**
   * Soft-delete: `false` esconde o país de TODAS as superfícies (OTP, checkout,
   * onboarding) sem apagar o doc. Ausente → tratado como ativo (compat). País
   * NUNCA é deletado de verdade — só desativado.
   */
  active: z.boolean().optional(),
  /**
   * Override da região dos tenants NOVOS deste país (#1078, F2 da #960 — 2026-10-29 09:31 +0100).
   * Ausente = padrão do grupo (`defaultRegionForCountry`: América do Sul → SA, resto → Bélgica).
   * Só vale com `config/regions.europeWest1Enabled` ligado (`resolveTenantRegion`); tenant já
   * criado nunca muda. Lido pelas Functions (`lib/regionsConfig.ts`) e pela aba Países do admin.
   *
   * Auditoria 2026-10-29 10:57 +0100 (#1078): sem o `.catch`, um valor desconhecido (editado à mão
   * no console, ou região nova lida por um build antigo) reprovava o doc INTEIRO do mercado — o país
   * sumia do billing (`getMarketConfig` → `null`) e o admin o mostrava "Não configurado", com a ação
   * em massa que o gravaria `saleable: false`. Aqui o valor desconhecido vira ausente; quem decide a
   * região (`marketRegionOverride` em `lib/regionsConfig.ts`) lê o campo CRU e BLOQUEIA, e
   * `getMarketConfig`/admin reportam. Não trocar por `.optional()` puro.
   */
  region: TenantRegionSchema.optional().catch(undefined),
  /** Epoch ms de criação do doc (preservado em updates). */
  created_at: z.number().int().positive().optional(),
  updated_at: z.number().int().positive(),
  updated_by: UserIdSchema,
});
export type MarketConfig = z.infer<typeof MarketConfigSchema>;

/**
 * Doc `/config/psps/items/{id}` — Sprint 40. CATÁLOGO ABERTO de PSPs gerido
 * pela aba PSP em `/countries` (super-admin). `id` é slug livre — pode-se
 * adicionar QUALQUER PSP. A aba Países lê este catálogo (entradas `enabled`)
 * no dropdown de PSP do país — fim da lista hardcoded no frontend.
 *
 * `enabled: false` esconde o PSP do dropdown sem apagar. Hard-delete só é
 * permitido quando 0 assinaturas ativas referenciam o PSP (callable
 * `deletePspConfig` checa `external_provider`); com ≥1 ativa, é permanente.
 *
 * `code_integrated` NÃO é persistido aqui — é derivado em runtime do
 * `PROVIDER_REGISTRY` (apps/functions/.../billing/providers) e mostrado na UI
 * como status "registrado no código" vs "só catálogo". Catálogo ≠ cobrança:
 * um PSP só cobra com `<psp>.ts` + secrets + registry.
 *
 * `supported_currencies`/`supported_countries` vazios = sem restrição (todos).
 */
/**
 * Ativo/inativo por modo de um PSP no catálogo (`/psp` do admin). Ausente no
 * doc (todo doc anterior à #756) = produção segue o `enabled` antigo e o
 * sandbox fica desligado — leia por `pspModeEnabled` (`marketPsp.ts`).
 *
 * Origem: #756 — 2026-10-01 13:31 +0200 (owner: "deve poder também cadastrar
 * sandbox de cada PSP"; o seletor do país mostra só o que está ativo aqui).
 */
export const PspModesSchema = z.object({
  live: z.object({ enabled: z.boolean() }),
  sandbox: z.object({ enabled: z.boolean() }),
});
export type PspModes = z.infer<typeof PspModesSchema>;

export const PspConfigSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, { message: 'id precisa ser slug minúsculo.' })
    .max(40),
  display_name: z.string().trim().min(1).max(60),
  enabled: z.boolean(),
  supported_currencies: z.array(CurrencySchema).default([]),
  supported_countries: z.array(CountryCodeSchema).default([]),
  notes: z.string().max(500).nullable().optional(),
  /** #756 — ativo por modo (produção/sandbox). Ver `PspModesSchema`. */
  modes: PspModesSchema.optional(),
  /**
   * Identificador da NOSSA conta no PSP (Stripe: `acct_…`). Dado da conta do
   * PSP, editado em `/psp/:id` — não da entidade (owner, 2026-10-01). Ao
   * trocar a chave de API, `adminSetPspSecret` recusa chave de outra conta
   * (`account_mismatch`). Ausente/nulo = não confere.
   */
  account_id: z.string().trim().max(100).nullable().optional(),
  created_at: z.number().int().positive(),
  updated_at: z.number().int().positive(),
  updated_by: UserIdSchema,
});
export type PspConfig = z.infer<typeof PspConfigSchema>;

/** Input do callable `setPspConfig` (create/edit de PSP no catálogo). */
export const SetPspConfigInput = z.object({
  id: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, {
      message: 'id precisa ser slug minúsculo (a-z, 0-9, _).',
    })
    .max(40),
  display_name: z.string().trim().min(1).max(60),
  enabled: z.boolean(),
  supported_currencies: z.array(CurrencySchema).optional(),
  supported_countries: z.array(CountryCodeSchema).optional(),
  notes: z.string().max(500).nullable().optional(),
  /** #756 — ativo por modo. Ausente = preserva o do doc. */
  modes: PspModesSchema.optional(),
  /** #756 — ID da nossa conta no PSP (Stripe `acct_…`). Ausente = preserva. */
  account_id: z.string().trim().max(100).nullable().optional(),
  reason: z.string().trim().min(1).max(500),
});
export type SetPspConfigInput = z.infer<typeof SetPspConfigInput>;

/** Input do callable `deletePspConfig` (hard-delete, só sem subs ativas). */
export const DeletePspConfigInput = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/).max(40),
  reason: z.string().trim().min(1).max(500),
});
export type DeletePspConfigInput = z.infer<typeof DeletePspConfigInput>;

/**
 * Doc `/config/sellerEntities/{id}` — Sprint 35. PJ própria que emite
 * fatura para os mercados configurados em `/config/markets/`. MVP: 1
 * entry `etiquetachef-srl` (P.IVA italiana, cap €100.000/ano).
 *
 * `cap_mode: YEARLY_CAP` exige `annual_cap_amount_cents` +
 * `annual_cap_currency` — `/dashboard/finance` alerta quando seller
 * atinge 80% do cap. `cap_mode: UNLIMITED` (futuro Dubai LLC etc) zera
 * o monitor.
 *
 * `stripe_account_id` e `paddle_account_id` são populados quando o
 * seller é onboarded em cada PSP — null permite manter doc antes do
 * onboarding bancário (status `PENDING`).
 */
export const SellerEntitySchema = z.object({
  id: nonEmpty,
  legal_name: z.string().trim().min(3).max(200),
  trading_name: z.string().trim().max(120).nullable(),
  headquarter_country: CountryCodeSchema,
  tax_id: z.string().trim().min(5).max(50),
  address: z.object({
    street: z.string().trim().min(2).max(200),
    city: z.string().trim().min(2).max(100),
    state: z.string().trim().max(100).nullable(),
    postal_code: z.string().trim().min(2).max(20),
  }),
  default_currency: CurrencySchema,
  cap_mode: z.enum(['UNLIMITED', 'YEARLY_CAP']),
  annual_cap_amount_cents: z.number().int().positive().nullable(),
  annual_cap_currency: CurrencySchema.nullable(),
  stripe_account_id: z.string().regex(/^acct_[A-Za-z0-9]+$/).nullable(),
  paddle_account_id: nonEmpty.nullable(),
  /**
   * Dados fiscais da PJ brasileira (CCM, CNAE, regime, NBS, config da NFS-e por
   * modo) — só em entidade com `headquarter_country: BR`. Ausente nas
   * entidades estrangeiras e nos docs anteriores à #756.
   *
   * Origem: #756 — 2026-10-01 11:33 +0200. Schema e exemplo em `brFiscal.ts`.
   */
  br_fiscal: SellerEntityBrFiscalSchema.nullable().optional(),
  /**
   * O gerente de conta do Asaas liberou a tokenização de cartão na conta de
   * PRODUÇÃO desta PJ. Sem ela não há anual parcelado (renovado pelo token) nem
   * troca de valor em assinatura de cartão no Asaas live — a `/pay` deixa de
   * oferecer o parcelado em vez de vender algo que não renova. Ausente = não.
   * O sandbox não depende disto (lá a tokenização é aberta).
   *
   * Origem: #756 — 2026-10-01 11:33 +0200 (owner: "ainda não" aprovada).
   * Usado por: `asaasLiveTokenizationEnabled` (`apps/functions/src/lib/billing/asaasSellerEntity.ts`).
   */
  asaas_live_tokenization_enabled: z.boolean().optional(),
  /**
   * O PSP cuja CONTA é desta PJ (BR → `asaas`, SRL → `stripe`). A NFS-e e o
   * "Enviar ao Asaas" acham a PJ do Asaas por aqui, e o país só aceita entidade
   * do PSP que ele usa. Ausente/nulo = doc anterior à #756 (tolerado).
   *
   * Origem: #756 — 2026-10-01 13:31 +0200 (owner: na entidade "deverá
   * selecionar 1 PSP para usar"). Usado por: `findSellerEntityForProvider`
   * (`apps/functions/src/lib/billing/asaasSellerEntity.ts`), `setMarketConfig`
   * (`seller_provider_mismatch`), admin (`SellerEntityEditorPage`, `MarketPspFields`).
   */
  payment_provider: PaymentProviderSchema.nullable().optional(),
  /**
   * O modo da conta do PSP desta entidade: `live` ou `sandbox` (owner,
   * 2026-10-01: "PSP desta entidade — cadê as SANDBOX?"). A NFS-e e a conta do
   * Asaas da tela seguem este modo. Ausente = vale para os dois (doc anterior).
   */
  payment_mode: z.enum(ALL_PAYMENT_MODES).nullable().optional(),
  status: SellerEntityStatusSchema,
  notes: z.string().max(500).nullable(),
  created_at: z.number().int().positive(),
  updated_at: z.number().int().positive(),
  updated_by: UserIdSchema.nullable(),
});
export type SellerEntity = z.infer<typeof SellerEntitySchema>;

/**
 * Input do callable `adminUpsertSellerEntity`: cria ou edita uma entidade
 * vendedora inteira (os campos do doc menos os carimbos), com o motivo do audit.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7: o admin edita a `etiquetachef-br`
 * com todos os dados para cobrar e emitir nota).
 *
 * Contrato: `id` em slug (vira o id do doc, imutável). As regras que dependem
 * do banco (CNPJ válido para sede BR, uma só PJ ativa por PSP × modo, entidade
 * que atende país vendável não sai de ATIVA nem troca de PSP) são do callable.
 * O documento fiscal é editável sempre — a trava saiu na #958 (2026-10-13), e a
 * troca fica no audit log com o motivo.
 *
 * Usado por: admin — editor de entidades vendedoras
 * (`apps/admin/src/features/sellerEntities/`); Functions —
 * `admin/sellerEntities.ts`.
 */
export const UpsertSellerEntityInputSchema = SellerEntitySchema.omit({
  created_at: true,
  updated_at: true,
  updated_by: true,
})
  .extend({
    id: z.string().regex(/^[a-z0-9][a-z0-9-]{1,48}$/, { message: 'id em slug: minúsculas, números e hífen.' }),
    reason: z.string().trim().min(1).max(500),
  })
  .superRefine((v, ctx) => {
    if (v.cap_mode === 'YEARLY_CAP' && (v.annual_cap_amount_cents === null || v.annual_cap_currency === null)) {
      ctx.addIssue({ code: 'custom', path: ['annual_cap_amount_cents'], message: 'teto anual exige valor e moeda.' });
    }
    if (v.headquarter_country !== 'BR' && v.br_fiscal) {
      ctx.addIssue({ code: 'custom', path: ['br_fiscal'], message: 'dados fiscais BR só em entidade com sede no BR.' });
    }
  });
export type UpsertSellerEntityInput = z.infer<typeof UpsertSellerEntityInputSchema>;

/**
 * Input de `adminGetAsaasFiscalInfo`/`adminSyncAsaasFiscalInfo`: qual conta do
 * Asaas (produção ou sandbox). A sincronização exige motivo (escreve na conta).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200.
 */
export const AsaasFiscalInfoModeInputSchema = z.object({
  mode: z.enum(ALL_PAYMENT_MODES),
  /**
   * A entidade aberta no editor. O callable recusa se ela não for a PJ do Asaas
   * (a ativa com sede no BR) — auditoria #756, achado 4: sem isto a tela
   * conferia/enviava outra entidade que a que o admin estava vendo.
   */
  sellerEntityId: z.string().min(1),
  reason: z.string().trim().min(1).max(500).optional(),
});
export type AsaasFiscalInfoModeInput = z.infer<typeof AsaasFiscalInfoModeInputSchema>;

/**
 * Input de `adminGetAsaasFiscalCatalog`: busca na lista de serviços
 * municipais da conta do Asaas do modo pedido (o município é o da conta).
 *
 * Origem: #756 — 2026-10-07 17:58 +0200 (owner: "nome, id e código do serviço
 * municipal devem ser dropdown" no editor da entidade vendedora).
 *
 * Contrato: `description` é o filtro de texto do Asaas (`?description=`),
 * opcional; vazio lista o começo da lista. Só leitura.
 *
 * Usado por: callable `adminGetAsaasFiscalCatalog`
 * (`apps/functions/src/admin/sellerEntities.ts`); admin web
 * (`SellerEntityEditorPage`, dropdown do serviço municipal).
 */
export const AsaasMunicipalServicesInputSchema = z.object({
  mode: z.enum(ALL_PAYMENT_MODES),
  description: z.string().trim().max(100).optional(),
});
export type AsaasMunicipalServicesInput = z.infer<typeof AsaasMunicipalServicesInputSchema>;

/**
 * Doc `/audit/billing/{eventId}` — log auditável de toda operação
 * financeira (Sprint 35). Retenção legal de 10 anos; `actorIpHash`
 * (SHA-256 + salt) zerado em 90 dias pelo job `pruneAuditIpHashes`
 * (minimização LGPD art. 6º-III). Plain IP NUNCA persiste.
 *
 * Coleção separada de `/audit/{eventId}` legacy (tenant/user/branch).
 * Helper canônico `logAuditBilling(action, payload)` (PR D1) é o único
 * caminho de escrita — idempotente por `eventId` (UUID).
 */
export const AuditBillingEventSchema = z.object({
  eventId: nonEmpty,
  at: z.number().int().positive(),
  actorType: AuditActorTypeSchema,
  actorId: nonEmpty,
  /** SHA-256(ip + AUDIT_IP_HASH_SALT). 64 chars hex; nullable para
   * eventos sem IP (jobs internos, webhooks server-to-server). Zerado
   * em > 90d pelo job `pruneAuditIpHashes`. */
  actorIpHash: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  actorUserAgent: z.string().max(500).nullable(),
  action: AuditBillingActionSchema,
  tenantId: TenantIdSchema,
  subscriptionId: nonEmpty.nullable(),
  before: z.unknown(),
  after: z.unknown(),
  amountCents: z.number().int().nullable(),
  currency: CurrencySchema.nullable(),
  externalProvider: PaymentProviderSchema.nullable(),
  externalEventId: nonEmpty.nullable(),
  reason: z.string().max(500).nullable(),
  metadata: metadataRecord,
});
export type AuditBillingEvent = z.infer<typeof AuditBillingEventSchema>;

/** SHA-256 em hex minúsculo (64 chars) — formato exigido pelo `user_data`
 * do Meta Conversions API. Reaproveitado pelos hashes de email/telefone. */
const sha256Hex = z.string().regex(/^[a-f0-9]{64}$/);

/**
 * Dados de identidade do usuário para match no Meta CAPI (`user_data`).
 * Email/telefone vão SEMPRE como SHA-256 (PII nunca em claro). `externalId`
 * é o `app_instance_id` do Firebase (D.4) para match cross-device; `ip` e
 * `userAgent` melhoram o match mas são opcionais (server-to-server pode não
 * tê-los). Todos opcionais: o Meta aceita o evento com qualquer subconjunto,
 * mas quanto mais chaves, maior o Event Match Quality.
 */
export const AdsUserDataSchema = z.object({
  emailHash: sha256Hex.optional(),
  phoneHash: sha256Hex.optional(),
  externalId: z.string().min(1).optional(),
  ip: z.string().min(1).optional(),
  userAgent: z.string().min(1).optional(),
  // ---- #972 (2026-10-15 12:07 +0200) — identificadores do aparelho/navegador
  // em que a compra self-service aconteceu (`selfServiceReservations.ads_context`).
  /** Cookie `_fbp` do Pixel (web). Vai cru para a Meta, nunca com hash. */
  fbp: z.string().min(1).max(200).optional(),
  /** Cookie `_fbc` (clique no anúncio, `fb.1.<ms>.<fbclid>`). Cru. */
  fbc: z.string().min(1).max(500).optional(),
  /** `client_id` do GA4 (parte `<n>.<n>` do cookie `_ga`) — obrigatório no MP web. */
  gaClientId: z.string().min(1).max(100).optional(),
  /** `app_instance_id` do Firebase do aparelho da COMPRA — obrigatório no MP de app. */
  appInstanceId: z.string().min(1).max(200).optional(),
});
export type AdsUserData = z.infer<typeof AdsUserDataSchema>;

/**
 * Entrada validada de uma conversão de mídia paga (`dispatchAdsConversion` →
 * `sendMetaCapi`). `eventId` é determinístico e compartilhado com o Pixel web
 * (`${tenantId}:${eventName}:${sessionId|invoiceId}`) para dedup no Meta — ver
 * `docs/ads-event-id.md`. `value.amountMinor` é em unidades menores (cents);
 * o `sendMetaCapi` converte para unidades maiores no `custom_data.value`.
 * Moedas travadas em BRL/EUR/USD (memória `currencies_locked_3`).
 */
export const AdsConversionInputSchema = z.object({
  eventName: z.enum(ALL_ADS_CONVERSION_EVENTS),
  eventId: nonEmpty,
  eventTimeMillis: z.number().int().positive(),
  tenantId: TenantIdSchema,
  userId: UserIdSchema.optional(),
  userData: AdsUserDataSchema,
  value: z
    .object({
      amountMinor: z.number().int().nonnegative(),
      currency: CurrencySchema,
    })
    .optional(),
  /** `custom_data.customer_segmentation` do Meta: cliente novo vs. existente. */
  customerSegmentation: z.enum(ALL_ADS_CUSTOMER_SEGMENTATIONS).optional(),
  /**
   * #972 — plataforma da conversão. Ausente = fonte fixa por evento (antes da
   * #972). `android`/`ios` viram `action_source: 'app'` na Meta, que EXIGE
   * `app_data.extinfo` (versão `a2`/`i2` + versão do SO — ver `device`).
   */
  channel: z.enum(ALL_ADS_CONVERSION_CHANNELS).optional(),
  /** #972 — dados do aparelho para o `extinfo` da Meta (app). Todos opcionais. */
  device: z
    .object({
      osVersion: z.string().min(1).max(40).optional(),
      appVersion: z.string().min(1).max(40).optional(),
      model: z.string().min(1).max(80).optional(),
    })
    .optional(),
});
export type AdsConversionInput = z.infer<typeof AdsConversionInputSchema>;
